import { isPlatformBrowser } from '@angular/common';
import { inject, Injector, Injectable, NgZone, PLATFORM_ID } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { ENVIRONMENT } from '@environment';
import { ChannelService } from '@usheru-hff/sdk/channel';
import { APP_GIT_VERSION, AppGitVersion } from '@usheru-hff/shared/utils';
import {
  PRIMENG_CHILD_SELECTORS,
  PRIMENG_COLOR_PROPERTIES,
  resolveBridgeMappings,
  getSearchContainerForChildren,
  enumerateTokensFromChain,
  inferPrimeNgComponentType,
  resolveVariableFromHints
} from '@usheru-hff/sdk/ai-bugfix-reasoning';
import type { PrimengBridgeEntry } from '@usheru-hff/sdk/ai-bugfix-reasoning';
import {
  findRelevantAiBugfixLessons as findRelevantLessonsFromPackage,
  debugLessonScoring
} from '@usheru-hff/sdk/ai-bugfix-lessons';
import type {
  AiBugfixLessonRecord,
  AiRelevantBugfixLesson,
  LessonScoringWeights
} from '@usheru-hff/sdk/ai-bugfix-lessons';
import { filter, Subject } from 'rxjs';

interface ReasoningEngineModule {
  findRelevantEntries: (
    componentMap: {
      entries: Array<{
        kind: string;
        selector: string;
        symbolName: string;
        sourcePath: string;
        searchTokens: string[];
        relatedPaths?: string[];
        selectors?: {
          nestedComponents?: string[];
          interactive?: string[];
        };
        exposedCssVariables?: string[];
        consumedCssVariables?: string[];
        usedBy?: string[];
        reachableByApps?: string[];
        configMapping?: string;
      }>;
      appStyleBundles?: Record<string, unknown>;
      searchIndex?: object;
    },
    input: {
      app: string;
      scope: string;
      bugContext: string;
      expectedResult: string;
      reviewFeedback?: string;
      path?: string;
      likelyFiles: string[];
    },
    adapters: {
      fileReader: (p: string) => string | null;
      pathExists: (p: string) => boolean;
      templateReader: (e: { templatePath?: string; sourcePath: string }) => string;
    }
  ) => Array<{ score: number; sourcePath: string; selector: string; symbolName: string; kind: string }>;
  classifyFixRouting: (
    input: { bugContext: string; expectedResult: string; reviewFeedback?: string; scope: string },
    relevantEntries: Array<{
      score: number;
      sourcePath: string;
      selector: string;
      symbolName: string;
      kind: string;
      relatedPaths?: string[];
    }>,
    codeSnippets: Array<{ path: string; snippet: string }>,
    adapters: {
      fileReader: (p: string) => string | null;
      pathExists: (p: string) => boolean;
      templateReader: (e: { templatePath?: string; sourcePath: string }) => string;
      environmentKeys: string[];
      styleFilePaths: string[];
    }
  ) => {
    fixType: string;
    routeAction: string;
    visualIssueType?: string;
    existingStyleVariableHints: string[];
    layoutConstraintHints: string[];
  };
}

let reasoningEngineModule: ReasoningEngineModule | null = null;
let reasoningEngineLoadPromise: Promise<ReasoningEngineModule> | null = null;

function loadReasoningEngine(): Promise<ReasoningEngineModule> {
  if (reasoningEngineModule) return Promise.resolve(reasoningEngineModule);
  if (reasoningEngineLoadPromise) return reasoningEngineLoadPromise;

  reasoningEngineLoadPromise = import('@usheru-hff/sdk/ai-bugfix-reasoning')
    .then(mod => {
      reasoningEngineModule = mod as unknown as ReasoningEngineModule;
      return reasoningEngineModule;
    })
    .catch(() => {
      reasoningEngineLoadPromise = null;
      return null;
    }) as Promise<ReasoningEngineModule>;

  return reasoningEngineLoadPromise;
}

/**
 * One browser step captured for AI/debug tooling.
 *
 * Example:
 * `{
 *   index: 1,
 *   type: 'entry',
 *   title: 'Open /watch-at-home?tags=drama',
 *   url: '/watch-at-home?tags=drama',
 *   path: '/watch-at-home',
 *   hash: '',
 *   queryParams: { tags: 'drama' }
 * }`
 */
export interface AiRecordedStep {
  index: number;
  type: 'entry' | 'navigation';
  title: string;
  url: string;
  path: string;
  hash: string;
  queryParams: Record<string, string | string[]>;
}

type AiSerializableValue = string | number | boolean | string[];

export interface AiAutomatedBugfixInputQuality {
  issue_type?: string;
  confidence: 'high' | 'medium' | 'low';
  warnings: string[];
  questions: string[];
  detected_reference_text?: string;
  detected_reference_kind?: string;
}

export type RecordStepEventType = 'click' | 'navigation' | 'hover' | 'scroll' | 'change' | 'submit' | 'input';

export interface AiRecordedDomInteraction {
  index: number;
  at: string;
  kind: 'click' | 'change' | 'submit' | 'mouseenter' | 'input' | 'scroll';
  settleMs?: number;
  mutationCount?: number;
  title: string;
  url: string;
  path: string;
  selector: string;
  selectorCandidates?: string[];
  role: string;
  tagName: string;
  inputType?: string;
  label?: string;
  text?: string;
  name?: string;
  value?: string;
  checked?: boolean;
  surface?: string;
  componentChain?: string[];
  scrollX?: number;
  scrollY?: number;
}

export interface AiCssFixSuggestion {
  variable: string;
  currentValue: string;
  suggestedValue: string;
  scope: string;
  primengToken?: string;
  reason: string;
  rawCss?: string;
}

export interface AiSelectorHint {
  selector: string;
  role: string;
  tagName: string;
  label?: string;
  text?: string;
  surface?: string;
}

export interface AiSelectorListing {
  app: string;
  current_url: string;
  current_path: string;
  focus_selector?: string;
  primary_surface?: AiSelectorHint;
  component_chain: string[];
  surfaces: AiSelectorHint[];
  interactive_targets: AiSelectorHint[];
  recent_interactions: Array<Pick<AiRecordedDomInteraction, 'title' | 'selector' | 'role' | 'label' | 'text'>>;
  guidance: string[];
  example_bug_context: string;
}

export interface AiTargetElementHint extends AiSelectorHint {
  selectorCandidates: string[];
  componentChain: string[];
  relatedOwners?: Array<{
    kind: string;
    selector: string;
    tagName: string;
    classes?: string[];
    reason?: string;
  }>;
  structuralOwner?: {
    selector: string;
    tagName: string;
    classes: string[];
    overlayScope?: string;
    iconSelector?: string;
    inputSelector?: string;
  };
  layout?: {
    clientWidth: number;
    clientHeight: number;
    offsetWidth: number;
    offsetHeight: number;
    boundingWidth: number;
    boundingHeight: number;
    boundingTop: number;
    boundingLeft: number;
    scrollX: number;
    scrollY: number;
    viewportWidth: number;
    viewportHeight: number;
  };
}

export interface AiAutomatedBugfixSkillRequest {
  app?: string;
  bug_context?: string;
  target_element?: AiTargetElementHint | null;
  files?: string[];
  expected_outcome?: string;
  validation?: string | string[];
  validation_commands?: string[];
  serve_configuration?: string;
  source_of_truth?: 'local' | 'production';
  visual_expectations?: Record<string, AiSerializableValue>;
  model?: string;
  reasoning_effort?: string;
  pr?: string | number;
  issue?: string;
  review_feedback?: string;
  lookup_hint?: string[];
  branch?: string;
}

export type AiAutomatedBugfixSkillConsoleRequest = string | AiAutomatedBugfixSkillRequest | undefined;

interface ComponentMapPayload {
  appStyleBundles?: Record<
    string,
    {
      projectPath: string;
      stylePaths: string[];
      transitiveStylePaths: string[];
      files: Array<{
        path: string;
        exposedCssVariables?: string[];
        consumedCssVariables?: string[];
      }>;
      exposedCssVariables?: string[];
      consumedCssVariables?: string[];
    }
  >;
  entries: Array<{
    kind: string;
    selector: string;
    symbolName: string;
    sourcePath: string;
    searchTokens: string[];
    relatedPaths?: string[];
    exposedCssVariables?: string[];
    consumedCssVariables?: string[];
    primengBridge?: Record<string, { primengToken: string; file: string }>;
  }>;
  searchIndex?: object;
}

export interface AiAppResolution {
  value: string;
  source: 'request' | 'git_version' | 'vite_asset_path' | 'channel_name' | 'hostname' | 'fallback';
  confidence: 'high' | 'medium' | 'low';
  candidates: string[];
}

// Lesson types imported from @usheru-hff/sdk/ai-bugfix-lessons
// AiBugfixLessonRecord, AiRelevantBugfixLesson

interface AiResolvedAppCandidate {
  value: string;
  source: AiAppResolution['source'];
  confidence: AiAppResolution['confidence'];
}

export interface AiAutomatedBugfixSkillInput extends Omit<AiAutomatedBugfixSkillRequest, 'app' | 'bug_context'> {
  app: string;
  bug_context: string;
  runtime_context: {
    source: 'window.usheru.buildAutomatedBugfixSkillInput';
    app_type: string;
    channel_name: string | null;
    hostname: string;
    origin: string;
    page_title: string;
    angular_version?: string;
    html_app_version?: string;
    source_branch?: string;
    source_commit?: string;
    git_version?: AppGitVersion;
    current_url: string;
    current_path: string;
    target_element?: AiTargetElementHint;
    input_quality?: AiAutomatedBugfixInputQuality;
    app_resolution: AiAppResolution;
    recorded_steps: AiRecordedStep[];
    recorded_dom_interactions: AiRecordedDomInteraction[];
    relevant_lessons?: AiRelevantBugfixLesson[];
    chat_overrides?: Record<string, AiSerializableValue>;
  };
}

export interface AiAutomatedBugfixSkillHelpResult {
  message: string;
  recordStepsEnabled: boolean;
  recordStepsConfig: string;
  recordedSteps: number;
  recordedDomInteractions: number;
  recordStepsUrl: string;
}

export interface AiBugfixAnalysisResult {
  relevantEntries: Array<{ score: number; sourcePath: string; selector: string; symbolName: string; kind: string }>;
  fixRouting: {
    fixType: string;
    routeAction: string;
    visualIssueType?: string;
    existingStyleVariableHints: string[];
    layoutConstraintHints: string[];
  };
  loadedFrom: 'bundle' | 'fallback';
}

export interface AiBugfixReviewResult {
  payload: AiAutomatedBugfixSkillInput;
  payloadJson: string;
  analysis: {
    scope: string;
    relevantFiles: Array<{ score: number; sourcePath: string; selector: string; symbolName: string; kind: string }>;
    fixRouting: {
      fixType: string;
      routeAction: string;
      visualIssueType?: string;
      existingStyleVariableHints: string[];
      layoutConstraintHints: string[];
    };
    assumptions: string[];
  };
}

export interface AiBugfixReviewTicket {
  id: string;
  status: 'pending' | 'analyzing' | 'done' | 'error';
  copyPayload: () => void;
}

export interface AiBugfixReviewHelpResult {
  message: string;
  recordStepsEnabled: boolean;
  recordStepsConfig: string;
  recordedSteps: number;
  recordedDomInteractions: number;
  recordStepsUrl: string;
}

export interface AiUsheruHelpResult {
  message: string;
  commands: Array<{ command: string; description: string; detailedHelp: string }>;
}

export interface AiUsheruCommandHelpResult {
  message: string;
  command: string;
}

export type AiBugfixReviewConsoleRequest = { bug_context: string; expected_outcome: string } | string | undefined;

declare global {
  interface Window {
    usheru?: {
      recordedSteps?: AiRecordedStep[];
      recordedDomInteractions?: AiRecordedDomInteraction[];
      buildAutomatedBugfixSkillInput?: (
        request?: AiAutomatedBugfixSkillConsoleRequest,
        targetElement?: Element | null
      ) => string | AiAutomatedBugfixSkillHelpResult;
      reviewAutomatedBugfixPlan?: (
        request?: AiBugfixReviewConsoleRequest,
        targetElement?: Element | null
      ) => AiBugfixReviewTicket | AiBugfixReviewHelpResult;
      _lastReviewPayloadJson?: string;
      _lastAppliedFix?: { selector?: string; property?: string; value?: string; rawCss?: string } | null;
      _webLlmChat?: {
        open: () => void;
        close: () => void;
        destroy: () => void;
        setTargetElement?: (element: Element | null) => void;
      };
      _chatInteractionFilter?: string;
      _chatInteractionFilter_raw?: number[];
      _chatPayloadOverrides?: Record<string, AiSerializableValue>;
      _aiDebug?: boolean;
      _lastClickedElement?: Element | null;
      _debugButton?: HTMLElement | null;
      aiDebug?: () => void;
      report?: (input?: string | Element | { bug?: string; expected?: string; element?: Element | null }) => void;
      setBrokenElement?: (element?: Element | null) => AiTargetElementHint | null;
      listSelectors?: (limit?: number) => AiSelectorListing;
      describeElement?: (element?: Element | null) => AiTargetElementHint | null;
      analyzeBugfix?: (options: {
        bugContext: string;
        expectedOutcome: string;
        scope?: string;
      }) => Promise<AiBugfixAnalysisResult | { error: string }>;
      help?: (
        command?: string
      ) =>
        | AiUsheruHelpResult
        | AiUsheruCommandHelpResult
        | AiAutomatedBugfixSkillHelpResult
        | AiBugfixReviewHelpResult
        | void;
      startRecording?: (types?: string | string[]) => void;
      stopRecording?: () => void;
      clearRecording?: () => void;
      diagnoseElement?: (element?: Element | null, app?: string, bugContext?: string) => Promise<object | null>;
      injectFix?: (opts: { selector?: string; property?: string; value?: string; rawCss?: string }) => string | null;
      setLessonWeights?: (weights: Partial<import('@usheru-hff/sdk/ai-bugfix-lessons').LessonScoringWeights>) => void;
      debugLessonScoring?: () => import('@usheru-hff/sdk/ai-bugfix-lessons').LessonDebugEntry[];
      recordBugfixLesson?: (opts: {
        bug_context: string;
        expected_outcome: string;
        fixes: Array<{
          label: string;
          selector: string;
          rawCss: string;
          source?: string;
          fixType?: string;
          routeAction?: string;
        }>;
        app?: string;
        scope?: string;
        path?: string;
        targetElementHint?: AiTargetElementHint | null;
      }) => import('@usheru-hff/sdk/ai-bugfix-lessons').AiBugfixLessonRecord[];
    };
    GPU?: any;
  }
}

interface WebLlmChatModule {
  CreateMLCEngine: (
    modelId: string,
    opts?: { initProgressCallback?: (report: { text: string }) => void }
  ) => Promise<MLCEngine>;
}

interface MlcChatMessage {
  role: string;
  content: string;
}
interface MlcChatResponse {
  choices: Array<{ message: { content: string | null }; finish_reason: string; index: number }>;
  id: string;
  model: string;
  created: number;
}

interface MLCEngine {
  chat: {
    completions: {
      create: (request: {
        messages: MlcChatMessage[];
        max_tokens?: number;
        stream?: boolean;
      }) => Promise<MlcChatResponse>;
    };
  };
}

let webLlmChatModule: WebLlmChatModule | null = null;
let webLlmEngine: MLCEngine | null = null;
let webLlmLoadPromise: Promise<WebLlmChatModule> | null = null;
let webLlmChatWidget: HTMLElement | null = null;
let webLlmEngineLoadPromise: Promise<MLCEngine | null> | null = null;
let webLlmLoadProgress: { text: string; progress: number } | null = null;
let webLlmLoadProgressListeners: Array<(report: { text: string; progress: number }) => void> = [];

function hasWebGpuSupport(): boolean {
  return typeof window !== 'undefined' && !!(navigator as any).gpu;
}

function loadWebLlmChat(): Promise<WebLlmChatModule> {
  if (webLlmChatModule) return Promise.resolve(webLlmChatModule);
  if (webLlmLoadPromise) return webLlmLoadPromise;

  webLlmLoadPromise = import('@mlc-ai/web-llm')
    .then(mod => {
      webLlmChatModule = mod as unknown as WebLlmChatModule;
      return webLlmChatModule;
    })
    .catch(() => {
      webLlmLoadPromise = null;
      return null;
    }) as Promise<WebLlmChatModule>;

  return webLlmLoadPromise;
}

async function createWebLlmEngine(
  progressCallback?: (report: { text: string; progress: number }) => void
): Promise<MLCEngine | null> {
  if (webLlmEngine) return webLlmEngine;
  if (webLlmEngineLoadPromise) return webLlmEngineLoadPromise;

  const onProgress = (report: { progress: number; text: string; timeElapsed: number }) => {
    const progress = typeof report.progress === 'number' ? Math.round(report.progress * 100) : 0;
    const statusText = `${report.text || 'Loading local model...'} ${progress}%`;
    webLlmLoadProgress = { text: statusText, progress };
    webLlmLoadProgressListeners.forEach(fn => fn({ text: statusText, progress }));
    if (progressCallback) progressCallback({ text: statusText, progress });
  };

  webLlmEngineLoadPromise = loadWebLlmChat().then(mod => {
    if (!mod || !mod.CreateMLCEngine) return null;
    return mod
      .CreateMLCEngine('Llama-3.2-3B-Instruct-q4f32_1-MLC', {
        initProgressCallback: onProgress
      })
      .then(engine => {
        webLlmEngine = engine;
        return engine;
      })
      .catch(() => null);
  });

  return webLlmEngineLoadPromise;
}

function onWebLlmLoadProgress(listener: (report: { text: string; progress: number }) => void) {
  if (webLlmEngine) {
    listener({ text: 'Model loaded ✓', progress: 100 });
    return;
  }
  if (webLlmLoadProgress) {
    listener(webLlmLoadProgress);
  }
  webLlmLoadProgressListeners.push(listener);
}

function destroyWebLlmChatWidget() {
  if (webLlmChatWidget) {
    webLlmChatWidget.remove();
    webLlmChatWidget = null;
  }
}

function buildFixSuggestionPrompt(input: {
  bugContext: string;
  expectedOutcome: string;
  scope: string;
  app: string;
  targetSelector: string;
  fixType: string;
  routeAction: string;
  componentChain: string[];
  topComponents: Array<{ selector: string; symbolName: string; kind: string; score: number }>;
  existingStyleVariableHints: string[];
  cssDiagnosticsText?: string;
  cssFixSuggestions: Array<{ variable: string; currentValue: string; suggestedValue: string; scope: string }>;
}): string {
  const topComponentsBlock = input.topComponents
    .slice(0, 3)
    .map((c, i) => `${i + 1}. <${c.selector}> (${c.kind}, score: ${c.score}%)`)
    .join('\n');

  const cssVarsBlock =
    input.existingStyleVariableHints.length > 0
      ? input.existingStyleVariableHints.slice(0, 10).join(', ')
      : 'none detected';

  const cssDiagBlock = input.cssDiagnosticsText ? input.cssDiagnosticsText.slice(0, 600) : 'not available';

  const fixSuggestionBlock =
    input.cssFixSuggestions.length > 0
      ? input.cssFixSuggestions
          .slice(0, 5)
          .map(
            f =>
              `  Set ${f.variable}: ${f.suggestedValue} on <${f.scope}>` +
              (f.currentValue && f.currentValue !== f.suggestedValue ? ` (currently: ${f.currentValue})` : '')
          )
          .join('\n')
      : '  none';

  return `You are a CSS fix advisor for the usheru-hff Angular component library.
Given a deterministic bug analysis, recommend the single best concrete fix.

BUG:
  ${input.bugContext}
EXPECTED:
  ${input.expectedOutcome}
SCOPE: ${input.scope}
APP: ${input.app}
TARGET: ${input.targetSelector}
CHAIN: ${input.componentChain.join(' → ') || 'unknown'}
FIX TYPE: ${input.fixType}
ROUTE ACTION: ${input.routeAction}

TOP COMPONENTS:
${topComponentsBlock}

DETECTED CSS VARIABLES: ${cssVarsBlock}

CSS DIAGNOSTICS (truncated):
${cssDiagBlock}

DIAGNOSTIC FIX SUGGESTIONS:
${fixSuggestionBlock}

RULES:
- CRITICAL: Suggest ONLY from the DETECTED CSS VARIABLES list above. Do NOT invent new variable names.
- If none of the detected variables fit, set Variable to "NONE" and explain what new variable to add.
- Prefer app-level overrides in apps/<app>/src/styles/styles.scss over shared component edits.
- Keep response under 5 lines. Use format below.

OUTPUT FORMAT (exactly):
Variable: <name>
Value: <value>
Scope: <selector or component host>
File: <file path>
Reason: <one sentence why this fix>`;
}

const CONFIG_CATALOG_PATHS = ['/assets/ai-tooling/configuration-catalog.json', '/docs/configuration-catalog.json'];

let configCatalogCache: ConfigurationCatalog | null = null;
let configCatalogPromise: Promise<ConfigurationCatalog> | null = null;

interface ConfigurationCatalogEntry {
  id: string;
  kind: string;
  configPath: string;
  summary?: string;
  declaredIn?: { path: string; line: number };
  fields?: Array<{ name: string; type: string; description?: string }>;
  setIn?: string[];
}

interface ConfigurationCatalog {
  workflow?: { appOverrideFiles?: string[] };
  entries: ConfigurationCatalogEntry[];
}

async function fetchConfigurationCatalog(): Promise<ConfigurationCatalog> {
  if (configCatalogCache) return configCatalogCache;
  if (configCatalogPromise) return configCatalogPromise;
  configCatalogPromise = (async () => {
    for (const url of CONFIG_CATALOG_PATHS) {
      try {
        const r = await fetch(url);
        if (r.ok) {
          const data: ConfigurationCatalog = await r.json();
          configCatalogCache = data;
          return configCatalogCache;
        }
      } catch {
        /* try next */
      }
    }
    const fallback: ConfigurationCatalog = { entries: [] };
    configCatalogCache = fallback;
    return fallback;
  })();
  return configCatalogPromise;
}

function extractEnvironmentKeys(catalog: ConfigurationCatalog): string[] {
  return catalog.entries
    .filter(e => e.kind === 'component-config-group' || e.kind === 'environment-group')
    .map(e => e.configPath);
}

function buildAlternativeFixPrompt(input: {
  bugContext: string;
  expectedOutcome: string;
  scope: string;
  app: string;
  targetSelector: string;
  componentChain: string[];
  existingProposals: Array<{ label: string; rawCss: string; priority: number; source: string }>;
  fixSurfaces: Array<{ type: string; path: string; description: string; file: string; properties?: string[] }>;
}): string {
  const proposalsBlock = input.existingProposals
    .slice(0, 5)
    .map((p, i) => `${i + 1}. ${p.label} (priority ${p.priority}, ${p.source})`)
    .join('\n');

  const surfacesBlock = input.fixSurfaces
    .map(
      (s, i) =>
        `${i + 1}. [${s.type}] ${s.path}\n   ${s.description}\n   File: ${s.file}` +
        (s.properties?.length ? `\n   Properties: ${s.properties.slice(0, 5).join(', ')}` : '')
    )
    .join('\n\n');

  return `You are a fix reviewer for the usheru-hff Angular codebase.
The deterministic pipeline already proposed these fixes:
${proposalsBlock || '  (none)'}

YOUR TASK: Suggest a BETTER or DIFFERENT fix approach that the pipeline may have missed.
The pipeline is good at finding CSS variable overrides. You should look for:
- Environment config changes (component config, theme, feature flags)
- Component source code changes
- Structural or behavioral fixes

AVAILABLE NON-CSS FIX SURFACES:
${surfacesBlock || '  (none detected)'}

BUG:
  ${input.bugContext}
EXPECTED:
  ${input.expectedOutcome}
SCOPE: ${input.scope}
APP: ${input.app}
TARGET: ${input.targetSelector}
CHAIN: ${input.componentChain.join(' → ') || 'unknown'}

RULES:
- Suggest ONLY from the available fix surfaces above. Do NOT invent config keys or file paths.
- If the pipeline's proposals are already the best approach, respond "Fix Type: none"
- Prefer app-level environment config over source code changes
- Prefer theme changes over component-specific config when both apply
- Keep response under 6 lines. Use the exact format below.

OUTPUT FORMAT:
Fix Type: env-config | theme | code-change | css-override | none
Key/Path: <config key or source file path>
Value/Change: <conceptual description of the change to make>
File: <file to edit>
Reason: <one sentence why this is better than the pipeline proposals>`;
}

function buildNonCssFixSurfaces(
  relevantEntries: Array<{
    selector: string;
    symbolName: string;
    configMapping?: string;
    semanticTags?: string[];
    sourcePath: string;
    templatePath?: string;
    stylePaths?: string[];
    kind: string;
    score: number;
  }>,
  app: string,
  catalog: ConfigurationCatalog
): Array<{ type: string; path: string; description: string; file: string; properties?: string[] }> {
  const surfaces: Array<{ type: string; path: string; description: string; file: string; properties?: string[] }> = [];
  const seen = new Set<string>();

  for (const entry of relevantEntries.slice(0, 3)) {
    // 1. configMapping → env-config surface
    if (entry.configMapping) {
      const configPath = `components.${entry.configMapping}`;
      if (!seen.has(configPath)) {
        seen.add(configPath);
        const catalogEntry = catalog.entries.find(e => e.configPath === configPath);
        surfaces.push({
          type: 'env-config',
          path: configPath,
          description: `Configure ${entry.symbolName} behavior via environment config`,
          file: `apps/${app}/src/environments/environment.all.ts`,
          properties: catalogEntry?.fields?.map(f => f.name)
        });
      }
    }

    // 2. App styles override
    const cssFile = `apps/${app}/src/styles/styles.scss`;
    if (!seen.has(cssFile)) {
      seen.add(cssFile);
      surfaces.push({
        type: 'css-override',
        path: cssFile,
        description: `App-level style overrides for ${entry.symbolName}`,
        file: cssFile
      });
    }

    // 3. Component source changes
    if (entry.sourcePath && !seen.has(entry.sourcePath)) {
      seen.add(entry.sourcePath);
      surfaces.push({
        type: 'code-change',
        path: entry.sourcePath.replace(/^.*?src\//, ''),
        description: `Modify ${entry.symbolName} component logic`,
        file: entry.sourcePath
      });
    }

    // 4. Component template changes
    if (entry.templatePath && !seen.has(entry.templatePath)) {
      seen.add(entry.templatePath);
      surfaces.push({
        type: 'code-change',
        path: entry.templatePath.replace(/^.*?src\//, ''),
        description: `Modify ${entry.symbolName} template structure`,
        file: entry.templatePath
      });
    }
  }

  // 5. Theme surface (always, if relevant)
  const themeKey = 'theme';
  if (!seen.has(themeKey)) {
    const themeEntry = catalog.entries.find(e => e.configPath === themeKey);
    if (themeEntry || true) {
      seen.add(themeKey);
      surfaces.push({
        type: 'theme',
        path: 'theme',
        description: 'App-wide theme properties: mainFont, mainFontBold, brandColor, textColor, etc.',
        file: `apps/${app}/src/environments/environment.all.ts`,
        properties: themeEntry?.fields?.map(f => f.name)
      });
    }
  }

  // 6. Top-level environment config surfaces (from catalog)
  for (const catalogEntry of catalog.entries) {
    if (catalogEntry.kind !== 'component-config-group' && catalogEntry.kind !== 'environment-group') continue;
    if (seen.has(catalogEntry.configPath)) continue;
    // Only include if it matches semantic context
    const matchesContext = relevantEntries.some(
      e =>
        e.semanticTags?.some(t => catalogEntry.configPath.includes(t) || t.includes(catalogEntry.configPath)) ||
        e.configMapping === catalogEntry.configPath.replace('components.', '')
    );
    if (!matchesContext) continue;
    seen.add(catalogEntry.configPath);
    surfaces.push({
      type: catalogEntry.kind === 'environment-group' ? 'env-config' : 'env-config',
      path: catalogEntry.configPath,
      description: catalogEntry.summary || `Configure ${catalogEntry.configPath}`,
      file: `apps/${app}/src/environments/environment.all.ts`,
      properties: catalogEntry.fields?.map(f => f.name)
    });
  }

  // 7. Environment interface file (for custom changes)
  const envInterfacePath = 'libs/environment/src/lib/environment/environment.interface.ts';
  if (!seen.has(envInterfacePath)) {
    surfaces.push({
      type: 'code-change',
      path: envInterfacePath,
      description: 'Add new property to the Environment interface for custom configuration',
      file: envInterfacePath
    });
  }

  return surfaces.slice(0, 8);
}

async function suggestWebLlmFix(input: {
  app: string;
  bugContext: string;
  expectedOutcome: string;
  scope: string;
  targetSelector: string;
  fixType: string;
  routeAction: string;
  componentChain: string[];
  topComponents: Array<{ selector: string; symbolName: string; kind: string; score: number }>;
  existingStyleVariableHints: string[];
  cssDiagnosticsText?: string;
  cssFixSuggestions: Array<{ variable: string; currentValue: string; suggestedValue: string; scope: string }>;
  progressCallback?: (report: { text: string; progress: number }) => void;
}): Promise<string | null> {
  const engine = await createWebLlmEngine(input.progressCallback);
  if (!engine) return null;

  try {
    const prompt = buildFixSuggestionPrompt(input);
    const response = await engine.chat.completions.create({
      messages: [{ role: 'user', content: prompt }],
      max_tokens: 256,
      stream: false
    });
    return response.choices[0]?.message?.content || null;
  } catch {
    return null;
  }
}

function createWebLlmChatWidget(context: {
  app: string;
  bugContext: string;
  expectedOutcome: string;
  scope: string;
  relevantComponents: string[];
  targetSelector: string;
  readinessScore: number;
  fixType: string;
  routeAction: string;
  topComponent: string;
  questions: string[];
  hasRecordedSteps: boolean;
  recordedSteps?: AiRecordedStep[];
  recordedDomInteractions?: AiRecordedDomInteraction[];
  cssFixSuggestions?: AiCssFixSuggestion[];
  reasoningContext?: {
    targetElementHint?: AiTargetElementHint | null;
    relevantEntries: Array<{ score: number; sourcePath: string; selector: string; symbolName: string; kind: string }>;
    fixRouting: {
      fixType: string;
      routeAction: string;
      visualIssueType?: string;
      existingStyleVariableHints: string[];
      layoutConstraintHints: string[];
    };
    cssVars?: string[];
  };
  /** Pre-loaded CSS diagnostics from review step — skips lazy load */
  cssDiagnosticsText?: string;
}): { open: () => void; close: () => void; destroy: () => void } {
  if (webLlmChatWidget) {
    webLlmChatWidget.style.display = 'flex';
    return {
      open: () => {
        webLlmChatWidget!.style.display = 'flex';
      },
      close: () => {
        webLlmChatWidget!.style.display = 'none';
      },
      destroy: destroyWebLlmChatWidget
    };
  }

  const root = document.createElement('div');
  root.setAttribute('id', 'usheru-bugfix-chat');
  root.style.cssText =
    'position:fixed;bottom:20px;right:20px;width:380px;height:480px;background:#1e1e2e;color:#cdd6f4;border-radius:12px;box-shadow:0 8px 32px rgba(0,0,0,0.4);display:flex;flex-direction:column;z-index:999999;font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif;font-size:13px;overflow:hidden;';

  // Header
  const header = document.createElement('div');
  header.style.cssText =
    'padding:10px 14px;background:#313244;border-bottom:1px solid #45475a;display:flex;align-items:center;justify-content:space-between;cursor:move;user-select:none;';
  header.innerHTML = '<span style="font-weight:600;font-size:13px;">🔍 Bugfix Assistant</span>';

  const headerActions = document.createElement('div');
  headerActions.style.cssText = 'display:flex;gap:4px;align-items:center;';

  const resetBtn = document.createElement('button');
  resetBtn.innerHTML = '↻';
  resetBtn.style.cssText =
    'background:none;border:none;color:#a6adc8;cursor:pointer;font-size:14px;padding:2px 6px;border-radius:4px;';
  resetBtn.title = 'Reset conversation';
  headerActions.appendChild(resetBtn);

  const closeBtn = document.createElement('button');
  closeBtn.innerHTML = '✕';
  closeBtn.style.cssText =
    'background:none;border:none;color:#a6adc8;cursor:pointer;font-size:14px;padding:2px 6px;border-radius:4px;';
  closeBtn.addEventListener('click', () => {
    root.style.display = 'none';
  });
  headerActions.appendChild(closeBtn);
  header.appendChild(headerActions);
  root.appendChild(header);

  // Draggable
  let isDragging = false,
    dragOffsetX = 0,
    dragOffsetY = 0;
  header.addEventListener('mousedown', e => {
    isDragging = true;
    dragOffsetX = e.clientX - root.getBoundingClientRect().left;
    dragOffsetY = e.clientY - root.getBoundingClientRect().top;
    root.style.right = 'auto';
    root.style.bottom = 'auto';
  });
  document.addEventListener('mousemove', e => {
    if (!isDragging) return;
    root.style.left = `${e.clientX - dragOffsetX}px`;
    root.style.top = `${e.clientY - dragOffsetY}px`;
  });
  document.addEventListener('mouseup', () => {
    isDragging = false;
  });

  // Messages area
  const messages = document.createElement('div');
  messages.style.cssText = 'flex:1;overflow-y:auto;padding:10px;display:flex;flex-direction:column;gap:8px;';
  root.appendChild(messages);

  // System context message
  const sysMsg = document.createElement('div');
  sysMsg.style.cssText =
    'background:#313244;padding:8px 10px;border-radius:8px;font-size:12px;color:#a6adc8;line-height:1.4;';
  let sysHtml = `
    <strong style="color:#89b4fa;">App:</strong> ${context.app}<br>
    <strong style="color:#89b4fa;">Bug:</strong> ${context.bugContext}<br>
    <strong style="color:#89b4fa;">Expected:</strong> ${context.expectedOutcome}<br>
    <strong style="color:#89b4fa;">Scope:</strong> ${context.scope}<br>
    <strong style="color:#89b4fa;">Component:</strong> ${context.relevantComponents.join(' → ')}<br>
    <strong style="color:#89b4fa;">Element:</strong> ${context.targetSelector}
  `;
  if (context.recordedSteps && context.recordedSteps.length > 0) {
    sysHtml += `<br><strong style="color:#89b4fa;">Steps (${context.recordedSteps.length}):</strong> ${context.recordedSteps.map(s => s.path).join(' → ')}`;
  }
  sysMsg.innerHTML = sysHtml;
  messages.appendChild(sysMsg);

  // Readiness score badge
  const scoreColor = context.readinessScore >= 85 ? '#a6e3a1' : context.readinessScore >= 65 ? '#f9e2af' : '#f38ba8';
  const scoreMsg = document.createElement('div');
  scoreMsg.style.cssText = `background:#45475a;padding:8px 10px;border-radius:8px;font-size:13px;line-height:1.4;align-self:flex-start;max-width:90%;`;
  scoreMsg.innerHTML = `<strong style="color:${scoreColor};">Readiness: ${context.readinessScore}%</strong><br><span style="color:#a6adc8;">Fix: ${context.fixType} → ${context.routeAction}<br>Target: &lt;${context.topComponent}&gt;</span>`;
  messages.appendChild(scoreMsg);

  // Welcome message
  const welcome = document.createElement('div');
  welcome.style.cssText =
    'background:#45475a;padding:8px 10px;border-radius:8px;font-size:12px;line-height:1.4;align-self:flex-start;max-width:95%;';
  let welcomeHtml =
    context.readinessScore >= 80
      ? '<strong style="color:#a6e3a1;">Ready.</strong> Type <strong style="color:#f9e2af;">"proceed"</strong> for the JSON payload.'
      : '<strong style="color:#89b4fa;">Hi!</strong> I\'ll help you complete this bug report.';
  if (context.reasoningContext && context.readinessScore < 80) {
    welcomeHtml += `<br><br>Readiness: ${context.readinessScore}%. Ask me anything or type <strong style="color:#f9e2af;">"proceed"</strong> when ready.`;
  }
  welcome.innerHTML = welcomeHtml;
  messages.appendChild(welcome);

  // Recorded interactions — show with kind, 1-based positions, and filtered hover options
  const allRecorded = (context.recordedDomInteractions || []).slice(0, 20);
  const mouseenterInteractions = allRecorded.filter(i => i.kind === 'mouseenter');
  if (allRecorded.length > 0) {
    if (!window.usheru._chatInteractionFilter_raw) {
      window.usheru._chatInteractionFilter_raw = mouseenterInteractions.map(i => i.index);
    }
    const diagMsg = document.createElement('div');
    diagMsg.style.cssText =
      'background:#313244;padding:8px 10px;border-radius:8px;font-size:11px;line-height:1.5;align-self:flex-start;max-width:95%;';
    let html = `<strong style="color:#f9e2af;">${allRecorded.length} interactions recorded</strong><br>`;
    allRecorded.forEach((i, idx) => {
      const label = i.label || i.text || '';
      const kindColor =
        i.kind === 'mouseenter'
          ? '#a6e3a1'
          : i.kind === 'click'
            ? '#89b4fa'
            : i.kind === 'input'
              ? '#f9e2af'
              : i.kind === 'change'
                ? '#f5c2e7'
                : i.kind === 'submit'
                  ? '#fab387'
                  : '#a6adc8';
      html += `[${idx + 1}] <span style="color:${kindColor};">${i.kind}</span> <span style="color:#a6adc8;">#${i.index}</span> ${i.selector || ''}${label ? ' (' + label.slice(0, 40) + ')' : ''}<br>`;
    });
    if (mouseenterInteractions.length > 0) {
      html += `<br><span style="color:#a6adc8;">Reply with:</span><br>`;
      html += `• <strong style="color:#89b4fa;">keep options</strong> — only hover elements with role="option" (search results)<br>`;
      html += `• <strong style="color:#89b4fa;">keep all</strong> — keep every recorded hover<br>`;
      html += `• <strong style="color:#89b4fa;">keep [1, 3]</strong> — keep only the listed hover positions<br>`;
      html += `• <strong style="color:#89b4fa;">remove [2, 5]</strong> — remove specific hover positions<br>`;
      html += `• Or just type <strong style="color:#f9e2af;">"proceed"</strong> to keep the current selection.`;
    }
    diagMsg.innerHTML = html;
    messages.appendChild(diagMsg);
  }

  // Input area
  const inputArea = document.createElement('div');
  inputArea.style.cssText = 'padding:10px;border-top:1px solid #45475a;display:flex;gap:6px;background:#313244;';

  const input = document.createElement('input');
  input.type = 'text';
  input.placeholder = 'Answer or ask anything...';
  input.style.cssText =
    'flex:1;background:#1e1e2e;border:1px solid #45475a;border-radius:6px;padding:6px 10px;color:#cdd6f4;font-size:12px;outline:none;';
  inputArea.appendChild(input);

  const sendBtn = document.createElement('button');
  sendBtn.textContent = '↑';
  sendBtn.style.cssText =
    'background:#89b4fa;color:#1e1e2e;border:none;border-radius:6px;padding:6px 12px;font-weight:600;cursor:pointer;font-size:14px;';
  inputArea.appendChild(sendBtn);
  root.appendChild(inputArea);

  document.body.appendChild(root);
  webLlmChatWidget = root;

  // Loading indicator bar
  const loadingBar = document.createElement('div');
  loadingBar.style.cssText =
    'padding:6px 10px;background:#313244;border-bottom:1px solid #45475a;font-size:11px;color:#a6adc8;display:flex;align-items:center;gap:8px;';
  const loadingBarTrack = document.createElement('div');
  loadingBarTrack.style.cssText = 'flex:1;height:4px;background:#45475a;border-radius:2px;overflow:hidden;';
  const loadingBarFill = document.createElement('div');
  loadingBarFill.style.cssText = 'height:100%;width:0%;background:#89b4fa;border-radius:2px;transition:width 0.2s;';
  loadingBarTrack.appendChild(loadingBarFill);
  const loadingBarText = document.createElement('span');
  loadingBarText.style.cssText = 'min-width:120px;';
  loadingBar.appendChild(loadingBarText);
  loadingBar.appendChild(loadingBarTrack);
  loadingBarText.textContent = 'Connecting...';
  root.insertBefore(loadingBar, root.firstChild.nextSibling);

  onWebLlmLoadProgress(report => {
    loadingBarFill.style.width = `${report.progress}%`;
    loadingBarText.textContent = report.text || 'Loading...';
    if (report.progress >= 100) {
      setTimeout(() => {
        loadingBar.style.display = 'none';
      }, 2000);
    }
  });

  const addMessage = (text: string, isUser: boolean) => {
    const msg = document.createElement('div');
    msg.style.cssText = `padding:8px 10px;border-radius:8px;font-size:12px;line-height:1.5;max-width:95%;align-self:${isUser ? 'flex-end' : 'flex-start'};background:${isUser ? '#89b4fa;color:#1e1e2e;' : '#45475a;'}white-space:pre-wrap;`;

    if (isUser) {
      msg.textContent = text;
    } else {
      let html = text
        .replace(
          /```(\w*)\n([\s\S]*?)```/g,
          '<pre style="background:#1e1e2e;padding:8px;border-radius:6px;overflow-x:auto;margin:6px 0;font-size:11px;"><code>$2</code></pre>'
        )
        .replace(
          /`([^`]+)`/g,
          '<code style="background:#1e1e2e;padding:1px 4px;border-radius:3px;font-size:11px;">$1</code>'
        )
        .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
        .replace(/\*([^*]+)\*/g, '<em>$1</em>')
        .replace(/\n/g, '<br>');
      msg.innerHTML = html;
    }

    messages.appendChild(msg);
    messages.scrollTop = messages.scrollHeight;
  };

  // LLM-driven conversation state
  const conversationHistory: Array<{ role: string; content: string }> = [];
  let conversationTurns = 0;

  // Lazy CSS diagnostics cache (fetched on demand when user asks CSS questions)
  let cssDiagnosticsText: string | null = null;
  let cssFixSuggestions = [...(context.cssFixSuggestions || [])];
  let cssDiagnosticsLoading = false;

  const cssKeywordPattern =
    /color|background|contrast|variable|hover|focus|\bvar\(|brightness|dark|light|grey|gray|rgb|hex|opacity/i;

  const isStructuralVisualIssue = () =>
    ['position', 'alignment', 'spacing', 'size', 'visibility'].includes(
      context.reasoningContext?.fixRouting.visualIssueType || ''
    ) ||
    /\b(icon|left|right|position|align|alignment|spacing|padding|margin|width|height)\b/i.test(
      `${context.bugContext} ${context.expectedOutcome}`
    );

  const findStructuralDiagnosticElement = () => {
    if (!isStructuralVisualIssue()) {
      return null;
    }

    const structuralSelectorPattern =
      /\.p-iconfield|\.p-input-icon|\.p-inputicon|\.p-inputtext|p-iconfield|p-multiselect|p-select|p-dropdown/i;
    const candidates = [...(context.recordedDomInteractions || [])].reverse();
    for (const interaction of candidates) {
      if (!['mouseenter', 'click', 'input'].includes(interaction.kind)) {
        continue;
      }
      const selectors = [interaction.selector, ...(interaction.selectorCandidates || [])].filter(Boolean);
      for (const selector of selectors) {
        if (!structuralSelectorPattern.test(selector)) {
          continue;
        }
        try {
          const el = document.querySelector<HTMLElement>(selector);
          if (el && el.offsetParent !== null) {
            return el;
          }
        } catch {
          // Ignore invalid selector candidates from runtime capture.
        }
      }
    }

    return null;
  };

  const loadCssDiagnostics = async (): Promise<string | null> => {
    if (cssDiagnosticsText !== null) return cssDiagnosticsText;
    if (cssDiagnosticsLoading) return null;
    cssDiagnosticsLoading = true;

    try {
      const structuralEl = findStructuralDiagnosticElement();
      const targetEl =
        structuralEl || (context.targetSelector ? document.querySelector<HTMLElement>(context.targetSelector) : null);
      if (!targetEl) {
        // Fallback: try the first component in the chain
        const fallbackSelector = context.relevantComponents[0];
        const fallbackEl = fallbackSelector ? document.querySelector<HTMLElement>(fallbackSelector) : null;
        if (!fallbackEl) return null;
      }

      const el =
        structuralEl ||
        document.querySelector<HTMLElement>(context.targetSelector) ||
        document.querySelector<HTMLElement>(context.relevantComponents[0]);
      if (!el) return null;

      if (typeof window.usheru?.diagnoseElement !== 'function') return null;
      const report = await window.usheru.diagnoseElement(el, context.app, context.bugContext);
      if (!report) return null;

      const r = report as any;
      cssFixSuggestions = Array.isArray(r.fixSuggestions) ? r.fixSuggestions : [];
      const lines: string[] = [];
      lines.push(`--- Live CSS Diagnostics for <${r.target.tagName}> ---`);
      lines.push(`Chain: ${r.target.componentChain.join(' → ')}`);

      if (r.contrastIssues?.length > 0) {
        for (const issue of r.contrastIssues) {
          lines.push(
            `⚠ Contrast: ${issue.propA}=${r.computedColors[issue.propA]} vs ${issue.propB}=${r.computedColors[issue.propB]} ratio ${issue.ratio.toFixed(2)}`
          );
        }
      }

      if (r.computedColors && Object.keys(r.computedColors).length > 0) {
        lines.push(
          `Computed: ${Object.entries(r.computedColors)
            .map(([k, v]) => `${k}=${v}`)
            .join(', ')}`
        );
      }

      for (const host of r.hosts || []) {
        lines.push(`<${host.tagName}> vars:`);
        for (const v of host.variables || []) {
          const icon = v.kind === 'bridge' ? '🔗' : v.kind === 'consumed' ? '📥' : '📤';
          lines.push(`  ${icon} ${v.name}: ${v.value}`);
        }
        if (host.mapEntry) lines.push(`  (map: ${host.mapEntry.symbolName})`);
      }

      if (r.fixSuggestions?.length > 0) {
        lines.push(`Fix suggestions:`);
        for (const fix of r.fixSuggestions) {
          lines.push(
            `  Set ${fix.variable}: ${fix.suggestedValue} on <${fix.scope}> → ${fix.primengToken || fix.reason}`
          );
        }
      }

      cssDiagnosticsText = lines.join('\n');
      return cssDiagnosticsText;
    } catch {
      return null;
    }
  };

  const generateStructuralCssFix = (): AiCssFixSuggestion[] => {
    let el = findStructuralDiagnosticElement();
    if (!el && context.targetSelector) {
      el = document.querySelector<HTMLElement>(context.targetSelector);
    }
    if (!el || !(el instanceof HTMLElement)) return [];
    if (!isStructuralVisualIssue()) return [];

    // Derive scope from component chain (innermost-first), then DOM fallback
    const chain = context.relevantComponents || [];
    const GENERIC_TAGS = ['app-root', 'body', 'html', 'div', 'span'];
    const nonGeneric = chain.filter(t => !GENERIC_TAGS.includes(t));
    // nonGeneric is innermost-first; everything except the innermost element becomes the ancestor scope
    let ancestors = nonGeneric.slice(1).reverse(); // CSS order (outermost-first)
    if (ancestors.length === 0) {
      const p = el.parentElement;
      if (p) {
        const found = p.closest<HTMLElement>(
          'ush-movies-page, ush-movie-list-page, ush-custom-page, ush-movies-catalogue, ush-movie-search-page, ush-search-page'
        );
        if (found) ancestors = [found.tagName.toLowerCase()];
      }
    }
    const scope = ancestors.length > 0 ? `${ancestors.join(' ')} ` : '';
    const outermostTag = ancestors[0] || '';
    const combined = `${context.bugContext} ${context.expectedOutcome}`.toLowerCase();
    const compStyle = window.getComputedStyle(el);
    const elTag = el.tagName.toLowerCase();

    // --- Detect known interactive surfaces from the DOM ---
    const iconfield =
      elTag === 'p-iconfield'
        ? el
        : el.closest<HTMLElement>('p-iconfield, [class*="p-iconfield"], [class*="p-input-icon"]');
    const inOverlay = !!el.closest('.p-select-overlay, .p-multiselect-panel, p-overlay, .p-overlay, [role="listbox"]');

    // Walk parent chain for known surfaces (classes matching tokens in bugContext)
    const knownSurface = (() => {
      let cur = iconfield || el;
      while (cur) {
        const match = !cur.classList
          ? null
          : Array.from(cur.classList).find(c => {
              if (inOverlay && /overlay|dropdown|select|multiselect|panel/i.test(c)) return false;
              const segments = c.split(/[-_]/);
              return segments.some(s => combined.includes(s.toLowerCase()) && s.length > 3);
            });
        if (match) return match;
        cur = cur.parentElement;
      }
      return inOverlay ? 'overlay' : '';
    })();

    // --- Build all relevant fix suggestions ---

    const suggestions: AiCssFixSuggestion[] = [];

    // 1. Icon position in p-iconfield / p-input-icon (old and new PrimeNG)
    if (iconfield) {
      const iconClasses = iconfield.className || '';
      const leftClass = iconClasses.includes('p-iconfield-left') || iconClasses.includes('p-input-icon-left');
      const rightClass = iconClasses.includes('p-iconfield-right') || iconClasses.includes('p-input-icon-right');
      const hasIconLeft = /\b(icon|search).*\b(left|left.?side)\b|\b(left|left.?side)\b.*\b(icon|search)\b/i.test(
        context.bugContext
      );
      const hasIconRight = /\b(icon|search).*\b(right|right.?side)\b|\b(right|right.?side)\b.*\b(icon|search)\b/i.test(
        context.bugContext
      );
      const wantRight = /\b(?:on the\s+)?right\b/.test(context.expectedOutcome);
      const wantLeft = /\b(?:on the\s+)?left\b/.test(context.expectedOutcome);

      // Detect theme variable: check stylesheets for var(--*) on p-iconfield/p-inputicon rules
      let themeVar = '';
      try {
        for (const sheet of Array.from(document.styleSheets)) {
          try {
            const rules = sheet.cssRules || [];
            for (const rule of Array.from(rules)) {
              if (!(rule instanceof CSSStyleRule)) continue;
              const selText = rule.selectorText;
              if (
                !selText.includes('p-iconfield') &&
                !selText.includes('p-inputicon') &&
                !selText.includes('p-input-icon')
              )
                continue;
              const leftVal = rule.style.getPropertyValue('left');
              if (leftVal && leftVal.includes('var(--')) {
                const match = leftVal.match(/var\((--[\w-]+)/);
                if (match) {
                  themeVar = match[1];
                  break;
                }
              }
              for (const p of ['right', 'margin-left', 'margin-right']) {
                const v = rule.style.getPropertyValue(p);
                if (v && v.includes('var(--')) {
                  const match = v.match(/var\((--[\w-]+)/);
                  if (match) {
                    themeVar = match[1];
                    break;
                  }
                }
              }
              if (themeVar) break;
            }
          } catch {
            /* cross-origin sheet */
          }
          if (themeVar) break;
        }
      } catch {
        /* sheet enumeration */
      }

      // Also check computed style of icon element directly as fallback
      if (!themeVar) {
        const iconEl = iconfield.querySelector<HTMLElement>(
          '.p-inputicon, .p-input-icon, i, svg, [class*="p-inputicon"]'
        );
        if (iconEl) {
          const iconStyle = window.getComputedStyle(iconEl);
          for (const prop of ['left', 'right', 'order', 'margin-left', 'margin-right']) {
            const val = iconStyle.getPropertyValue(prop);
            if (val.startsWith('var(--')) {
              themeVar = val.match(/var\((--[\w-]+)/)?.[1] || '';
              break;
            }
          }
        }
        if (!themeVar) {
          const fieldStyle = window.getComputedStyle(iconfield);
          for (const prop of ['left', 'right']) {
            const val = fieldStyle.getPropertyValue(prop);
            if (val.startsWith('var(--')) {
              themeVar = val.match(/var\((--[\w-]+)/)?.[1] || '';
              break;
            }
          }
        }
      }

      // Build icon selector dynamically from detected DOM
      const iconEl = iconfield.querySelector<HTMLElement>('.p-inputicon, .p-input-icon, i, svg');
      const iconPosition = iconEl ? window.getComputedStyle(iconEl).position : '';
      const iconTag = iconEl?.tagName.toLowerCase() || 'i';
      const iconSuffix = iconEl && iconEl.className ? `.${iconEl.className.split(' ')[0]}` : '';
      const iconSel = iconSuffix ? `${iconTag}${iconSuffix}` : iconTag;

      // Icon is on left, should be on right
      if ((leftClass || hasIconLeft) && (wantRight || (!wantLeft && hasIconLeft))) {
        // Priority A: Theme variable route — use existing CSS var if the theme wires one
        if (themeVar) {
          const themeCss = outermostTag
            ? `${scope}${iconfield.tagName.toLowerCase()} { ${themeVar}: auto; }\n${scope}${iconfield.tagName.toLowerCase()} ${iconSel} { right: 0.5rem; }`
            : `${iconfield.tagName.toLowerCase()} ${iconSel} { left: auto !important; right: 0.5rem !important; }`;
          suggestions.push({
            variable: 'rawCss',
            currentValue: `${themeVar}: <theme fallback>`,
            suggestedValue: themeCss,
            scope: outermostTag || iconfield.tagName.toLowerCase(),
            reason: `Theme variable ${themeVar} found. Override it to reposition.`,
            rawCss: themeCss
          });
        }

        // Priority B: Direct position toggle on the positioned child
        suggestions.push({
          variable: 'rawCss',
          currentValue: 'icon on left',
          suggestedValue: `${scope}${iconfield.tagName.toLowerCase()} ${iconSel} { left: auto !important; right: 0.5rem !important; }`,
          scope: outermostTag || iconfield.tagName.toLowerCase(),
          reason: `Toggle icon from left to right${scope ? ` scoped to ${outermostTag}` : ''}`,
          rawCss: `${scope}${iconfield.tagName.toLowerCase()} ${iconSel} { left: auto !important; right: 0.5rem !important; }`
        });

        // Priority C: Old PrimeNG class-based approach (only when class detected)
        if (iconClasses.includes('p-input-icon-left')) {
          suggestions.push({
            variable: 'rawCss',
            currentValue: 'icon on left',
            suggestedValue: `${scope}${iconfield.tagName.toLowerCase()} { left: auto; right: 0.75rem; }`,
            scope: outermostTag || iconfield.tagName.toLowerCase(),
            reason: 'Switch icon from left to right (old PrimeNG .p-input-icon-left)',
            rawCss: `${scope}${iconfield.tagName.toLowerCase()} { left: auto; right: 0.75rem !important; }`
          });
        }

        // Priority D: Flex-based approach — only when icon is NOT absolutely positioned
        if (iconPosition !== 'absolute' && iconPosition !== 'fixed') {
          suggestions.push({
            variable: 'rawCss',
            currentValue: 'icon on left',
            suggestedValue: `${scope}${iconfield.tagName.toLowerCase()} { flex-direction: row-reverse; order: 1; }`,
            scope: outermostTag || iconfield.tagName.toLowerCase(),
            reason: `Flip icon from left to right${scope ? ` scoped to ${outermostTag}` : ''}`,
            rawCss: `${scope}${iconfield.tagName.toLowerCase()} { flex-direction: row-reverse; } ${scope}${iconfield.tagName.toLowerCase()} ${iconSel} { order: 1; margin-left: 0; margin-right: 0.5rem; }`
          });
        }
      }

      // Icon is on right, should be on left
      if ((rightClass || hasIconRight) && (wantLeft || (!wantRight && hasIconRight))) {
        if (iconClasses.includes('p-input-icon-right')) {
          suggestions.push({
            variable: 'rawCss',
            currentValue: 'icon on right',
            suggestedValue: `${scope}${iconfield.tagName.toLowerCase()} { left: 0.75rem; right: auto; }`,
            scope: outermostTag || iconfield.tagName.toLowerCase(),
            reason: 'Switch icon from right to left (old PrimeNG .p-input-icon-right)',
            rawCss: `${scope}${iconfield.tagName.toLowerCase()} { left: 0.75rem !important; right: auto !important; }`
          });
        }
        if (iconPosition !== 'absolute' && iconPosition !== 'fixed') {
          suggestions.push({
            variable: 'rawCss',
            currentValue: 'icon on right',
            suggestedValue: `${scope}${iconfield.tagName.toLowerCase()} { flex-direction: row; }`,
            scope: outermostTag || iconfield.tagName.toLowerCase(),
            reason: `Flip icon from right to left${scope ? ` scoped to ${outermostTag}` : ''}`,
            rawCss: `${scope}${iconfield.tagName.toLowerCase()} { flex-direction: row; } ${scope}${iconfield.tagName.toLowerCase()} ${iconSel} { order: -1; }`
          });
        }
      }

      // Existing app anchor hint for discovered surface
      if (knownSurface && knownSurface !== 'overlay') {
        const anchorCss = `.${knownSurface} ${iconfield.tagName.toLowerCase()} ${iconSel} { left: auto !important; right: 0.5rem !important; }`;
        suggestions.push({
          variable: 'rawCss',
          currentValue: 'existing anchor',
          suggestedValue: anchorCss,
          scope: `.${knownSurface}`,
          reason: `Existing surface ".${knownSurface}" detected. Consider targeting this anchor in styles.scss.`,
          rawCss: anchorCss
        });
      }
    }

    // 2. Flex alignment / justify on any flex container
    if (compStyle.display.includes('flex') && /\b(align|justify|position|order)\b/.test(combined)) {
      const currentJustify = compStyle.justifyContent;
      const currentAlign = compStyle.alignItems;
      const toggles: string[] = [];

      if (/\b(center|centered|middle)\b/.test(combined)) {
        if (currentJustify !== 'center') toggles.push(`justify-content: center`);
        if (currentAlign !== 'center') toggles.push(`align-items: center`);
      } else if (/left/.test(combined) && currentJustify !== 'flex-start') {
        toggles.push(`justify-content: flex-start`);
      } else if (/right/.test(combined) && currentJustify !== 'flex-end') {
        toggles.push(`justify-content: flex-end`);
      } else {
        toggles.push(`justify-content: ${currentJustify === 'flex-start' ? 'flex-end' : 'flex-start'}`);
      }

      if (toggles.length > 0) {
        const css = `${scope}${elTag} { ${toggles.join('; ')}; }`;
        suggestions.push({
          variable: 'rawCss',
          currentValue: `flex ${currentJustify}`,
          suggestedValue: css,
          scope: outermostTag || elTag,
          reason: `Toggle flex alignment on ${elTag}${scope ? ` (scoped to ${outermostTag})` : ''}`,
          rawCss: css
        });
      }
    }

    // 3. Text alignment on block/inline-block containers
    if (/\b(text.?align|align)\b/.test(combined)) {
      const textAlign = compStyle.textAlign;
      const isBlock =
        compStyle.display === 'block' || compStyle.display === 'inline-block' || compStyle.display.endsWith('flex');
      if (isBlock) {
        const targetAlign = /center/.test(combined)
          ? 'center'
          : /right/.test(combined)
            ? 'right'
            : /left/.test(combined)
              ? 'left'
              : textAlign === 'left'
                ? 'right'
                : 'left';
        if (targetAlign && targetAlign !== textAlign) {
          const css = `${scope}${elTag} { text-align: ${targetAlign}; }`;
          suggestions.push({
            variable: 'rawCss',
            currentValue: `text-align: ${textAlign}`,
            suggestedValue: css,
            scope: outermostTag || elTag,
            reason: `Change text alignment from ${textAlign} to ${targetAlign}`,
            rawCss: css
          });
        }
      }
    }

    // 4. Float positioning
    if (/\bfloat\b/.test(combined)) {
      const currentFloat = compStyle.float;
      const targetFloat = /right/.test(combined)
        ? 'right'
        : /left/.test(combined)
          ? 'left'
          : currentFloat === 'left'
            ? 'right'
            : 'left';
      if (targetFloat !== currentFloat) {
        const css = `${scope}${elTag} { float: ${targetFloat}; }`;
        suggestions.push({
          variable: 'rawCss',
          currentValue: `float: ${currentFloat}`,
          suggestedValue: css,
          scope: outermostTag || elTag,
          reason: `Change float from ${currentFloat} to ${targetFloat}`,
          rawCss: css
        });
      }
    }

    // 5. Generic left/right position for positioned elements
    if (/\b(left|right).*\b(position|moving)\b|\b(position|moving)\b.*\b(left|right)\b/.test(combined)) {
      const pos = compStyle.position;
      if (pos === 'relative' || pos === 'absolute' || pos === 'fixed') {
        const wantsRight =
          /\b(?:on the\s+)?right\b/.test(context.expectedOutcome) || /\braise|increase|move.*right\b/.test(combined);
        const wantsLeft =
          /\b(?:on the\s+)?left\b/.test(context.expectedOutcome) || /\blower|decrease|move.*left\b/.test(combined);
        if (wantsRight) {
          const css = `${scope}${elTag} { left: auto; right: 0; }`;
          suggestions.push({
            variable: 'rawCss',
            currentValue: `left: ${compStyle.left} / right: ${compStyle.right} (${pos})`,
            suggestedValue: css,
            scope: outermostTag || elTag,
            reason: `Toggle positioned element from left-anchored to right-anchored`,
            rawCss: css
          });
        }
        if (wantsLeft) {
          const css = `${scope}${elTag} { right: auto; left: 0; }`;
          suggestions.push({
            variable: 'rawCss',
            currentValue: `right: ${compStyle.right} / left: ${compStyle.left} (${pos})`,
            suggestedValue: css,
            scope: outermostTag || elTag,
            reason: `Toggle positioned element from right-anchored to left-anchored`,
            rawCss: css
          });
        }
      }
    }

    return suggestions;
  };

  const renderFixSuggestionCards = (suggestions: AiCssFixSuggestion[]) => {
    if (suggestions.length === 0) {
      return false;
    }

    const msg = document.createElement('div');
    msg.style.cssText =
      'padding:8px 10px;border-radius:8px;font-size:12px;line-height:1.5;max-width:95%;align-self:flex-start;background:#45475a;';
    msg.innerHTML =
      '<div style="font-weight:600;margin-bottom:6px;">✅ Fix suggestion — adjust the value and apply:</div>';

    for (const fix of suggestions) {
      const rawCss = String(fix.rawCss || '').trim();
      const card = document.createElement('div');
      card.style.cssText = 'margin:6px 0;padding:8px;background:#313244;border-radius:6px;';
      card.innerHTML = `<div style="font-size:12px;margin-bottom:6px;"><strong>${fix.variable}</strong>: ${fix.suggestedValue} on <code>${fix.scope}</code></div>`;
      const row = document.createElement('div');
      row.style.cssText = 'display:flex;gap:4px;align-items:flex-start;';
      const valueInput = rawCss ? document.createElement('textarea') : document.createElement('input');
      const isHexColor = !rawCss && /^#[0-9a-f]{6}$/i.test(fix.suggestedValue);
      if (valueInput instanceof HTMLInputElement) {
        valueInput.type = isHexColor ? 'color' : 'text';
      }
      valueInput.id = 'ush-fix-value-' + fix.variable.replace(/\s/g, '');
      valueInput.value = rawCss || fix.suggestedValue;
      valueInput.style.cssText = rawCss
        ? 'min-width:220px;min-height:78px;flex:1;background:#1e1e2e;border:1px solid #585b70;border-radius:4px;color:#cdd6f4;padding:5px 6px;font-size:11px;font-family:monospace;'
        : isHexColor
          ? 'width:32px;height:28px;border:1px solid #585b70;border-radius:4px;background:transparent;padding:0;cursor:pointer;'
          : 'min-width:90px;flex:1;background:#1e1e2e;border:1px solid #585b70;border-radius:4px;color:#cdd6f4;padding:5px 6px;font-size:12px;';
      const applyBtn = document.createElement('button');
      applyBtn.textContent = 'Apply';
      applyBtn.style.cssText =
        'background:#a6e3a1;color:#1e1e2e;border:none;border-radius:4px;padding:4px 10px;font-size:12px;cursor:pointer;font-weight:600;';
      applyBtn.onclick = () => {
        if (rawCss) {
          window.usheru.injectFix({ rawCss: valueInput.value });
        } else {
          window.usheru.injectFix({ selector: fix.scope, property: fix.variable, value: valueInput.value });
        }
        applyBtn.textContent = 'Applied ✓';
        applyBtn.style.background = '#585b70';
        applyBtn.style.color = '#cdd6f4';
        setTimeout(() => {
          applyBtn.textContent = 'Apply';
          applyBtn.style.background = '#a6e3a1';
          applyBtn.style.color = '#1e1e2e';
        }, 2000);
      };
      const undoBtn = document.createElement('button');
      undoBtn.textContent = 'Undo';
      undoBtn.style.cssText =
        'background:#f38ba8;color:#1e1e2e;border:none;border-radius:4px;padding:4px 10px;font-size:12px;cursor:pointer;';
      undoBtn.onclick = () => {
        document.getElementById('ush-bugfix-inject')?.remove();
        window.usheru._lastAppliedFix = null;
      };
      row.appendChild(valueInput);
      row.appendChild(applyBtn);
      row.appendChild(undoBtn);
      card.appendChild(row);
      if (fix.reason) {
        const reason = document.createElement('div');
        reason.style.cssText = 'font-size:11px;color:#a6adc8;margin-top:6px;';
        reason.textContent = fix.reason;
        card.appendChild(reason);
      }
      msg.appendChild(card);
    }

    messages.appendChild(msg);
    messages.scrollTop = messages.scrollHeight;
    return true;
  };

  const suggestFixFromContext = () => {
    const text = `${context.bugContext} ${context.expectedOutcome}`.toLowerCase();
    const suggestions: Array<{ property: string; value: string }> = [];

    // Icon position: icon on left/right side
    if (/\b(icon|search)\b.*\b(left|right)\b|\b(left|right)\b.*\b(icon|search)\b/.test(text)) {
      if (/\bright\b/.test(text)) {
        suggestions.push({ property: 'flex-direction', value: 'row-reverse' });
        suggestions.push({ property: 'order', value: '1' });
        suggestions.push({ property: 'float', value: 'right' });
        if (text.includes('multiselect') || text.includes('select') || text.includes('dropdown')) {
          suggestions.push({ property: 'left', value: 'auto' });
        }
      } else {
        suggestions.push({ property: 'flex-direction', value: 'row' });
        suggestions.push({ property: 'order', value: '0' });
        suggestions.push({ property: 'float', value: 'left' });
      }
    }

    // Alignment
    if (/\b(align|center|left|right|justify)\b/.test(text)) {
      if (/\bcenter\b/.test(text)) {
        suggestions.push({ property: 'text-align', value: 'center' });
        suggestions.push({ property: 'justify-content', value: 'center' });
        suggestions.push({ property: 'align-items', value: 'center' });
      }
    }

    // Spacing
    if (/\b(padding|margin|gap|space|spacing)\b/.test(text)) {
      if (/\b(too much|too large|excess|extra|reduce|less|decrease)\b/.test(text)) {
        suggestions.push({ property: 'padding', value: '0' });
        suggestions.push({ property: 'margin', value: '0' });
      } else if (/\b(too little|too small|missing|no space|add|increase|more)\b/.test(text)) {
        suggestions.push({ property: 'padding', value: '8px' });
        suggestions.push({ property: 'gap', value: '8px' });
      }
    }

    // Visibility
    if (/\b(hide|hidden|visible|show|display|appear|disappear)\b/.test(text)) {
      if (/\b(hide|hidden|disappear|not visible)\b/.test(text)) {
        suggestions.push({ property: 'display', value: 'none' });
      } else {
        suggestions.push({ property: 'display', value: 'flex' });
        suggestions.push({ property: 'visibility', value: 'visible' });
      }
    }

    return suggestions;
  };

  const renderCustomFixForm = () => {
    const selector = context.targetSelector || '';
    const hints = suggestFixFromContext();
    const defaultHint = hints[0] || { property: '', value: '' };

    const msg = document.createElement('div');
    msg.style.cssText =
      'padding:10px;border-radius:8px;font-size:12px;line-height:1.5;max-width:95%;align-self:flex-start;background:#45475a;';
    msg.innerHTML = '<div style="font-weight:600;margin-bottom:8px;">Apply a custom CSS fix:</div>';

    const fields = [
      { label: 'Selector', id: 'ush-custom-selector', placeholder: 'e.g. p-multiselect .p-iconfield', value: selector },
      { label: 'Property', id: 'ush-custom-property', placeholder: 'e.g. flex-direction', value: defaultHint.property },
      { label: 'Value', id: 'ush-custom-value', placeholder: 'e.g. row-reverse', value: defaultHint.value }
    ];

    for (const f of fields) {
      const row = document.createElement('div');
      row.style.cssText = 'display:flex;align-items:center;gap:6px;margin-bottom:6px;';
      const label = document.createElement('label');
      label.htmlFor = f.id;
      label.textContent = f.label;
      label.style.cssText = 'width:60px;font-size:12px;font-weight:600;color:#cdd6f4;flex-shrink:0;';
      const input = document.createElement('input');
      input.id = f.id;
      input.type = 'text';
      input.placeholder = f.placeholder;
      input.value = f.value;
      input.style.cssText =
        'flex:1;background:#1e1e2e;border:1px solid #585b70;border-radius:4px;color:#cdd6f4;padding:5px 6px;font-size:12px;';
      row.appendChild(label);
      row.appendChild(input);
      msg.appendChild(row);
    }

    // Show alternative hints as quick-apply pills
    if (hints.length > 1) {
      const hintRow = document.createElement('div');
      hintRow.style.cssText = 'display:flex;flex-wrap:wrap;gap:4px;margin-bottom:6px;';
      for (let i = 1; i < hints.length; i++) {
        const hint = hints[i];
        const pill = document.createElement('button');
        pill.textContent = `${hint.property}: ${hint.value}`;
        pill.style.cssText =
          'background:#585b70;color:#cdd6f4;border:none;border-radius:12px;padding:3px 8px;font-size:11px;cursor:pointer;';
        pill.onclick = () => {
          const propInput = document.getElementById('ush-custom-property') as HTMLInputElement;
          const valInput = document.getElementById('ush-custom-value') as HTMLInputElement;
          if (propInput) propInput.value = hint.property;
          if (valInput) valInput.value = hint.value;
        };
        hintRow.appendChild(pill);
      }
      msg.appendChild(hintRow);
    }

    const btnRow = document.createElement('div');
    btnRow.style.cssText = 'display:flex;gap:6px;margin-top:4px;flex-wrap:wrap;';

    const applyBtn = document.createElement('button');
    applyBtn.textContent = 'Apply & Verify';
    applyBtn.style.cssText =
      'background:#a6e3a1;color:#1e1e2e;border:none;border-radius:4px;padding:5px 12px;font-size:12px;cursor:pointer;font-weight:600;';
    applyBtn.onclick = () => {
      const sel = (document.getElementById('ush-custom-selector') as HTMLInputElement)?.value?.trim();
      const prop = (document.getElementById('ush-custom-property') as HTMLInputElement)?.value?.trim();
      const val = (document.getElementById('ush-custom-value') as HTMLInputElement)?.value?.trim();
      if (!sel || !prop || !val) {
        applyBtn.textContent = 'Fill all fields';
        applyBtn.style.background = '#f38ba8';
        setTimeout(() => {
          applyBtn.textContent = 'Apply & Verify';
          applyBtn.style.background = '#a6e3a1';
        }, 1500);
        return;
      }
      window.usheru.injectFix({ selector: sel, property: prop, value: val });
      applyBtn.textContent = 'Applied ✓';
      applyBtn.style.background = '#585b70';
      applyBtn.style.color = '#cdd6f4';
    };

    const undoBtn = document.createElement('button');
    undoBtn.textContent = 'Undo';
    undoBtn.style.cssText =
      'background:#f38ba8;color:#1e1e2e;border:none;border-radius:4px;padding:5px 12px;font-size:12px;cursor:pointer;';
    undoBtn.onclick = () => {
      document.getElementById('ush-bugfix-inject')?.remove();
      window.usheru._lastAppliedFix = null;
      addMessage(
        'Fix removed. Type <strong style="color:#f9e2af;">"fix"</strong> to try again, or type a CSS rule directly.',
        false
      );
    };

    btnRow.appendChild(applyBtn);
    btnRow.appendChild(undoBtn);
    msg.appendChild(btnRow);

    messages.appendChild(msg);
    messages.scrollTop = messages.scrollHeight;
  };

  const buildFollowUpPrompt = async (userMessage: string): Promise<string> => {
    const rc = context.reasoningContext;
    let prompt = `QA assistant for a CSS bug report. Bug: "${context.bugContext}". Expected: "${context.expectedOutcome}". Scope: ${context.scope}. Component: ${context.relevantComponents.join(' → ')}. Fix: ${rc?.fixRouting.fixType} → ${rc?.fixRouting.routeAction}.`;

    // Use pre-loaded CSS diagnostics from review step, or lazy-load on demand
    if (context.cssDiagnosticsText) {
      prompt += `\n${context.cssDiagnosticsText}`;
      cssDiagnosticsText = context.cssDiagnosticsText;
    } else if (cssKeywordPattern.test(userMessage) && !cssDiagnosticsText && !cssDiagnosticsLoading) {
      const diag = await loadCssDiagnostics();
      if (diag) {
        prompt += `\n${diag}`;
      }
    } else if (cssDiagnosticsText) {
      prompt += `\n${cssDiagnosticsText}`;
    }

    if (conversationTurns === 0) {
      if (cssDiagnosticsText && cssDiagnosticsText.includes('Fix suggestions:')) {
        prompt += `\nCSS diagnostics found a fix. Reply with the concrete CSS variable fix and the injectFix command. Then say "Ready — type proceed." Maximum 2 sentences.`;
      } else if (cssDiagnosticsText) {
        prompt += `\nCSS diagnostics ran — no hover contrast detected. Reply: "Ready — type proceed." Maximum 1 sentence.`;
      } else {
        prompt += `\nReply: "Ready — type proceed." Maximum 1 sentence.`;
      }
    } else {
      if (/diagnos|inspect|check|hover|trigger|live/i.test(userMessage)) {
        prompt += `\nUser asked for live diagnosis. Reply: "Call window.usheru.diagnoseElement($0) while hovering the element. To test a fix: window.usheru.injectFix({ selector: '...', property: '--css-variable', value: 'new-color' })". Maximum 1 sentence.`;
      } else {
        prompt += `\nUser said: "${userMessage}". Reply with ONE short follow-up question if still missing something important. Otherwise say: "Ready — type proceed." Maximum 1 sentence.`;
      }
    }

    return prompt;
  };

  const askFollowUp = async (userMessage?: string) => {
    const loadingMsg = document.createElement('div');
    loadingMsg.style.cssText = 'padding:8px 10px;border-radius:8px;font-size:12px;color:#a6adc8;align-self:flex-start;';
    loadingMsg.textContent = conversationTurns === 0 ? 'Thinking...' : 'Reviewing...';
    messages.appendChild(loadingMsg);
    messages.scrollTop = messages.scrollHeight;

    const engine = await createWebLlmEngine();
    if (!engine) {
      loadingMsg.remove();
      return;
    }

    try {
      const prompt = await buildFollowUpPrompt(userMessage || '');
      const systemMessages: Array<{ role: string; content: string }> = [{ role: 'system', content: prompt }];
      if (conversationHistory.length === 0) {
        conversationHistory.push({ role: 'user', content: 'Review this bug report.' });
      }
      if (conversationHistory.length > 0) {
        systemMessages.push(...conversationHistory.slice(-6));
      }

      const response = await engine.chat.completions.create({
        messages: systemMessages,
        max_tokens: 128,
        stream: false
      });

      const content = response.choices[0]?.message?.content;
      loadingMsg.remove();

      if (content) {
        conversationHistory.push({ role: 'assistant', content });
        addMessage(content, false);
      }
    } catch (err) {
      loadingMsg.remove();
      // Follow-up question failed silently
    }
  };

  if (context.readinessScore >= 80) {
    loadingBar.style.display = 'none';
    if (renderFixSuggestionCards(cssFixSuggestions)) {
      // Structured diagnostics already rendered.
    } else if (context.cssDiagnosticsText) {
      const hasFix = context.cssDiagnosticsText.includes('Fix suggestions:');
      if (hasFix) {
        const fixLines = context.cssDiagnosticsText.split('\n').filter(l => l.includes('Set '));
        const msg = document.createElement('div');
        msg.style.cssText =
          'padding:8px 10px;border-radius:8px;font-size:12px;line-height:1.5;max-width:95%;align-self:flex-start;background:#45475a;';
        msg.innerHTML =
          '<div style="font-weight:600;margin-bottom:6px;">✅ Fix suggestion — adjust the value and apply:</div>';
        for (const line of fixLines) {
          const colonIdx = line.indexOf(':');
          const onIdx = line.indexOf(' on <');
          const closeIdx = line.lastIndexOf('>');
          if (colonIdx === -1 || onIdx === -1) continue;
          const prop = line.substring(line.indexOf('Set ') + 4, colonIdx).trim();
          const val = line.substring(colonIdx + 2, onIdx).trim();
          const scope = line.substring(onIdx + 5, closeIdx).trim();
          const card = document.createElement('div');
          card.style.cssText = 'margin:6px 0;padding:8px;background:#313244;border-radius:6px;';
          card.innerHTML = `<div style="font-size:12px;margin-bottom:6px;"><strong>${prop}</strong>: ${val} on <code>${scope}</code></div>`;
          const row = document.createElement('div');
          row.style.cssText = 'display:flex;gap:4px;align-items:center;';
          const valueInput = document.createElement('input');
          const isHexColor = /^#[0-9a-f]{6}$/i.test(val);
          valueInput.type = isHexColor ? 'color' : 'text';
          valueInput.id = 'ush-fix-value-' + prop.replace(/\s/g, '');
          valueInput.value = val;
          valueInput.style.cssText = isHexColor
            ? 'width:32px;height:28px;border:1px solid #585b70;border-radius:4px;background:transparent;padding:0;cursor:pointer;'
            : 'min-width:90px;flex:1;background:#1e1e2e;border:1px solid #585b70;border-radius:4px;color:#cdd6f4;padding:5px 6px;font-size:12px;';
          const applyBtn = document.createElement('button');
          applyBtn.textContent = 'Apply';
          applyBtn.style.cssText =
            'background:#a6e3a1;color:#1e1e2e;border:none;border-radius:4px;padding:4px 10px;font-size:12px;cursor:pointer;font-weight:600;';
          applyBtn.onclick = () => {
            window.usheru.injectFix({ selector: scope, property: prop, value: valueInput.value });
            applyBtn.textContent = 'Applied ✓';
            applyBtn.style.background = '#585b70';
            applyBtn.style.color = '#cdd6f4';
            setTimeout(() => {
              applyBtn.textContent = 'Apply';
              applyBtn.style.background = '#a6e3a1';
              applyBtn.style.color = '#1e1e2e';
            }, 2000);
          };
          const undoBtn = document.createElement('button');
          undoBtn.textContent = 'Undo';
          undoBtn.style.cssText =
            'background:#f38ba8;color:#1e1e2e;border:none;border-radius:4px;padding:4px 10px;font-size:12px;cursor:pointer;';
          undoBtn.onclick = () => {
            document.getElementById('ush-bugfix-inject')?.remove();
            window.usheru._lastAppliedFix = null;
          };
          row.appendChild(valueInput);
          row.appendChild(applyBtn);
          row.appendChild(undoBtn);
          card.appendChild(row);
          msg.appendChild(card);
        }
        messages.appendChild(msg);
        messages.scrollTop = messages.scrollHeight;
      } else if (isStructuralVisualIssue()) {
        const structuralFixes = generateStructuralCssFix();
        if (structuralFixes.length > 0) {
          renderFixSuggestionCards(structuralFixes);
        } else {
          addMessage(
            '🔍 CSS diagnostics ran — no fix suggestions to test.<br>Type <strong style="color:#f9e2af;">"fix"</strong> to propose a structural CSS fix, or "proceed" for the CI payload.',
            false
          );
        }
      } else {
        addMessage(
          '🔍 CSS diagnostics ran — no fix suggestions to test.<br>Or type "proceed" for the CI payload.',
          false
        );
      }
    } else if (isStructuralVisualIssue()) {
      const structuralFixes = generateStructuralCssFix();
      if (structuralFixes.length > 0) {
        renderFixSuggestionCards(structuralFixes);
      } else {
        addMessage(
          'Review complete. Type "proceed" for the CI payload, or <strong style="color:#f9e2af;">"fix"</strong> to propose a structural CSS fix.',
          false
        );
      }
    } else {
      addMessage('Review complete. Type "proceed" for the CI payload.', false);
    }
  } else {
    loadingBar.style.display = 'flex';
    void askFollowUp();
  }

  const initialContext = {
    app: context.app,
    scope: context.scope,
    bugContext: context.bugContext,
    expectedOutcome: context.expectedOutcome,
    fixType: context.fixType,
    routeAction: context.routeAction
  };
  const payloadOverrides = new Map<string, AiSerializableValue>();
  const overrideAliases: Record<string, string> = {
    app: 'app',
    application: 'app',
    scope: 'scope',
    'bug context': 'bug_context',
    bug_context: 'bug_context',
    bug: 'bug_context',
    'expected outcome': 'expected_outcome',
    expected_outcome: 'expected_outcome',
    expected: 'expected_outcome',
    'serve configuration': 'serve_configuration',
    serve_configuration: 'serve_configuration',
    'serve config': 'serve_configuration',
    validation: 'validation',
    files: 'files',
    'source of truth': 'source_of_truth',
    source_of_truth: 'source_of_truth',
    'review feedback': 'review_feedback',
    review_feedback: 'review_feedback',
    notes: 'notes',
    'visual issue type': 'visual_expectations.issueType',
    'issue type': 'visual_expectations.issueType',
    issue_type: 'visual_expectations.issueType',
    'comparison properties': 'visual_expectations.comparisonProperties',
    comparison_properties: 'visual_expectations.comparisonProperties',
    'reference selector': 'visual_expectations.referenceSelector',
    reference_selector: 'visual_expectations.referenceSelector',
    'reference text': 'visual_expectations.referenceControlText',
    reference_control_text: 'visual_expectations.referenceControlText',
    'fix type': 'runtime_context.review_overrides.fix_type',
    fix_type: 'runtime_context.review_overrides.fix_type',
    'route action': 'runtime_context.review_overrides.route_action',
    route_action: 'runtime_context.review_overrides.route_action',
    'target selector': 'runtime_context.target_element.selector',
    target_selector: 'runtime_context.target_element.selector'
  };

  const normalizeOverridePath = (rawPath: string) => {
    const cleaned = rawPath
      .trim()
      .replace(/^payload\./i, '')
      .replace(/^json\./i, '')
      .replace(/\s+/g, ' ')
      .replace(/[?!.]+$/g, '');
    const aliasKey = cleaned.toLowerCase();
    if (overrideAliases[aliasKey]) {
      return overrideAliases[aliasKey];
    }

    return cleaned
      .replace(/\s*>\s*/g, '.')
      .replace(/\s+/g, '_')
      .replace(/__+/g, '_');
  };

  const parseOverrideValue = (path: string, rawValue: string): AiSerializableValue => {
    const trimmed = rawValue.trim();
    const unquoted = trimmed.match(/^(['"])([\s\S]*)\1$/)?.[2];
    const valueText = unquoted ?? trimmed;
    const pathLower = path.toLowerCase();

    if (/^(true|false)$/i.test(valueText)) {
      return /^true$/i.test(valueText);
    }
    if (/^-?\d+(?:\.\d+)?$/.test(valueText)) {
      return Number(valueText);
    }
    if (
      /(?:files|validation_commands|lookup_hint|comparisonproperties|selectorcandidates|referencecontroltextcandidates)$/i.test(
        pathLower
      ) ||
      (!/^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i.test(valueText) && valueText.includes(','))
    ) {
      return valueText
        .split(',')
        .map(part => part.trim())
        .filter(Boolean);
    }

    return valueText;
  };

  const setByPath = (target: any, path: string, value: AiSerializableValue) => {
    const parts = path.split('.').filter(Boolean);
    if (parts.length === 0) return;
    let cursor = target;
    for (const part of parts.slice(0, -1)) {
      if (!cursor[part] || typeof cursor[part] !== 'object' || Array.isArray(cursor[part])) {
        cursor[part] = {};
      }
      cursor = cursor[part];
    }
    cursor[parts[parts.length - 1]] = value;
  };

  const refreshRoutingSummary = () => {
    scoreMsg.innerHTML = `<strong style="color:${scoreColor};">Readiness: ${context.readinessScore}%</strong><br><span style="color:#a6adc8;">Fix: ${context.fixType} → ${context.routeAction}<br>Target: &lt;${context.topComponent}&gt;</span>`;
  };

  const applyOverrideToContext = (path: string, value: AiSerializableValue | undefined) => {
    if (value === undefined) return;
    const stringValue = Array.isArray(value) ? value.join(', ') : String(value);

    switch (path) {
      case 'app':
        context.app = stringValue;
        return;
      case 'scope':
        context.scope = stringValue;
        return;
      case 'bug_context':
        context.bugContext = stringValue;
        return;
      case 'expected_outcome':
        context.expectedOutcome = stringValue;
        return;
      case 'runtime_context.review_overrides.fix_type':
        context.fixType = stringValue;
        if (context.reasoningContext) context.reasoningContext.fixRouting.fixType = stringValue;
        refreshRoutingSummary();
        return;
      case 'runtime_context.review_overrides.route_action':
        context.routeAction = stringValue;
        if (context.reasoningContext) context.reasoningContext.fixRouting.routeAction = stringValue;
        refreshRoutingSummary();
        return;
      default:
        return;
    }
  };

  const resetContextOverride = (path: string) => {
    switch (path) {
      case 'app':
        context.app = initialContext.app;
        return;
      case 'scope':
        context.scope = initialContext.scope;
        return;
      case 'bug_context':
        context.bugContext = initialContext.bugContext;
        return;
      case 'expected_outcome':
        context.expectedOutcome = initialContext.expectedOutcome;
        return;
      case 'runtime_context.review_overrides.fix_type':
        context.fixType = initialContext.fixType;
        if (context.reasoningContext) context.reasoningContext.fixRouting.fixType = initialContext.fixType;
        refreshRoutingSummary();
        return;
      case 'runtime_context.review_overrides.route_action':
        context.routeAction = initialContext.routeAction;
        if (context.reasoningContext) context.reasoningContext.fixRouting.routeAction = initialContext.routeAction;
        refreshRoutingSummary();
        return;
      default:
        return;
    }
  };

  const syncPayloadOverridesGlobal = () => {
    const overrides: Record<string, AiSerializableValue> = {};
    for (const [path, value] of payloadOverrides.entries()) {
      overrides[path] = value;
    }
    window.usheru._chatPayloadOverrides = overrides;
  };

  const applyPayloadOverrides = (payload: any) => {
    if (payloadOverrides.size === 0) return;
    payload.runtime_context = payload.runtime_context || {};
    payload.runtime_context.chat_overrides = {};
    for (const [path, value] of payloadOverrides.entries()) {
      setByPath(payload, path, value);
      payload.runtime_context.chat_overrides[path] = value;
    }
  };

  const formatOverrideValue = (value: AiSerializableValue) => (Array.isArray(value) ? value.join(', ') : String(value));

  const handlePayloadOverrideCommand = (textValue: string) => {
    const helpMatch = textValue.match(/^(?:help\s+)?(?:commands?|options?|overrides?\s+help)$/i);
    if (helpMatch) {
      addMessage(
        [
          'Payload commands:',
          'set scope: movies catalogue',
          'set expected_outcome: the icon is aligned right',
          'set visual_expectations.issueType: position',
          'set comparison properties: order, position, left, right',
          'set route action: structural-style-override',
          'unset visual_expectations.referenceSelector',
          'show overrides',
          'clear overrides'
        ].join('\n'),
        false
      );
      return true;
    }

    const showMatch = textValue.match(/^(?:show|list)\s+(?:payload\s+)?overrides?$/i);
    if (showMatch) {
      if (payloadOverrides.size === 0) {
        addMessage('No payload overrides are set.', false);
        return true;
      }
      const lines = [...payloadOverrides.entries()].map(([path, value]) => `${path}: ${formatOverrideValue(value)}`);
      addMessage(`Current payload overrides:\n${lines.join('\n')}`, false);
      return true;
    }

    const clearMatch = textValue.match(/^(?:clear|reset)\s+(?:payload\s+)?overrides?$/i);
    if (clearMatch) {
      for (const path of payloadOverrides.keys()) {
        resetContextOverride(path);
      }
      payloadOverrides.clear();
      syncPayloadOverridesGlobal();
      addMessage('Cleared all payload overrides.', false);
      return true;
    }

    const unsetMatch = textValue.match(/^(?:unset|remove|clear)\s+(.+?)$/i);
    if (unsetMatch) {
      const path = normalizeOverridePath(unsetMatch[1]);
      payloadOverrides.delete(path);
      resetContextOverride(path);
      syncPayloadOverridesGlobal();
      addMessage(`Removed payload override for ${path}.`, false);
      return true;
    }

    const setMatch =
      textValue.match(/^(?:set|change|update|use)\s+(.+?)\s*(?::|=|\bto\b|\bis\b|\bshould\s+be\b)\s*(.+)$/i) ||
      textValue.match(/^([A-Za-z][A-Za-z0-9_.\s-]{1,80})\s+(?:is|should\s+be|to)\s+(.+)$/i) ||
      textValue.match(/^([A-Za-z][A-Za-z0-9_.\s-]{1,80})\s*:\s*(.+)$/);
    if (!setMatch) {
      return false;
    }

    const path = normalizeOverridePath(setMatch[1]);
    const value = parseOverrideValue(path, setMatch[2]);
    payloadOverrides.set(path, value);
    applyOverrideToContext(path, value);
    syncPayloadOverridesGlobal();
    addMessage(`✓ Payload override set: ${path} = ${formatOverrideValue(value)}`, false);
    return true;
  };

  const buildFinalPayload = () => {
    const usheru = window.usheru;
    if (typeof usheru?.buildAutomatedBugfixSkillInput === 'function') {
      const result = usheru.buildAutomatedBugfixSkillInput({
        bug_context: context.bugContext,
        expected_outcome: context.expectedOutcome,
        target_element: context.reasoningContext?.targetElementHint || undefined
      });
      let payload: any;
      if (typeof result === 'object') {
        payload = { ...result };
      } else {
        try {
          payload = JSON.parse(result);
        } catch {
          return result;
        }
      }
      // Apply user overrides from chat conversation
      if (context.scope) {
        payload.scope = context.scope;
      }
      applyPayloadOverrides(payload);
      if (usheru._lastAppliedFix) {
        payload.runtime_context = payload.runtime_context || {};
        payload.runtime_context.verified_fix = { ...usheru._lastAppliedFix };
      }
      if (payload?.runtime_context?.recorded_dom_interactions) {
        const allInteractions = payload.runtime_context.recorded_dom_interactions;
        payload.runtime_context.recorded_dom_interactions = allInteractions.filter(
          i => i.surface !== '#usheru-bugfix-chat' && !String(i.selector || '').startsWith('#usheru-bugfix-chat')
        );
        const filter = window.usheru._chatInteractionFilter;
        if (filter && filter !== 'keep_all') {
          const allInteractions = payload.runtime_context.recorded_dom_interactions;
          if (filter === 'keep_options') {
            payload.runtime_context.recorded_dom_interactions = allInteractions.filter(i => {
              if (i.kind !== 'mouseenter') return true;
              return i.role === 'option';
            });
          } else if (filter.startsWith('keep_indices:')) {
            const keepSet = new Set(filter.slice('keep_indices:'.length).split(',').map(Number));
            payload.runtime_context.recorded_dom_interactions = allInteractions.filter(i => {
              if (i.kind !== 'mouseenter') return true;
              return keepSet.has(i.index);
            });
          } else if (filter.startsWith('remove_indices:')) {
            const removeSet = new Set(filter.slice('remove_indices:'.length).split(',').map(Number));
            payload.runtime_context.recorded_dom_interactions = allInteractions.filter(i => {
              if (i.kind !== 'mouseenter') return true;
              return !removeSet.has(i.index);
            });
          }
        }
      }
      const json = JSON.stringify(payload);
      usheru._lastReviewPayloadJson = json;
      return JSON.stringify(payload, null, 2);
    }

    const fallback = {
      app: context.app,
      bug_context: context.bugContext,
      expected_outcome: context.expectedOutcome,
      scope: context.scope,
      target_element: context.targetSelector,
      components: context.relevantComponents,
      fix_type: context.fixType,
      route_action: context.routeAction,
      reproduction_steps: collectedAnswers.reproduction_steps || '',
      visual_details: collectedAnswers.visual_details || '',
      environment: collectedAnswers.environment || '',
      readiness_score: context.readinessScore
    };
    applyPayloadOverrides(fallback);
    const json = JSON.stringify(fallback);
    usheru._lastReviewPayloadJson = json;
    return JSON.stringify(fallback, null, 2);
  };

  const collectedAnswers: Record<string, string> = {};

  resetBtn.addEventListener('click', () => {
    Object.keys(collectedAnswers).forEach(k => delete collectedAnswers[k]);
    conversationHistory.length = 0;
    conversationTurns = 0;
    while (messages.children.length > 3) {
      messages.removeChild(messages.lastChild);
    }
    addMessage('Conversation reset. Starting fresh!', false);
    void askFollowUp();
  });

  const isRoutingQuestion = (text: string): boolean => {
    const lower = text.toLowerCase();
    return (
      lower.includes('component') ||
      lower.includes('fix type') ||
      lower.includes('route action') ||
      lower.includes('css ') ||
      lower.includes('typescript') ||
      lower.includes('html ') ||
      lower.includes('template') ||
      lower.includes('should i') ||
      lower.includes('which') ||
      lower.includes('where should') ||
      lower.includes('what component') ||
      lower.includes('better') ||
      lower.includes('suggest') ||
      lower.includes('type of fix') ||
      lower.includes('other type') ||
      lower.includes('other fix') ||
      lower.includes('other options') ||
      lower.includes('alternative') ||
      lower.includes('different fix') ||
      lower.includes('routing') ||
      lower.includes('options to fix') ||
      (lower.includes('other') && lower.includes('fix'))
    );
  };

  const sendMessage = async () => {
    const text = input.value.trim();
    if (!text) return;
    input.value = '';
    addMessage(text, true);

    const lowerText = text.toLowerCase();

    if (
      lowerText === 'proceed' ||
      lowerText === 'done' ||
      lowerText === 'json' ||
      lowerText.includes('proceed') ||
      lowerText.includes('generate json')
    ) {
      const jsonPayload = buildFinalPayload();
      const narrative = (() => {
        try {
          const p = JSON.parse(typeof jsonPayload === 'string' ? jsonPayload : JSON.stringify(jsonPayload));
          const lines: string[] = [];
          const interactions = p?.runtime_context?.recorded_dom_interactions || [];
          const visualEx = p?.visual_expectations || {};
          const targetEl = p?.runtime_context?.target_element;
          for (const entry of p?.runtime_context?.recorded_steps || []) {
            lines.push(`${entry.index || ''}. Navigate to ${entry.title || entry.url}`);
          }
          for (const i of interactions) {
            const idx = i.index || '';
            const val = i.value ? ` "${i.value}"` : '';
            if (i.kind === 'click') lines.push(`${idx}. Click ${i.title || i.selector}`);
            else if (i.kind === 'input') lines.push(`${idx}. Fill ${i.selector || i.title} with${val}`);
            else if (i.kind === 'mouseenter') lines.push(`${idx}. Hover ${i.selector || i.title}`);
            else if (i.kind === 'change' && i.value) lines.push(`${idx}. Change to${val}`);
            else lines.push(`${idx}. ${i.title || i.kind}`);
          }
          lines.push('', 'Then check:');
          if (targetEl) {
            lines.push(`  Target: ${targetEl.selector || targetEl.tagName || ''}`);
            const props = visualEx.comparisonProperties || ['color', 'background-color', 'border-color'];
            lines.push(`  Computed ${props.join(', ')}`);
          }
          if (visualEx.issueType) lines.push(`  Issue type: ${visualEx.issueType}`);
          const vf = p?.runtime_context?.verified_fix;
          if (vf) {
            lines.push(`  Verified fix (applied in-browser): ${vf.property}: ${vf.value} on ${vf.selector}`);
            lines.push(`  ↳ ${vf.selector} { ${vf.property}: ${vf.value} !important; }`);
          }
          lines.push(`  Route: ${p?.runtime_context?.current_path || ''}`);
          return lines.join('\n');
        } catch {
          return '';
        }
      })();

      const displayPayload = typeof jsonPayload === 'string' ? jsonPayload : JSON.stringify(jsonPayload, null, 2);
      const narrativeBlock = narrative ? `\`\`\`\n${narrative}\n\`\`\`\n\n` : '';
      addMessage(`${narrativeBlock}Here's your JSON payload for CI:\n\n\`\`\`json\n${displayPayload}\n\`\`\``, false);
      addMessage(
        'Copy to clipboard — paste this into the browser console:\n`copy(window.usheru._lastReviewPayloadJson)`',
        false
      );

      void createWebLlmEngine().then(async engine => {
        if (engine) {
          try {
            const rc = context.reasoningContext;
            let reviewSystemPrompt = `You are a QA assistant reviewing a JSON bug report payload for an Angular component library.
Do NOT output CSS code, React tags, language tags, or priority fields.
Only suggest missing structural fields relevant to CSS/visual bug reports (e.g. additional selectors, exact color values, pixel gaps).
If you suggest changing the fix type, route action, or target component, explain WHY based on the scores provided.
Keep responses under 4 sentences.`;

            if (rc) {
              const entriesInfo = rc.relevantEntries
                .slice(0, 3)
                .map(e => `- ${e.selector} (kind: ${e.kind}, score: ${e.score}%)`)
                .join('\n');
              reviewSystemPrompt += `\n\nReasoning engine context:\n- Fix type: ${rc.fixRouting.fixType}\n- Route action: ${rc.fixRouting.routeAction}\n- Top component matches:\n${entriesInfo}`;
              if (rc.cssVars && rc.cssVars.length > 0) {
                reviewSystemPrompt += `\n- Existing CSS variables: ${rc.cssVars.slice(0, 5).join(', ')}`;
                const primeNgVars = rc.cssVars.filter(v =>
                  /p-(?:multiselect|select|dropdown|iconfield|iconwrapper)/.test(v)
                );
                if (primeNgVars.length > 0) {
                  reviewSystemPrompt += `\n- PrimeNG structural hint: CSS vars like "${primeNgVars.slice(0, 3).join('", "')}" indicate a newer PrimeNG version. The overlay filter now uses .p-iconfield-left > .p-inputicon layout instead of the old .p-multiselect-filter-container > .p-multiselect-filter-icon.`;
                }
              }
              if (context.cssDiagnosticsText) {
                reviewSystemPrompt += `\n\nLive CSS Diagnostics:\n${context.cssDiagnosticsText}`;
              }
            }

            const response = await engine.chat.completions.create({
              messages: [
                { role: 'system', content: reviewSystemPrompt },
                {
                  role: 'user',
                  content: `Review this JSON payload:\n\n${jsonPayload}\n\nIf it looks good, say "Looks ready for CI."`
                }
              ],
              max_tokens: 512,
              stream: false
            });
            const review = response.choices[0]?.message?.content;
            if (review) {
              addMessage(`**LLM Review:**\n${review}`, false);
            }
          } catch (err) {}
        }
      });

      return;
    }

    if (lowerText === 'improve') {
      void askFollowUp('The user wants to improve the report. What specific detail would you suggest adding?');
      return;
    }

    if (lowerText === 'fix' || lowerText === 'propose fix' || lowerText === 'apply fix') {
      // Try CSS variable suggestions from diagnostics first
      if (cssFixSuggestions.length > 0) {
        renderFixSuggestionCards(cssFixSuggestions);
        return;
      }
      if (context.cssDiagnosticsText?.includes('Fix suggestions:')) {
        const fixLines = context.cssDiagnosticsText.split('\n').filter(l => l.includes('Set '));
        const parsedFixes: AiCssFixSuggestion[] = fixLines
          .map(line => {
            const colonIdx = line.indexOf(':');
            const onIdx = line.indexOf(' on <');
            const closeIdx = line.lastIndexOf('>');
            if (colonIdx === -1 || onIdx === -1) return null;
            return {
              variable: line.substring(line.indexOf('Set ') + 4, colonIdx).trim(),
              currentValue: line.substring(colonIdx + 2, onIdx).trim(),
              suggestedValue: line.substring(colonIdx + 2, onIdx).trim(),
              scope: line.substring(onIdx + 5, closeIdx).trim(),
              reason: 'CSS variable fix from diagnostics'
            };
          })
          .filter((f): f is AiCssFixSuggestion => f !== null);
        if (parsedFixes.length > 0) {
          renderFixSuggestionCards(parsedFixes);
          return;
        }
      }

      // Fall back to structural DOM analysis
      const fixes = generateStructuralCssFix();
      if (fixes.length > 0) {
        renderFixSuggestionCards(fixes);
        return;
      }

      // Show custom fix form so the user can apply any CSS fix directly in the chat
      renderCustomFixForm();
      return;
    }

    if (handlePayloadOverrideCommand(text)) {
      return;
    }

    // CSS rule injection: type a rule directly like  selector { property: value }
    const cssRuleMatch = text.match(/^(.+?)\s*\{\s*([a-z-]+)\s*:\s*(.+?)\s*\}$/i);
    if (cssRuleMatch) {
      const selector = cssRuleMatch[1].trim();
      const property = cssRuleMatch[2].trim();
      const value = cssRuleMatch[3].trim();
      window.usheru.injectFix({ selector, property, value });
      addMessage(
        `✅ Applied: \`${selector} { ${property}: ${value} !important; }\`<br>Verify visually, then type <strong style="color:#f9e2af;">"proceed"</strong> to include it in the payload.`,
        false
      );
      return;
    }

    // --- User overrides for scope / fix type / route action ---
    const scopeOverride = text.match(
      /^(?:change\s+)?scope\s*(?::|is\s+|should\s+be\s+|change\s+to\s+|set\s+to\s+|=)\s*(.+)$/i
    );
    if (scopeOverride) {
      const value = scopeOverride[1].trim();
      context.scope = value;
      addMessage(`✓ Scope updated to "${value}"`, false);
      return;
    }
    const fixTypeOverride = text.match(
      /^(?:change\s+)?fix\s+type\s*(?::|is\s+|should\s+be\s+|change\s+to\s+|set\s+to\s+|=)\s*(.+)$/i
    );
    if (fixTypeOverride) {
      const value = fixTypeOverride[1].trim();
      context.fixType = value;
      if (context.reasoningContext) context.reasoningContext.fixRouting.fixType = value;
      scoreMsg.innerHTML = `<strong style="color:${scoreColor};">Readiness: ${context.readinessScore}%</strong><br><span style="color:#a6adc8;">Fix: ${context.fixType} → ${context.routeAction}<br>Target: &lt;${context.topComponent}&gt;</span>`;
      addMessage(`✓ Fix type updated to "${value}"`, false);
      return;
    }
    const routeActionOverride = text.match(
      /^(?:change\s+)?route\s+action\s*(?::|is\s+|should\s+be\s+|change\s+to\s+|set\s+to\s+|=)\s*(.+)$/i
    );
    if (routeActionOverride) {
      const value = routeActionOverride[1].trim();
      context.routeAction = value;
      if (context.reasoningContext) context.reasoningContext.fixRouting.routeAction = value;
      scoreMsg.innerHTML = `<strong style="color:${scoreColor};">Readiness: ${context.readinessScore}%</strong><br><span style="color:#a6adc8;">Fix: ${context.fixType} → ${context.routeAction}<br>Target: &lt;${context.topComponent}&gt;</span>`;
      addMessage(`✓ Route action updated to "${value}"`, false);
      return;
    }

    if (
      isRoutingQuestion(lowerText) &&
      context.reasoningContext &&
      context.reasoningContext.relevantEntries.length > 0
    ) {
      const loadingMsg = document.createElement('div');
      loadingMsg.style.cssText =
        'padding:8px 10px;border-radius:8px;font-size:12px;color:#a6adc8;align-self:flex-start;';
      loadingMsg.textContent = 'Checking reasoning context...';
      messages.appendChild(loadingMsg);
      messages.scrollTop = messages.scrollHeight;

      const engine = await createWebLlmEngine();
      if (!engine) {
        loadingMsg.remove();
        addMessage('AI model not available. Please check WebGPU support.', false);
        return;
      }

      loadingMsg.textContent = 'Thinking...';

      const rc = context.reasoningContext!;
      const entriesContext = rc.relevantEntries
        .slice(0, 3)
        .map((e, i) => `${i + 1}. ${e.selector} (kind: ${e.kind}, score: ${e.score}%, path: ${e.sourcePath})`)
        .join('\n');

      const prompt = `You are a fix-routing assistant for an Angular component library. The user is asking about which component or fix type to target.

CURRENT FIX ROUTING DECISION:
- Fix type: ${context.fixType}
- Route action: ${context.routeAction}
- CSS variables detected: ${(rc.cssVars || []).length > 0 ? rc.cssVars!.join(', ') : 'none'}
- Layout constraints: ${(rc.fixRouting.layoutConstraintHints || []).length > 0 ? rc.fixRouting.layoutConstraintHints!.join(', ') : 'none'}

TOP COMPONENT MATCHES (sorted by relevance score — HIGHER IS BETTER):
${entriesContext}

BUG: "${context.bugContext}"
EXPECTED: "${context.expectedOutcome}"
SCOPE: ${context.scope}

RULES:
- ALWAYS recommend the HIGHEST-scoring component as the primary target.
- Lower scores mean weaker relevance — only suggest them if the highest-scored match is below 50%.
- Explain WHY based on the score difference and the bug description.
- If the current fix type/route action seems wrong, suggest the better alternative with reasoning.
- Keep it to 2-3 sentences.
- When CSS variables include p-multiselect-overlay-*, p-select-overlay-*, p-iconfield, or p-iconwrapper, note that PrimeNG 19 changed the overlay filter DOM from .p-multiselect-filter-container > .p-multiselect-filter-icon to .p-iconfield-left > .p-inputicon. Old selectors targeting the first structure will not match.

User question: "${text}"`;

      try {
        const response = await engine.chat.completions.create({
          messages: [{ role: 'user', content: prompt }],
          max_tokens: 512,
          stream: false
        });

        const content = response.choices[0]?.message?.content;
        loadingMsg.remove();
        if (content) {
          addMessage(content, false);
        }
      } catch (err) {
        loadingMsg.remove();
        addMessage('Could not process routing question. Try again.', false);
      }
      return;
    }

    // Store answer by keyword matching for payload inclusion
    // Detect interaction filter answers
    const keepAllMatch = lowerText.match(/^keep\s+all$/);
    const keepOptionsMatch = lowerText.match(/^keep\s+options?$/);
    const keepPositionsMatch = lowerText.match(/^keep\s*[\[#]?\s*(\d[\d,\s]*)\s*[\]\s]*$/);
    const removePositionsMatch = lowerText.match(/^remove\s*[\[#]?\s*(\d[\d,\s]*)\s*[\]\s]*$/);
    if (keepAllMatch) {
      window.usheru._chatInteractionFilter = 'keep_all';
      addMessage('Will keep all recorded hover interactions.', false);
      return;
    } else if (keepOptionsMatch) {
      window.usheru._chatInteractionFilter = 'keep_options';
      addMessage('Will only keep hover interactions on role="option" elements.', false);
      return;
    } else if (keepPositionsMatch) {
      const positions = keepPositionsMatch[1]
        .split(/[\s,]+/)
        .map(Number)
        .filter(n => !isNaN(n) && n > 0);
      if (positions.length > 0) {
        const raw = window.usheru._chatInteractionFilter_raw;
        const indices = positions.map(p => raw[p - 1]).filter(i => i !== undefined);
        if (indices.length > 0) {
          window.usheru._chatInteractionFilter = 'keep_indices:' + indices.join(',');
          addMessage(`Will keep hover positions ${positions.join(', ')} (recording #${indices.join(', #')}).`, false);
          return;
        }
      }
    } else if (removePositionsMatch) {
      const positions = removePositionsMatch[1]
        .split(/[\s,]+/)
        .map(Number)
        .filter(n => !isNaN(n) && n > 0);
      if (positions.length > 0) {
        const raw = window.usheru._chatInteractionFilter_raw;
        const removeIndices = new Set(positions.map(p => raw[p - 1]).filter(i => i !== undefined));
        const keepIndices = raw.filter(i => !removeIndices.has(i));
        if (keepIndices.length > 0) {
          window.usheru._chatInteractionFilter = 'keep_indices:' + keepIndices.join(',');
          addMessage(`Will remove hover positions ${positions.join(', ')}.`, false);
        } else {
          window.usheru._chatInteractionFilter = 'keep_all';
          addMessage('Removing all would leave nothing — keeping all.', false);
        }
        return;
      }
    }

    if (
      lowerText.includes('color') ||
      lowerText.includes('#') ||
      lowerText.includes('rgb') ||
      lowerText.includes('spacing') ||
      lowerText.includes('pixel') ||
      lowerText.includes('gap') ||
      lowerText.includes('px') ||
      lowerText.includes('font')
    ) {
      collectedAnswers.visual_details = text;
    }
    if (
      lowerText.includes('reproduce') ||
      lowerText.includes('step') ||
      lowerText.includes('load') ||
      lowerText.includes('navigate') ||
      lowerText.includes('click') ||
      lowerText.includes('hover') ||
      lowerText.includes('scroll')
    ) {
      collectedAnswers.reproduction_steps = text;
    }
    if (
      lowerText.includes('chrome') ||
      lowerText.includes('firefox') ||
      lowerText.includes('safari') ||
      lowerText.includes('mobile') ||
      lowerText.includes('desktop') ||
      lowerText.includes('ios') ||
      lowerText.includes('android')
    ) {
      collectedAnswers.environment = text;
    }

    // Route to LLM for next follow-up question
    conversationHistory.push({ role: 'user', content: text });
    conversationTurns++;
    void askFollowUp(text);
  };

  sendBtn.addEventListener('click', sendMessage);
  input.addEventListener('keypress', e => {
    if (e.key === 'Enter') sendMessage();
  });

  return {
    open: () => {
      root.style.display = 'flex';
    },
    close: () => {
      root.style.display = 'none';
    },
    destroy: destroyWebLlmChatWidget
  };
}

function notifyWebLlmAvailable() {
  const usheru = getEagerUsheruGlobal();
  if (!usheru || usheru._webLlmChat) return;

  console.log('%c🤖 AI Bugfix Assistant available!', 'color:#89b4fa;font-weight:700;font-size:13px;');
  console.log(
    '  Open an interactive chat window: %cwindow.usheru._webLlmChat.open()',
    'color:#1e1e2e;background:#89b4fa;padding:2px 6px;'
  );
  console.log('  Ask questions about your bug report, get suggestions for improvements.');
}

function getEagerUsheruGlobal() {
  if (typeof window === 'undefined') {
    return null;
  }

  return (window.usheru = window.usheru ?? {});
}

function persistRecordStepsFlag() {
  if (typeof window === 'undefined' || typeof sessionStorage === 'undefined') return;
  try {
    const paramValue = new URLSearchParams(window.location.search).get('recordSteps');
    if (paramValue !== null) {
      sessionStorage.setItem('usheru-recordSteps', paramValue);
    }
  } catch {
    /* noop */
  }
}

function parseRecordStepsConfigLabel(): string {
  if (typeof window === 'undefined') return 'none';
  const raw =
    new URLSearchParams(window.location.search).get('recordSteps') ?? sessionStorage.getItem('usheru-recordSteps');
  if (!raw) return 'none';
  const trimmed = raw.trim().toLowerCase();
  if (trimmed === '' || trimmed === '1' || trimmed === 'true' || trimmed === 'all') return 'all';
  return trimmed
    .split(',')
    .map(s => s.trim())
    .filter(Boolean)
    .join(', ');
}

function registerEagerUsheruPlaceholder() {
  persistRecordStepsFlag();
  const usheru = getEagerUsheruGlobal();
  if (!usheru || usheru.buildAutomatedBugfixSkillInput) {
    return;
  }

  usheru.buildAutomatedBugfixSkillInput = () => ({
    message: 'AI bugfix helper is loading. Retry after Angular bootstrap completes.',
    recordStepsEnabled: parseRecordStepsConfigLabel() !== 'none',
    recordStepsConfig: parseRecordStepsConfigLabel(),
    recordedSteps: Array.isArray(usheru.recordedSteps) ? usheru.recordedSteps.length : 0,
    recordedDomInteractions: Array.isArray(usheru.recordedDomInteractions) ? usheru.recordedDomInteractions.length : 0,
    recordStepsUrl: window.location.href
  });

  usheru.reviewAutomatedBugfixPlan = () => ({
    id: 'loading',
    status: 'pending',
    copyPayload: () => {
      console.log('AI bugfix review helper is loading. Retry after Angular bootstrap completes.');
    }
  });
}

registerEagerUsheruPlaceholder();

export interface OpenBugReportAction {
  type: 'open-bug-report';
  payload: {
    ctx: { bugContext: string; expectedOutcome: string; app: string; route: string; scope: string };
    targetElementHint: AiTargetElementHint | null;
    proposedFixes: unknown[];
    relevantEntries: unknown[];
    fixRouting: {
      fixType: string;
      routeAction: string;
      visualIssueType: string;
      existingStyleVariableHints: string[];
      layoutConstraintHints: string[];
    };
  };
}

export type BridgeAction = OpenBugReportAction;

@Injectable({
  providedIn: 'root'
})
export class AiInformationService {
  readonly actions$ = new Subject<BridgeAction>();

  private readonly platformId = inject(PLATFORM_ID);
  private readonly router = inject(Router);
  private readonly env = inject(ENVIRONMENT);
  private readonly channelService = inject(ChannelService);
  private readonly appGitVersion = inject(APP_GIT_VERSION, { optional: true });
  private readonly ngZone = inject(NgZone);
  private readonly injector = inject(Injector);

  private initialized = false;
  private aiBugfixLessonsFetchStarted = false;
  private aiBugfixLessonsCache: AiBugfixLessonRecord[] | null = null;
  /** @internal emits when the lesson cache finishes loading (or fails). The bug-report form subscribes to re-run the lesson lookup once the cache is ready. */
  readonly aiBugfixLessonsReady$ = new Subject<void>();

  /** Overridable lesson scoring weights. Set via `window.usheru.setLessonWeights()`. */
  private lessonWeights: Partial<LessonScoringWeights> = {};

  constructor() {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.registerWindowApi();
    this.initDebugMode();
  }

  /**
   * Exposes browser console helpers on every browser page.
   * Enables interaction recording when `?recordSteps` is present.
   *
   * Examples:
   * `/watch-at-home?recordSteps=true`          — record all event types
   * `/watch-at-home?recordSteps=click`         — record only clicks
   * `/watch-at-home?recordSteps=click,scroll`  — record clicks and scrolls
   * `/watch-at-home?recordSteps=click,navigation,hover,scroll,change,submit,input` — specific types
   *
   * After init with recording enabled, the browser console can inspect:
   * `window.usheru.recordedSteps`
   * `window.usheru.recordedDomInteractions`
   * `window.usheru.listSelectors()`
   *
   * And build a paste-ready automated bugfix payload directly from the current page:
   * `copy(window.usheru.buildAutomatedBugfixSkillInput({ bug_context: 'what\'s broken', expected_outcome: 'expected result' }))`
   */
  init() {
    if (!isPlatformBrowser(this.platformId) || this.initialized) {
      return;
    }

    this.initialized = true;
    this.registerWindowApi();

    const config = this.getRecordStepsConfig();
    if (config.size === 0) {
      return;
    }

    this.getRecordedDomInteractions();
    this.registerDomInteractionListeners(config);

    if (config.has('*') || config.has('navigation')) {
      this.recordStep('entry', window.location.href);

      this.router.events
        .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
        .subscribe(event => {
          this.recordStep('navigation', event.urlAfterRedirects || event.url);
        });
    }
  }

  /**
   * Records real browser interactions so reproduction can prefer actual
   * clicks, changes, and submits over inferred analytics events.
   */
  recordDomInteraction(kind: AiRecordedDomInteraction['kind'], event: Event) {
    if (!event.isTrusted) {
      return;
    }

    this.recordDomInteractionWithTrackedSettle(kind, event.target);
  }

  private recordDomInteractionWithTrackedSettle(
    kind: AiRecordedDomInteraction['kind'],
    target: EventTarget | null,
    deferRecording = false
  ) {
    const element = target instanceof Element ? target : target instanceof Node ? target.parentElement : null;
    if (
      element?.closest('#usheru-bugfix-chat') ||
      element?.closest('ush-ai-bug-report') ||
      element?.closest('#ush-debug-btn')
    ) {
      return;
    }

    const settleMetricsPromise = this.measureDomSettleMetrics();
    const finalizeRecording = () => {
      void settleMetricsPromise.then(settleMetrics => this.recordDomInteractionFromTarget(kind, target, settleMetrics));
    };

    if (deferRecording) {
      window.setTimeout(finalizeRecording, 0);
      return;
    }

    finalizeRecording();
  }

  private async recordDomInteractionFromTarget(
    kind: AiRecordedDomInteraction['kind'],
    target: EventTarget | null,
    options?: {
      settleMs?: number;
      mutationCount?: number;
    }
  ) {
    if (!this.initialized) {
      return;
    }

    const settleMetrics = await this.measureDomSettleMetrics(options);
    const element = this.getInteractionElement(target, kind);
    if (!element || (kind === 'click' && this.shouldSkipClickInteraction(element))) {
      return;
    }

    const interaction = this.buildDomInteraction(kind, element, settleMetrics.settleMs, settleMetrics.mutationCount);
    const recordedDomInteractions = this.getRecordedDomInteractions();
    const previousInteraction = recordedDomInteractions[recordedDomInteractions.length - 1];

    if (previousInteraction && this.shouldSkipDuplicateDomInteraction(previousInteraction, interaction)) {
      return;
    }

    // Prefer click over mouseenter on same element — the user clicked, not hovered
    if (
      kind === 'click' &&
      previousInteraction?.kind === 'mouseenter' &&
      previousInteraction.selector === interaction.selector
    ) {
      recordedDomInteractions[recordedDomInteractions.length - 1] = {
        ...interaction,
        index: previousInteraction.index
      };
      return;
    }

    // Replace previous input on same element instead of stacking keystrokes
    if (
      kind === 'input' &&
      previousInteraction?.kind === 'input' &&
      previousInteraction.selector === interaction.selector
    ) {
      recordedDomInteractions[recordedDomInteractions.length - 1] = {
        ...interaction,
        index: previousInteraction.index
      };
      return;
    }

    recordedDomInteractions.push({
      ...interaction,
      index: recordedDomInteractions.length + 1
    });
  }

  /**
   * Builds the compact JSON string expected by the automated bugfix workflow.
   * REQUIRES BOTH: bug_context and expected_outcome. Passing only one will throw an error.
   * This is intentionally ready to paste into `reproduction_steps` without
   * wrapping it in another `JSON.stringify(...)` call.
   *
   * REQUIRED USAGE (both fields must be provided):
   * `copy(window.usheru.buildAutomatedBugfixSkillInput({ bug_context: 'The CTA wraps on mobile.', expected_outcome: 'The CTA should stay on one line.' }))`
   *
   * WITH ELEMENT INSPECTION:
   * `copy(window.usheru.buildAutomatedBugfixSkillInput({ bug_context: 'what\\'s broken', expected_outcome: 'expected result' }, $0))`
   *
   * FOR HELP (shows instructions):
   * `window.usheru.buildAutomatedBugfixSkillInput()` or `window.usheru.buildAutomatedBugfixSkillInput('help')`
   */
  buildAutomatedBugfixSkillInput(
    request?: AiAutomatedBugfixSkillConsoleRequest,
    targetElement?: Element | null
  ): string | AiAutomatedBugfixSkillHelpResult {
    if (this.shouldReturnAutomatedBugfixHelp(request)) {
      return this.printAutomatedBugfixSkillHelp();
    }

    this.validateAutomatedBugfixRequest(request);
    const payload = this.buildAutomatedBugfixSkillPayload(request, targetElement);
    this.printAutomatedBugfixSkillPayloadPreflight(payload);

    return JSON.stringify(payload);
  }

  private validateAutomatedBugfixRequest(request: AiAutomatedBugfixSkillConsoleRequest): void {
    const normalizedRequest = typeof request === 'string' ? { bug_context: request } : (request ?? {});
    const bugContext = String(normalizedRequest.bug_context || '').trim();
    const expectedOutcome = String(normalizedRequest.expected_outcome || '').trim();

    if (!bugContext) {
      throw new Error(
        'buildAutomatedBugfixSkillInput requires bug_context. ' +
          'Usage: copy(window.usheru.buildAutomatedBugfixSkillInput({bug_context: "what\\s broken", expected_outcome: "how to verify"}))'
      );
    }

    if (!expectedOutcome) {
      throw new Error(
        'buildAutomatedBugfixSkillInput requires expected_outcome. ' +
          'Usage: copy(window.usheru.buildAutomatedBugfixSkillInput({bug_context: "what\\s broken", expected_outcome: "how to verify"}))'
      );
    }
  }

  private shouldReturnAutomatedBugfixHelp(request: AiAutomatedBugfixSkillConsoleRequest) {
    if (request === undefined || request === null) {
      return true;
    }

    return typeof request === 'string' && /^(help|\?|example|examples)$/i.test(request.trim());
  }

  private getRecordStepsConfigLabel(): string {
    const config = this.getRecordStepsConfig();
    if (config.size === 0) return 'none';
    if (config.has('*')) return 'all';
    return Array.from(config).join(', ');
  }

  private printAutomatedBugfixSkillHelp(): AiAutomatedBugfixSkillHelpResult {
    const location = this.parseUrl(window.location.href);
    const appResolution = this.resolveAppIdentifier();
    const config = this.getRecordStepsConfig();
    const recordStepsEnabled = config.size > 0;
    const configLabel = this.getRecordStepsConfigLabel();
    const recordingUrl = this.buildRecordStepsUrl();

    const divider = '━'.repeat(70);
    console.log(`\n${divider}`);
    console.log('%c🔧 AI BUGFIX HELPER', 'color:#0f766e;font-weight:700;font-size:14px');
    console.log(`${divider}\n`);

    console.log('%cQUICK START:', 'color:#2563eb;font-weight:700;margin-top:8px');
    console.log(
      "1. Provide both required fields: %ccopy(window.usheru.buildAutomatedBugfixSkillInput({bug_context: 'what\\s wrong', expected_outcome: 'how to verify fix'}))",
      'color:#111827;background:#f3f4f6;padding:2px 4px'
    );
    console.log(
      "2. With element inspection: %ccopy(window.usheru.buildAutomatedBugfixSkillInput({bug_context: '...', expected_outcome: '...'}, $0))",
      'color:#111827;background:#f3f4f6;padding:2px 4px'
    );
    console.log(
      "3. Compare current branch against production: %ccopy(window.usheru.buildAutomatedBugfixSkillInput({bug_context: '...', expected_outcome: '...', source_of_truth: 'production'}))",
      'color:#111827;background:#f3f4f6;padding:2px 4px'
    );
    console.log('4. Paste JSON into GitHub Actions reproduction_steps');

    console.log('%c\nFIELD GUIDE (required + useful optional):', 'color:#2563eb;font-weight:700;margin-top:8px');
    console.log(
      "%cbug_context%c: What's broken right now (specific, not vague)",
      'color:#111827;font-weight:700',
      'color:inherit'
    );
    console.log(
      "%cexpected_outcome%c: How to know it's fixed (describe the visual change you expect)",
      'color:#111827;font-weight:700',
      'color:inherit'
    );
    console.log(
      '%c$0%c: Selected element in DevTools (optional, use when multiple similar controls exist)',
      'color:#111827;font-weight:700',
      'color:inherit'
    );
    console.log(
      "%csource_of_truth%c: Set to 'production' when the current branch is wrong but production is the expected baseline",
      'color:#111827;font-weight:700',
      'color:inherit'
    );

    if (!recordStepsEnabled) {
      console.log('%c\nRECORD INTERACTIONS:', 'color:#b45309;font-weight:700;margin-top:8px');
      console.log('Reload with: %c' + recordingUrl, 'color:#111827;background:#f3f4f6;padding:2px 4px');
      console.log('Then interact with the bug before calling the helper.');
      console.log(
        '  Use ?recordSteps=true for all types, or pick specific ones: ?recordSteps=click,navigation,hover,scroll'
      );
    }

    console.log('%c\nCONTEXT:', 'color:#2563eb;font-weight:700;margin-top:8px');
    console.table({
      app: appResolution.value,
      route: location.path,
      recordingActive: recordStepsEnabled ? 'yes' : 'no',
      recordingTypes: configLabel
    });

    console.log(`${divider}\n`);

    return {
      message:
        'Helper ready. Call with {bug_context: "what\'s wrong", expected_outcome: "how to verify"} to build payload.',
      recordStepsEnabled,
      recordStepsConfig: configLabel,
      recordedSteps: this.getRecordedSteps().length,
      recordedDomInteractions: this.getRecordedDomInteractions().length,
      recordStepsUrl: recordingUrl
    };
  }

  private async prefetchAiBugfixLessons() {
    if (this.aiBugfixLessonsFetchStarted) {
      return;
    }
    this.aiBugfixLessonsFetchStarted = true;

    try {
      const app = this.resolveAppIdentifier().value;
      const lessonUrls = [
        `/assets/ai-tooling/ai-bugfix-lessons/${app}.jsonl`,
        '/assets/ai-tooling/ai-bugfix-lessons.jsonl',
        `/docs/ai-bugfix-lessons/${app}.jsonl`,
        '/docs/ai-bugfix-lessons.jsonl'
      ];
      let response: Response | null = null;
      for (const url of lessonUrls) {
        try {
          const candidate = await fetch(url);
          if (candidate.ok) {
            response = candidate;
            break;
          }
        } catch {
          /* try next URL */
        }
      }
      if (!response) {
        this.aiBugfixLessonsCache = [];
        this.aiBugfixLessonsReady$.next();
        return;
      }

      const text = await response.text();
      this.aiBugfixLessonsCache = text
        .split(/\r?\n/)
        .map(line => line.trim())
        .filter(Boolean)
        .map(line => {
          try {
            return JSON.parse(line) as AiBugfixLessonRecord;
          } catch {
            return null;
          }
        })
        .filter((record): record is AiBugfixLessonRecord => !!record)
        .slice(-200);
      this.aiBugfixLessonsReady$.next();
    } catch {
      this.aiBugfixLessonsCache = [];
      this.aiBugfixLessonsReady$.next();
    }
  }

  /**
   * Builds lesson records from one or more validated fixes.
   * Outputs lesson entries to the console.
   * Supports multi-fix: pass an array of fixes to record in one call.
   */
  recordBugfixLesson(opts: {
    bug_context: string;
    expected_outcome: string;
    fixes: Array<{
      label: string;
      selector: string;
      rawCss: string;
      source?: string;
      fixType?: string;
      routeAction?: string;
    }>;
    app?: string;
    scope?: string;
    path?: string;
    targetElementHint?: AiTargetElementHint | null;
  }): AiBugfixLessonRecord[] {
    const app = opts.app || this.resolveAppIdentifier().value;
    const location = this.parseUrl(window.location.href);
    const path = opts.path || location.path;
    const scope = opts.scope || this.inferAutomatedBugfixScope(location.path);
    const targetElementHint = opts.targetElementHint || null;

    const records: AiBugfixLessonRecord[] = [];

    for (const fix of opts.fixes) {
      const control = this.inferIssueControlFromHint(targetElementHint, fix.selector);
      const symptom = this.inferIssueSymptomFromText(opts.bug_context, opts.expected_outcome, fix.label);
      const routeAction = fix.routeAction || 'verified-fix-fallback';
      const fixType = fix.fixType || 'shared-style-fix';

      const memoryKey = { app, routeAction, control, symptom };
      const memoryKeyHash = this.stableHashString([app, routeAction, control, symptom].join('|'));

      const record: AiBugfixLessonRecord = {
        application: app,
        commitId: this.appGitVersion?.commit || 'unknown',
        memoryKey,
        memoryKeyHash,
        compactedInput: {
          scope,
          path,
          bugContext: opts.bug_context.slice(0, 320),
          expectedResult: opts.expected_outcome.slice(0, 320)
        },
        routeThoughtToFixProblem: {
          fixType,
          routeAction,
          componentKey: targetElementHint?.surface || null,
          summary: fix.label.slice(0, 420),
          diagnosticEvidence: `selector: ${fix.selector}; source: ${fix.source || 'manual'}`,
          reasoning: [`Verified fix targeting ${fix.selector}`, `CSS: ${fix.rawCss.slice(0, 260)}`],
          filesToEdit: []
        },
        touchedFiles: [],
        artifactDir: 'ai-tooling/memory/'
      };

      records.push(record);
    }

    if (isPlatformBrowser(this.platformId)) {
      const jsonlLines = records.map(r => JSON.stringify(r)).join('\n');
      console.groupCollapsed(
        `%c📝 Lesson records (${records.length}) — save each JSON record under ai-tooling/memory/lessons/<app>/`,
        'color:#a6e3a1;font-weight:bold'
      );
      console.log(jsonlLines);
      console.groupEnd();

      console.log(`%c💡 Save under: %cai-tooling/memory/lessons/${app}/`, 'color:#89b4fa', 'color:#f5c2e7');
    }

    return records;
  }

  private inferIssueControlFromHint(targetElementHint: AiTargetElementHint | null, selector: string): string {
    if (targetElementHint?.componentChain?.length) {
      const surface = targetElementHint.componentChain[0];
      if (surface && !surface.startsWith('app-')) return surface;
    }

    const hostMatch = selector.match(/^(ush-\S+|feature-\S+|lib-\S+|p-\S+)/);
    if (hostMatch) return hostMatch[1];

    const keywords = [
      ['load-more', 'load-more'],
      ['dropdown', 'dropdown'],
      ['multiselect', 'multiselect'],
      ['showtime', 'showtimes'],
      ['streaming', 'watch-at-home'],
      ['movie-card', 'movie-card'],
      ['channel-video', 'channel-video'],
      ['carousel', 'carousel'],
      ['button', 'button'],
      ['filter', 'filter']
    ];
    const lowerSelector = selector.toLowerCase();
    for (const [keyword, label] of keywords) {
      if (lowerSelector.includes(keyword)) return label;
    }

    return 'unknown';
  }

  private inferIssueSymptomFromText(bugContext: string, expectedOutcome: string, fixLabel: string): string {
    const text = `${bugContext || ''} ${expectedOutcome || ''} ${fixLabel || ''}`.toLowerCase();
    const parts: string[] = [];

    if (/hover/.test(text)) parts.push('hover');
    if (/active/.test(text)) parts.push('active');
    if (/focus/.test(text)) parts.push('focus');
    if (/icon|\+/.test(text)) parts.push('icon');
    if (/underline/.test(text)) parts.push('underline');
    if (/background/.test(text)) parts.push('background');
    if (/color|colour|white|black|red|blue|green|yellow|orange|pink|purple|gray|grey/.test(text)) parts.push('color');
    if (/visible|visibility|contrast/.test(text)) parts.push('visibility');
    if (/position|align|left|right|center|centred/.test(text)) parts.push('position');
    if (/spacing|gap|margin|padding/.test(text)) parts.push('spacing');
    if (/font|typography|size|line-height/.test(text)) parts.push('typography');

    return parts.length
      ? parts.join('-')
      : (expectedOutcome || bugContext)
          .slice(0, 80)
          .toLowerCase()
          .replace(/[^a-z0-9-]+/g, '-')
          .replace(/^-+|-+$/g, '');
  }

  private stableHashString(input: string): string {
    let hash = 0;
    for (let i = 0; i < input.length; i++) {
      const char = input.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash |= 0;
    }
    return (hash >>> 0).toString(16).padStart(8, '0');
  }

  /** @internal exposed for the bug-report component so the form can re-score lessons as the user types. */
  findRelevantAiBugfixLessons(input: {
    app: string;
    bugContext: string;
    expectedOutcome: string;
    path: string;
    targetElementHint?: AiTargetElementHint | null;
  }): AiRelevantBugfixLesson[] {
    return findRelevantLessonsFromPackage(this.aiBugfixLessonsCache || [], {
      ...input,
      weights: this.lessonWeights
    });
  }

  /** @internal exposed for the bug-report component so the form can fall back to manual entry when the cache is still loading. */
  hasAiBugfixLessonsCache(): boolean {
    return this.aiBugfixLessonsCache !== null;
  }

  private buildAutomatedBugfixSkillPayload(
    request: AiAutomatedBugfixSkillConsoleRequest,
    targetElement?: Element | null
  ): AiAutomatedBugfixSkillInput {
    const normalizedRequest = typeof request === 'string' ? { bug_context: request } : (request ?? {});
    const bugContext = typeof normalizedRequest.bug_context === 'string' ? normalizedRequest.bug_context.trim() : '';

    const location = this.parseUrl(window.location.href);
    const appResolution = this.resolveAppIdentifier(normalizedRequest.app);
    const targetElementHint = normalizedRequest.target_element || this.describeElement(targetElement);
    const inferredVisualExpectations = this.buildInferredVisualExpectations(normalizedRequest, targetElementHint);
    const visualExpectations = {
      ...inferredVisualExpectations,
      ...(normalizedRequest.visual_expectations ?? {})
    };
    const inputQuality = this.buildAutomatedBugfixInputQuality(
      normalizedRequest,
      targetElementHint,
      visualExpectations,
      location.path
    );
    const relevantLessons = this.findRelevantAiBugfixLessons({
      app: appResolution.value,
      bugContext,
      expectedOutcome: String(normalizedRequest.expected_outcome || '').trim(),
      path: location.path,
      targetElementHint
    });

    return {
      ...normalizedRequest,
      app: appResolution.value,
      bug_context: bugContext,
      serve_configuration: normalizedRequest.serve_configuration ?? 'production',
      ...(Object.keys(visualExpectations).length > 0 ? { visual_expectations: visualExpectations } : {}),
      runtime_context: {
        source: 'window.usheru.buildAutomatedBugfixSkillInput',
        app_type: this.env.appType,
        channel_name: this.channelService.channel?.name ?? null,
        hostname: window.location.hostname,
        origin: window.location.origin,
        page_title: document.title,
        angular_version: this.getAngularVersion(),
        html_app_version: this.getHtmlAppVersion(),
        source_branch:
          (typeof normalizedRequest.branch === 'string' && normalizedRequest.branch.trim()) ||
          this.appGitVersion?.branch?.trim() ||
          undefined,
        source_commit: this.appGitVersion?.commit?.trim() || undefined,
        git_version: this.getGitVersionMetadata(),
        current_url: location.url,
        current_path: location.path,
        target_element: targetElementHint || undefined,
        input_quality: { ...inputQuality, questions: [] },
        app_resolution: appResolution,
        recorded_steps: this.getRecordedSteps().map(step => ({ ...step, queryParams: { ...step.queryParams } })),
        recorded_dom_interactions: (() => {
          const raw = this.getRecordedDomInteractions();
          const surface = targetElementHint?.surface || '';
          const filtered = raw.filter(i => {
            if (i.kind !== 'mouseenter') return true;
            if (!surface) return true;
            return Array.isArray(i.componentChain) && i.componentChain.includes(surface);
          });
          const noChatWidget = filtered.filter(
            i => i.surface !== '#usheru-bugfix-chat' && !String(i.selector || '').startsWith('#usheru-bugfix-chat')
          );
          const seenSelectors = new Set<string>();
          const deduped: typeof noChatWidget = [];
          for (let i = noChatWidget.length - 1; i >= 0; i--) {
            const item = noChatWidget[i];
            if (item.kind === 'mouseenter') {
              const key = item.selector || item.title || '';
              if (seenSelectors.has(key)) continue;
              seenSelectors.add(key);
            }
            deduped.unshift(item);
          }
          return deduped.map(i => ({ ...i }));
        })(),
        ...(relevantLessons.length > 0 ? { relevant_lessons: relevantLessons } : {})
      }
    };
  }

  private printAutomatedBugfixSkillPayloadPreflight(payload: AiAutomatedBugfixSkillInput) {
    const inputQuality = payload.runtime_context.input_quality;
    const visualExpectations = payload.visual_expectations || {};
    const issues: string[] = [];
    const warnings: string[] = [];

    // Validate required fields
    if (!payload.app) issues.push('App not resolved');
    if (!payload.runtime_context.current_path) issues.push('Route not detected');
    if (!payload.bug_context?.trim()) issues.push('Bug context is empty');
    if (!payload.expected_outcome?.trim()) issues.push('Expected outcome is empty');

    // Check for vague language
    const vaguePatterns = /\b(wrong|broken|issue|not working|should fix|be visible)\b/i;
    if (vaguePatterns.test(payload.bug_context)) {
      warnings.push('Bug context is too vague — be specific about the symptom');
    }
    if (vaguePatterns.test(payload.expected_outcome)) {
      warnings.push('Expected outcome could be more specific — describe how to verify');
    }

    // Check for routing issues
    if (
      this.hasAutomatedBugfixScopePathMismatch(
        `${payload.bug_context}\n${payload.expected_outcome}`,
        payload.runtime_context.current_path,
        payload.runtime_context.target_element
      )
    ) {
      warnings.push('Route/scope might be mismatched — confirm the target surface matches the path');
    }

    // Print checklist
    const divider = '━'.repeat(70);
    console.log(`\n${divider}`);
    console.log('%c📋 INPUT QUALITY CHECK', 'color:#0f766e;font-weight:700;font-size:14px');
    console.log(`${divider}\n`);

    // Status lines
    const cmPayload = payload as unknown as Record<string, unknown>;
    console.log(`%c✓%c App: ${payload.app}`, 'color:#15803d;font-weight:700', 'color:inherit');
    if (cmPayload.ai_tooling_version) {
      const ts = cmPayload.ai_tooling_generated_at ? ` (${cmPayload.ai_tooling_generated_at})` : '';
      console.log(
        `%c✓%c AI Tooling: v${cmPayload.ai_tooling_version}${ts}`,
        'color:#15803d;font-weight:700',
        'color:inherit'
      );
    }
    // Show version line even when no ai_tooling_version (e.g. legacy or fallback path)
    if (!cmPayload.ai_tooling_version && cmPayload.ai_tooling_generated_at) {
      console.log(
        `%c✓%c Generated: ${cmPayload.ai_tooling_generated_at}`,
        'color:#15803d;font-weight:700',
        'color:inherit'
      );
    }
    console.log(
      `%c✓%c Route: ${payload.runtime_context.current_path}`,
      'color:#15803d;font-weight:700',
      'color:inherit'
    );

    if (payload.runtime_context.target_element?.selector) {
      console.log(
        `%c✓%c Element: ${payload.runtime_context.target_element.selector}`,
        'color:#15803d;font-weight:700',
        'color:inherit'
      );
    } else {
      console.log(`%c⚠%c Element selector: not captured`, 'color:#b45309;font-weight:700', 'color:inherit');
    }

    if (payload.runtime_context.relevant_lessons?.length) {
      console.log(
        `%c✓%c Relevant lessons: ${payload.runtime_context.relevant_lessons.length} from app-specific ai-bugfix lesson assets`,
        'color:#15803d;font-weight:700',
        'color:inherit'
      );
      payload.runtime_context.relevant_lessons.forEach(lesson => {
        const summary =
          lesson.routeThoughtToFixProblem?.summary || lesson.memoryKeyHash || lesson.application || 'lesson';
        console.log(`   - ${summary}`);
      });
    } else if (this.aiBugfixLessonsCache === null) {
      console.log(
        `%cℹ%c Lessons: still loading from app-specific ai-bugfix lesson assets; run again if you want prior fixes attached.`,
        'color:#2563eb;font-weight:700',
        'color:inherit'
      );
    }

    if (visualExpectations.referenceControlText) {
      console.log(
        `%c✓%c Reference: "${visualExpectations.referenceControlText}"`,
        'color:#15803d;font-weight:700',
        'color:inherit'
      );
    }

    // Issues
    if (issues.length > 0) {
      console.log(`\n%cISSUES (required):`, 'color:#dc2626;font-weight:700');
      issues.forEach(issue => console.log(`  %c✕%c ${issue}`, 'color:#dc2626;font-weight:700', 'color:inherit'));
    }

    // Warnings
    if (warnings.length > 0) {
      console.log(`\n%cIMPROVEMENTS (recommended):`, 'color:#b45309;font-weight:700');
      warnings.forEach(warning => console.log(`  %c⚠%c ${warning}`, 'color:#b45309;font-weight:700', 'color:inherit'));
    }

    // Ready status
    console.log(`\n${divider}`);
    const ready = issues.length === 0;
    console.log(
      `%c${ready ? '✓ READY TO SUBMIT' : '✕ NEEDS FIXES'}`,
      ready ? 'color:#15803d;font-weight:700;font-size:13px' : 'color:#dc2626;font-weight:700;font-size:13px'
    );
    console.log(`${divider}\n`);

    if (ready) {
      console.log('Copy the output above into GitHub Actions reproduction_steps input.');
    } else {
      console.log('Fix the issues above, then run the helper again.');
    }
  }

  private buildValidationExpectationsNarrative(payload: AiAutomatedBugfixSkillInput): string[] {
    const lines: string[] = [];
    const interactions = payload.runtime_context.recorded_dom_interactions || [];
    const visualEx = payload.visual_expectations || {};
    const targetEl = payload.runtime_context.target_element;

    lines.push('Validation will replay these steps:');

    for (const entry of payload.runtime_context.recorded_steps || []) {
      lines.push(`  ${entry.index}. Navigate to ${entry.title || entry.url}`);
    }

    for (const interaction of interactions) {
      const idx = interaction.index || 0;
      const label = interaction.title || interaction.kind;
      const valueStr = interaction.value ? ` "${interaction.value}"` : '';
      switch (interaction.kind) {
        case 'click':
          lines.push(`  ${idx}. Click ${label}`);
          break;
        case 'input':
          lines.push(`  ${idx}. Fill ${interaction.selector || label} with${valueStr}`);
          break;
        case 'change':
          if (interaction.value) {
            lines.push(`  ${idx}. Change ${interaction.selector || label} to${valueStr}`);
          } else {
            lines.push(`  ${idx}. Interact with ${interaction.selector || label} (change)`);
          }
          break;
        case 'mouseenter':
          lines.push(`  ${idx}. Hover ${interaction.selector || label}`);
          break;
        case 'submit':
          lines.push(`  ${idx}. Submit form ${label}`);
          break;
        default:
          lines.push(`  ${idx}. ${label}`);
      }
    }

    lines.push('');
    lines.push('Then validate:');

    if (targetEl) {
      const props = (visualEx.comparisonProperties as string[]) || ['color', 'background-color', 'border-color'];
      lines.push(`  Target: <${targetEl.tagName || ''}> ${targetEl.selector || ''}`);
      if (targetEl.componentChain?.length) {
        lines.push(`  Component chain: ${targetEl.componentChain.join(' → ')}`);
      }
      lines.push(`  Check computed ${props.join(', ')}`);
    }

    if (visualEx.issueType) {
      lines.push(`  Issue type: ${visualEx.issueType}`);
    }

    if (visualEx.referenceControlText) {
      lines.push(`  Reference: "${visualEx.referenceControlText}" (${visualEx.referenceControlKind || 'control'})`);
    }

    lines.push(`  Route must remain: ${payload.runtime_context.current_path}`);

    return lines;
  }

  private printValidationExpectations(payload: AiAutomatedBugfixSkillInput) {
    const narrative = this.buildValidationExpectationsNarrative(payload);
    console.group('%cValidation expectations', 'color:#7c3aed;font-weight:700');
    for (const l of narrative) {
      console.log(`  ${l}`);
    }
    console.groupEnd();
  }

  private buildInferredVisualExpectations(
    request: AiAutomatedBugfixSkillRequest,
    targetElementHint: AiTargetElementHint | null
  ): Record<string, AiSerializableValue> {
    const issueType = this.inferVisualIssueType(request, targetElementHint);
    const referenceControl = this.inferReferenceControl(request);
    const referenceElement = referenceControl
      ? this.findReferenceControlElement(referenceControl, targetElementHint)
      : null;
    const expectations: Record<string, AiSerializableValue> = {};

    if (issueType) {
      expectations.issueType = issueType;
      expectations.comparisonProperties = this.getComparisonPropertiesForIssueType(issueType);
    }

    if (referenceControl) {
      expectations.referenceControlText = referenceControl.text;
      expectations.referenceControlTextCandidates = referenceControl.textCandidates;
      expectations.referenceControlKind = referenceControl.kind;
    }

    if (referenceElement) {
      expectations.referenceSelector = referenceElement.selector;
      expectations.referenceSelectorCandidates = referenceElement.selectorCandidates;
      expectations.referenceSurface = referenceElement.surface || '';
      expectations.referenceComponentChain = referenceElement.componentChain;
    }

    return expectations;
  }

  private buildAutomatedBugfixInputQuality(
    request: AiAutomatedBugfixSkillRequest,
    targetElementHint: AiTargetElementHint | null,
    visualExpectations: Record<string, AiSerializableValue>,
    currentPath: string
  ): AiAutomatedBugfixInputQuality {
    const issueType = String(visualExpectations.issueType || '').trim();
    const referenceText = String(visualExpectations.referenceControlText || '').trim();
    const referenceKind = String(visualExpectations.referenceControlKind || '').trim();
    const referenceSelector = String(visualExpectations.referenceSelector || '').trim();
    const combined = `${request.bug_context || ''}\n${request.expected_outcome || ''}`.toLowerCase();
    const warnings: string[] = [];
    const questions: string[] = [];
    const localPathCandidate = this.stripLeadingLocaleSegment(currentPath);

    if (localPathCandidate !== currentPath) {
      warnings.push(
        `Current route ${currentPath} includes a leading locale segment. If local validation lands on /not-found, retry the same route without that prefix: ${localPathCandidate}.`
      );
      questions.push(`Should local validation use ${currentPath} or the locale-less route ${localPathCandidate}?`);
    }

    if (this.hasAutomatedBugfixScopePathMismatch(combined, currentPath, targetElementHint)) {
      warnings.push(
        'The written scope sounds like movie detail, but the current route or selected surface looks like movies catalogue. Prefer current_path and the rendered component over guessed scope labels.'
      );
    }

    if (!issueType) {
      warnings.push(
        'The helper could not infer whether this is typography, visibility/overlap, spacing/layout, color, size, alignment, interaction, or content.'
      );
      questions.push(
        'Which visual property is wrong: typography, visibility/overlap, spacing/layout, color, size, or alignment?'
      );
    }

    if (referenceText && !referenceSelector) {
      warnings.push(
        `The request mentions "${referenceText}" as a reference, but the helper did not locate a visible matching control on this page.`
      );
      questions.push(
        `Should the selected target match the nearby "${referenceText}" ${referenceKind || 'control'}? If yes, mention the exact visible reference text or inspect that reference control.`
      );
    }

    if (!referenceText && !request.source_of_truth && !this.hasExplicitVisualValue(combined)) {
      questions.push(
        'Should validation compare the selected target against production, a nearby reference control, or a specific value?'
      );
    }

    if (!targetElementHint && /\b(selected|this|nearby|font|spacing|layout|color|looks different)\b/i.test(combined)) {
      warnings.push(
        'No selected element was included. For ambiguous visual bugs, inspect the broken element and pass $0 as the helper second argument.'
      );
    }

    const domInteractions = this.getRecordedDomInteractions();
    if (/\b(overlay|dropdown|popup|panel|dialog|modal|menu)\b/i.test(combined) && domInteractions.length === 0) {
      warnings.push(
        'The bug involves an overlay/panel that must be opened to see the issue. Reload with ?recordSteps=true and click to open it before re-running.'
      );
    }

    const confidence: AiAutomatedBugfixInputQuality['confidence'] =
      issueType && (!referenceText || referenceSelector) && targetElementHint
        ? 'high'
        : issueType || targetElementHint
          ? 'medium'
          : 'low';

    return {
      ...(issueType ? { issue_type: issueType } : {}),
      confidence,
      warnings,
      questions: questions.slice(0, 2),
      ...(referenceText ? { detected_reference_text: referenceText } : {}),
      ...(referenceKind ? { detected_reference_kind: referenceKind } : {})
    };
  }

  private inferVisualIssueType(request: AiAutomatedBugfixSkillRequest, targetElementHint: AiTargetElementHint | null) {
    const combined = [
      request.bug_context,
      request.expected_outcome,
      targetElementHint?.selector,
      targetElementHint?.surface,
      targetElementHint?.label,
      targetElementHint?.text
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

    if (/\b(font|font-size|font-family|typography|typeface|text style)\b/.test(combined)) {
      return 'typography';
    }
    if (
      /\b(checkmark|checkbox|checked|tick|multiselect|selection|p-checkbox|check-icon|check\s*(?:mark|box|ed)?)\b/.test(
        combined
      ) &&
      /\b(colou?r|contrast|background|white|black|dark|light|visible|readable)\b/.test(combined)
    ) {
      return 'color';
    }
    if (
      /\b(not visible|hidden|invisible|overlap|overlapping|overlaps|overlaying|overlaid|covered|covering|occluded|occluding|behind|under)\b/.test(
        combined
      )
    ) {
      return 'visibility';
    }
    if (
      /\b(spacing|padding|margin|gap|too close|overlap|line gap|separation|layout)\b|::after|::before/.test(combined)
    ) {
      return 'spacing';
    }
    if (/\b(colou?r|background|contrast|readable)\b/.test(combined)) {
      return 'color';
    }
    if (/\b(width|height|size|too big|too small)\b/.test(combined)) {
      return 'size';
    }
    if (/\b(alignment|align|misaligned|centered|baseline)\b/.test(combined)) {
      return 'alignment';
    }
    if (/\b(left|right|position|icon)\b/.test(combined)) {
      return 'position';
    }
    if (/\b(click|hover|focus|active|open|close|select|change)\b/.test(combined)) {
      return 'interaction';
    }
    if (/\b(text|copy|label|title|content|translation)\b/.test(combined)) {
      return 'content';
    }

    return '';
  }

  private getComparisonPropertiesForIssueType(issueType: string): string[] {
    switch (issueType) {
      case 'typography':
        return ['font-size', 'font-family', 'font-weight', 'line-height', 'color'];
      case 'visibility':
        return ['display', 'visibility', 'opacity', 'bounding-box', 'z-index'];
      case 'spacing':
        return ['gap', 'margin', 'padding', 'bounding-box'];
      case 'color':
        return ['color', 'background-color', 'border-color'];
      case 'size':
        return ['width', 'height', 'bounding-box'];
      case 'alignment':
        return ['bounding-box', 'offset', 'alignment'];
      case 'position':
        return ['order', 'position', 'left', 'right', 'float', 'flex-direction', 'icon-position'];
      default:
        return [];
    }
  }

  private inferReferenceControl(request: AiAutomatedBugfixSkillRequest) {
    const combined = `${request.bug_context || ''}\n${request.expected_outcome || ''}`;
    const candidates: Array<{ text: string; kind: string }> = [];
    const pushCandidate = (text: string, kind = '') => {
      const normalizedText = this.normalizeReferenceText(text);
      if (
        !normalizedText ||
        candidates.some(candidate => this.slugify(candidate.text) === this.slugify(normalizedText))
      ) {
        return;
      }
      candidates.push({ text: normalizedText, kind: kind || this.inferReferenceKind(combined) });
    };

    for (const match of combined.matchAll(
      /\b([A-Z][A-Za-z0-9 &/-]{1,40})\s+(select|dropdown|control|field|input|label|button)\b/g
    )) {
      pushCandidate(match[1], match[2]);
    }

    for (const match of combined.matchAll(
      /\b(?:same\s+as|match(?:es)?|matching|like|against|compare(?:d)?\s+(?:to|with)|check)\s+(?:the\s+)?["'“”]?([A-Z][A-Za-z0-9 &/-]{1,40})["'“”]?/g
    )) {
      pushCandidate(match[1]);
    }

    if (/\bcinemas?\b/i.test(combined)) {
      pushCandidate('Cinema', /select|dropdown|control|field/i.test(combined) ? 'select' : '');
    }

    const selected = candidates[0];
    if (!selected) {
      return null;
    }

    return {
      text: selected.text,
      textCandidates: this.buildReferenceTextCandidates(selected.text),
      kind: selected.kind || 'control'
    };
  }

  private inferReferenceKind(value: string) {
    if (/\b(select|dropdown|combobox)\b/i.test(value)) {
      return 'select';
    }
    if (/\b(button|cta)\b/i.test(value)) {
      return 'button';
    }
    if (/\b(input|field)\b/i.test(value)) {
      return 'field';
    }
    if (/\blabel\b/i.test(value)) {
      return 'label';
    }
    return 'control';
  }

  private normalizeReferenceText(value: string) {
    return this.normalizeText(value)
      .replace(/\b(for|from|on|in|after|before|should|must|that|which|nearby)\b[\s\S]*$/i, '')
      .replace(/\b(select|dropdown|control|field|input|label|button)\b$/i, '')
      .trim();
  }

  private buildReferenceTextCandidates(value: string) {
    const text = this.normalizeReferenceText(value);
    const candidates = [text];
    if (/s$/i.test(text)) {
      candidates.push(text.replace(/s$/i, ''));
    } else {
      candidates.push(`${text}s`);
    }
    return [...new Set(candidates.filter(Boolean))];
  }

  private findReferenceControlElement(
    referenceControl: { textCandidates: string[]; kind: string },
    targetElementHint: AiTargetElementHint | null
  ): AiTargetElementHint | null {
    const targetSelector = targetElementHint?.selector || targetElementHint?.selectorCandidates?.[0] || '';
    const targetElement = targetSelector ? document.querySelector<HTMLElement>(targetSelector) : null;
    const root =
      targetElement?.closest<HTMLElement>(
        [
          'form',
          '[role="group"]',
          '[role="region"]',
          'ush-movies-page',
          'ush-custom-page',
          'alti-movie-detail',
          'app-root'
        ].join(', ')
      ) || document.body;
    const candidates = Array.from(
      root.querySelectorAll<HTMLElement>(
        [
          '.p-select-label',
          '.p-dropdown-label',
          '.p-multiselect-label',
          '[role="combobox"]',
          'input[placeholder]',
          'textarea[placeholder]',
          'button',
          'select',
          'label',
          'span',
          'div'
        ].join(', ')
      )
    )
      .filter(element => this.isElementVisible(element))
      .filter(element => !targetElement || element !== targetElement);

    const normalizedReferenceTexts = referenceControl.textCandidates.map(candidate => this.slugify(candidate));
    const ranked = candidates
      .map((element, index) => {
        const text = this.slugify(
          this.getElementText(element) ||
            element.getAttribute('placeholder') ||
            this.getElementLabel(element) ||
            element.getAttribute('aria-label') ||
            ''
        );
        if (!text || !normalizedReferenceTexts.includes(text)) {
          return null;
        }

        let score = 0;
        const className = typeof element.className === 'string' ? element.className.toLowerCase() : '';
        const role = element.getAttribute('role')?.toLowerCase() || '';
        if (className.includes('p-select-label') || className.includes('p-dropdown-label')) score += 10;
        if (className.includes('p-multiselect-label')) score += 9;
        if (role === 'combobox') score += 8;
        if (
          referenceControl.kind === 'select' &&
          element.closest('p-select, p-dropdown, p-multiselect, .p-select, .p-dropdown, .p-multiselect')
        ) {
          score += 6;
        }
        if (targetElement) {
          const targetRect = targetElement.getBoundingClientRect();
          const rect = element.getBoundingClientRect();
          score += Math.max(0, 6 - Math.abs(rect.top - targetRect.top) / 80);
        }
        return { element, index, score };
      })
      .filter((item): item is { element: HTMLElement; index: number; score: number } => !!item)
      .sort((left, right) => right.score - left.score || left.index - right.index);

    return ranked[0] ? this.describeElement(ranked[0].element) : null;
  }

  private hasExplicitVisualValue(value: string) {
    return /\b\d+(?:\.\d+)?\s*(?:px|rem|em|%)\b|#[0-9a-f]{3,8}\b|rgb\(|\b(white|black|red|blue|green|yellow|orange|pink|purple|gray|grey)\b/i.test(
      value
    );
  }

  private stripLeadingLocaleSegment(path: string) {
    const match = path.match(/^\/[a-z]{2}(?:-[a-z]{2})?(?=\/|$)(\/.*)?$/i);
    if (!match) {
      return path;
    }

    return match[1] || '/';
  }

  private buildLocalValidationRouteSuggestion(currentUrl: string) {
    const parsedUrl = new URL(currentUrl, window.location.origin);
    const localPath = this.stripLeadingLocaleSegment(parsedUrl.pathname);
    if (localPath === parsedUrl.pathname) {
      return currentUrl;
    }

    return `${localPath}${parsedUrl.search}${parsedUrl.hash}`;
  }

  private hasAutomatedBugfixScopePathMismatch(
    combinedInput: string,
    currentPath: string,
    targetElementHint?: AiTargetElementHint | null
  ) {
    const normalizedCombined = combinedInput.toLowerCase();
    const normalizedPath = currentPath.toLowerCase();
    const targetText = [
      targetElementHint?.selector,
      ...(targetElementHint?.selectorCandidates || []),
      targetElementHint?.surface,
      ...(targetElementHint?.componentChain || []),
      ...(targetElementHint?.relatedOwners || []).flatMap(owner => [
        owner.kind,
        owner.selector,
        owner.tagName,
        ...(owner.classes || [])
      ])
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    const pathLooksLikeMoviesCatalogue = /^\/(?:[a-z]{2}(?:-[a-z]{2})?)?\/movies(?:\/|$)/.test(normalizedPath);
    const targetLooksLikeMoviesCatalogue =
      /\bush-movies-page\b|tag-filter|multiselectbox|filters-container|sort-by-filter/.test(targetText);
    const inputSoundsLikeMovieDetail =
      /\bmovie detail\b|\bmovie page\b|\bsynopsis\b|\bposter\b|\btrailer\b|\bhero\b/.test(normalizedCombined);

    return inputSoundsLikeMovieDetail && (pathLooksLikeMoviesCatalogue || targetLooksLikeMoviesCatalogue);
  }

  private inferAutomatedBugfixScope(path: string): string {
    const lower = (path || '').toLowerCase();
    if (lower.startsWith('/movie/')) return 'movie detail';
    if (lower.startsWith('/movies') || lower.includes('/in-cinemas') || lower.includes('/watch-at-home'))
      return 'movies catalogue';
    if (lower === '/home' || lower === '/') return 'home page';
    if (lower.startsWith('/auth/')) return 'authentication';
    const firstSegment = lower.split('/').filter(Boolean)[0] || '';
    return firstSegment || 'generic';
  }

  private inferAutomatedBugfixRouteFocus(payload: AiAutomatedBugfixSkillInput) {
    const targetText = [
      payload.runtime_context.target_element?.selector,
      ...(payload.runtime_context.target_element?.selectorCandidates || []),
      ...(payload.runtime_context.target_element?.componentChain || []),
      payload.runtime_context.target_element?.surface,
      ...(payload.runtime_context.target_element?.relatedOwners || []).flatMap(owner => [
        owner.kind,
        owner.selector,
        owner.tagName,
        ...(owner.classes || [])
      ])
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    const recordedStepsPaths = (payload.runtime_context.recorded_steps ?? []).map(step => step.path).join(' ');
    const combined = [
      payload.runtime_context.current_path,
      payload.bug_context,
      payload.expected_outcome,
      targetText,
      recordedStepsPaths
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

    if (/ush-movies-carousel|carousel__title|carousel__view-all/.test(targetText)) {
      return 'home page / movies carousel';
    }

    if (/ush-feature-channel-carousel|ush-feature-channel-video|alti-channel-carousel/.test(targetText)) {
      return 'home page / channel carousel';
    }

    if (/(^|\s)\/home\b|\bhome page\b|\bhomepage\b|\bcarousel\b|\bheader\b|::after/.test(combined)) {
      return 'home page / header / carousel';
    }

    if (/\bshowtime|cinema|screening|ticket|booking\b/.test(combined)) {
      return 'showtimes / booking';
    }

    if (
      /(^|\s)\/movie\/|\bmovie.detail\b|\bmovie-detail|\bmovies?[- ]?detail\b|\bush-movie-detail|\bfeature-film-interactions\b|\bhero\b|\bposter\b|\bsynopsis\b|\btrailer\b/.test(
        combined
      )
    ) {
      return 'movie detail';
    }

    if (
      /(^|\s)\/movie-catalogue\/|\bmovie.catalogue\b|\bcatalogue\b|\bcatalog\b|\bload.more\b|\bfilter\b|\bsearch\b/.test(
        combined
      )
    ) {
      return 'movies catalogue';
    }

    if (/(^|\s)\/watch-at-home\/|\bwatch.at.home\b|\bstreaming\b/.test(combined)) {
      return 'watch at home';
    }

    return 'generic route';
  }

  private hasAutomatedBugfixRouteLocalWording(payload: AiAutomatedBugfixSkillInput, routeFocus: string) {
    const bugContext = payload.bug_context.toLowerCase();
    const currentPath = payload.runtime_context.current_path.toLowerCase();

    if (
      payload.runtime_context.target_element &&
      /\bselected element\b|\bselected component\b|\bselected control\b|\bselected title\b|\bselected header\b|\bselected section\b/.test(
        bugContext
      )
    ) {
      return true;
    }

    if (currentPath && currentPath !== '/' && bugContext.includes(currentPath)) {
      return true;
    }

    if (routeFocus.includes('home')) {
      return /\bhome\b|\bcarousel\b|\bheader\b|\btitle\b|::after/.test(bugContext);
    }

    return (
      /\b(on|in|after|when)\b/.test(bugContext) &&
      /\b(page|route|header|button|cta|carousel|modal|filter|section|title|line)\b/.test(bugContext)
    );
  }

  private logAutomatedBugfixChecklistEntry(ok: boolean, text: string) {
    const style = ok ? 'color:#15803d;font-weight:700' : 'color:#b45309;font-weight:700';
    console.log('%c%s%c %s', style, ok ? 'OK' : 'CHECK', 'color:inherit;font-weight:400', text);
  }

  private readonly RECORD_STEPS_KEY = 'usheru-recordSteps';
  private listenersRegistered = false;
  private recordingAbortController: AbortController | null = null;
  private scrollDebounceTimer: number | null = null;
  private mouseenterDebounceMap = new Map<string, number>();

  private getRecordStepsConfig(): Set<string> {
    const fromUrl = new URLSearchParams(window.location.search).get('recordSteps');
    const fromStorage = sessionStorage.getItem(this.RECORD_STEPS_KEY);
    const raw = fromUrl ?? fromStorage ?? '';
    if (fromUrl && fromUrl !== fromStorage) {
      try {
        sessionStorage.setItem(this.RECORD_STEPS_KEY, fromUrl);
      } catch {
        /* noop */
      }
    }
    if (!raw) return new Set();
    const trimmed = raw.trim().toLowerCase();
    if (trimmed === '' || trimmed === '1' || trimmed === 'true' || trimmed === 'all') {
      return new Set(['*']);
    }
    const types = trimmed
      .split(',')
      .map(s => s.trim())
      .filter(Boolean);
    return new Set(types);
  }

  /** Whether a specific event type is enabled in the current config. */
  private isRecordStepTypeEnabled(type: string): boolean {
    const config = this.getRecordStepsConfig();
    return config.has('*') || config.has(type);
  }

  // Avoid storing duplicate consecutive URLs when Angular emits equivalent end states.
  private recordStep(type: AiRecordedStep['type'], rawUrl: string) {
    const step = this.buildStep(type, rawUrl);
    const recordedSteps = this.getRecordedSteps();
    const previousStep = recordedSteps[recordedSteps.length - 1];

    if (previousStep?.url === step.url) {
      return;
    }

    recordedSteps.push({
      ...step,
      index: recordedSteps.length + 1
    });
  }

  /**
   * Live CSS variable diagnostics for a DOM element.
   * Walks up the component chain, reads resolved custom property values,
   * cross-references the component map, detects contrast issues,
   * and suggests scoped app-level overrides.
   *
   * Usage from console:
   *   await window.usheru.diagnoseElement($0)
   *   await window.usheru.diagnoseElement(document.querySelector('.p-autocomplete-option'))
   *
   * @param app Optional app identifier to prefer entries from a specific app (avoids cross-app name collisions)
   * @param bugContext Optional bug description to tailor fix suggestions (e.g. detects position/icon keywords)
   */
  async diagnoseElement(element?: Element | null, app?: string, bugContext?: string) {
    if (!element || !(element instanceof HTMLElement)) {
      console.warn('diagnoseElement expects an HTMLElement. Pass $0 from DevTools.');
      return null;
    }

    const componentMap = await this.fetchComponentMap();
    const chain = this.buildComponentChain(element);
    const entryStyle = getComputedStyle(element);
    const shouldSuggestColorFixes = this.shouldSuggestColorDiagnosticFixes(bugContext);

    const hoverStyles = this.findMatchingHoverStyles(element);

    const report: {
      target: { selector: string; tagName: string; componentChain: string[] };
      computedColors: Record<string, string>;
      computedLayout: Record<string, string>;
      layoutHints: string[];
      hoverStyles: Record<string, string>;
      contrastIssues: Array<{ propA: string; propB: string; ratio: number; severity: string }>;
      hosts: Array<{
        tagName: string;
        mapEntry: { symbolName: string; sourcePath: string } | null;
        variables: Array<{ name: string; value: string; kind: 'exposed' | 'consumed' | 'bridge' }>;
      }>;
      fixSuggestions: AiCssFixSuggestion[];
    } = {
      target: {
        selector: this.getElementSelector(element),
        tagName: element.tagName.toLowerCase(),
        componentChain: chain
      },
      computedColors: {},
      computedLayout: {},
      layoutHints: [],
      hoverStyles,
      contrastIssues: [],
      hosts: [],
      fixSuggestions: []
    };

    // Collect computed color properties
    for (const prop of ['color', 'background-color', 'border-top-color', 'border-bottom-color', 'fill', 'stroke']) {
      const val = entryStyle.getPropertyValue(prop).trim();
      if (val && val !== 'transparent' && !val.startsWith('rgba(0, 0, 0, 0)')) {
        report.computedColors[prop] = val;
      }
    }

    // Collect layout-relevant computed properties
    for (const prop of [
      'text-align',
      'background-position',
      'background-position-x',
      'background-position-y',
      'padding-left',
      'padding-right',
      'padding-top',
      'padding-bottom',
      'margin-left',
      'margin-right',
      'margin-top',
      'margin-bottom',
      'left',
      'right',
      'top',
      'bottom',
      'float',
      'display',
      'flex-direction',
      'align-items',
      'justify-content',
      'order',
      'white-space',
      'overflow',
      'position',
      'vertical-align'
    ]) {
      const val = entryStyle.getPropertyValue(prop).trim();
      if (val) {
        report.computedLayout[prop] = val;
      }
    }

    // Detect PrimeNG icon position classes
    const iconContainer = element.closest(
      '.p-input-icon-left, .p-input-icon-right, .p-iconfield-left, .p-iconfield-right'
    );
    if (iconContainer) {
      const side =
        iconContainer.classList.contains('p-input-icon-left') || iconContainer.classList.contains('p-iconfield-left')
          ? 'left'
          : 'right';
      const classPrefix =
        iconContainer.classList.contains('p-iconfield-left') || iconContainer.classList.contains('p-iconfield-right')
          ? 'p-iconfield'
          : 'p-input-icon';
      report.layoutHints.push(
        `PrimeNG icon position class "${classPrefix}-${side}" detected — icon is positioned on the ${side}`
      );
      if (bugContext && /\b(icon|left|right|position)\b/i.test(bugContext)) {
        const oppositeSide = side === 'left' ? 'right' : 'left';
        const iconContainerSelector = this.getPrimeNgIconContainerSelector(iconContainer as HTMLElement);
        const rightOffset = 'var(--p-form-field-padding-x, 0.75rem)';
        const iconPadding = 'calc((var(--p-icon-size, 1rem) * 1.5) + var(--p-form-field-padding-x, 0.75rem))';
        const rawCss =
          oppositeSide === 'right'
            ? `${iconContainerSelector} > .p-inputicon,\n${iconContainerSelector} > .p-icon {\n  left: auto !important;\n  right: ${rightOffset} !important;\n}\n${iconContainerSelector} > .p-inputtext {\n  padding-left: var(--p-form-field-padding-x, 0.75rem) !important;\n  padding-right: ${iconPadding} !important;\n}`
            : `${iconContainerSelector} > .p-inputicon,\n${iconContainerSelector} > .p-icon {\n  right: auto !important;\n  left: ${rightOffset} !important;\n}\n${iconContainerSelector} > .p-inputtext {\n  padding-right: var(--p-form-field-padding-x, 0.75rem) !important;\n  padding-left: ${iconPadding} !important;\n}`;
        report.fixSuggestions.push({
          variable: 'structural-icon-position',
          currentValue: `${classPrefix}-${side}`,
          suggestedValue: `${classPrefix}-${oppositeSide}`,
          scope: iconContainerSelector,
          reason: `Icon is on the ${side} (class "${classPrefix}-${side}") — test a narrow CSS override that places the icon on the ${oppositeSide}`,
          rawCss
        });
      }
    }

    // Detect contrast issues for target element
    const bg = report.computedColors['background-color'];
    const fg = report.computedColors['color'];
    if (bg && fg) {
      const ratio = this.computeContrastRatio(bg, fg);
      if (ratio !== null) {
        report.contrastIssues.push({
          propA: 'background-color',
          propB: 'color',
          ratio,
          severity: ratio < 3 ? 'FAIL (AA normal text needs >= 4.5)' : ratio < 4.5 ? 'WARN' : 'PASS'
        });
      }
    }

    // Inspect PrimeNG child elements (icon, checkbox, toggle, slider, etc.)
    const searchRoot = getSearchContainerForChildren(element) as HTMLElement | null;
    const childElt = searchRoot
      ? PRIMENG_CHILD_SELECTORS.reduce<HTMLElement | null>(
          (found, sel) => found || searchRoot.querySelector<HTMLElement>(sel),
          null
        )
      : null;
    if (childElt) {
      const childStyle = getComputedStyle(childElt);
      const childSelector = this.getElementSelector(childElt);

      // Collect color properties from the child element
      for (const prop of PRIMENG_COLOR_PROPERTIES) {
        const val = childStyle.getPropertyValue(prop).trim();
        if (val && val !== 'transparent' && !val.startsWith('rgba(0, 0, 0, 0)')) {
          report.computedColors[`child-${prop}`] = val;
        }
      }

      // Gather ancestor primengBridge entries into a flat list
      const ancestorBridges: PrimengBridgeEntry[] = [];
      for (const tagName of chain) {
        const host = element.closest<HTMLElement>(tagName);
        if (!host) continue;
        const entry = this.resolveComponentMapEntryForRuntimeSelector(componentMap.entries || [], tagName, app);
        if (!entry?.primengBridge) continue;
        for (const [compVar, bridge] of Object.entries(
          entry.primengBridge as Record<string, { primengToken: string }>
        )) {
          ancestorBridges.push({ componentVar: compVar, primengToken: bridge.primengToken, scope: tagName });
        }
      }

      // Read all PrimeNG token values from the child
      const childTokenValues = new Map<string, string>();
      for (const { primengToken } of ancestorBridges) {
        const val = childStyle.getPropertyValue(primengToken).trim();
        if (val && val !== 'transparent' && !val.startsWith('rgba(0, 0, 0, 0)')) {
          childTokenValues.set(primengToken, val);
        }
      }

      // Resolve bridge mappings using shared resolver
      const resolvedMappings = resolveBridgeMappings(ancestorBridges, childTokenValues);

      // Populate componentVarValue for each mapping
      for (const tagName of chain) {
        const host = element.closest<HTMLElement>(tagName);
        if (!host) continue;
        const hostStyle = getComputedStyle(host);
        for (const mapping of resolvedMappings) {
          if (mapping.scope === tagName) {
            const compVal = hostStyle.getPropertyValue(mapping.componentVar).trim();
            if (compVal) mapping.componentVarValue = compVal;
          }
        }
      }

      if (resolvedMappings.length > 0) {
        report.layoutHints.push(
          `PrimeNG bridge variable chain: ${resolvedMappings.map(m => `${m.scope} sets ${m.componentVar} → ${m.primengToken}=${m.tokenValue}`).join(' | ')}`
        );

        if (shouldSuggestColorFixes) {
          for (const mapping of resolvedMappings) {
            if (!mapping.tokenValue) continue;
            const isBackground = mapping.primengToken.includes('background');
            const suggestedValue = this.suggestFixColor(
              mapping.tokenValue,
              isBackground ? 'background-color' : 'color',
              report.computedColors
            );
            report.fixSuggestions.push({
              variable: mapping.componentVar,
              currentValue: mapping.componentVarValue,
              suggestedValue,
              scope: mapping.scope,
              primengToken: mapping.primengToken,
              reason: `${mapping.primengToken} resolves to ${mapping.tokenValue} via ${mapping.scope} → ${mapping.componentVar}`,
              rawCss: `${mapping.scope} {\n  ${mapping.componentVar}: ${suggestedValue};\n}`
            });
          }
        }
      } else {
        // Fallback: direct contrast check if no bridge mappings found
        const childBg = childStyle.getPropertyValue('background-color').trim();
        const parentBg =
          report.computedColors['background-color'] || entryStyle.getPropertyValue('background-color').trim();
        if (shouldSuggestColorFixes && childBg && parentBg) {
          const ratio = this.computeContrastRatio(childBg, parentBg);
          if (ratio !== null && ratio < 4.5) {
            report.contrastIssues.push({
              propA: 'child-element background-color',
              propB: 'target background-color',
              ratio,
              severity: ratio < 3 ? 'FAIL (child vs parent contrast too low)' : 'WARN'
            });
            const suggestedBg = this.suggestFixColor(childBg, 'background-color', report.computedColors);
            report.fixSuggestions.push({
              variable: 'child-background-color',
              currentValue: childBg,
              suggestedValue: suggestedBg,
              scope: childSelector,
              reason: `Child element background-color ${childBg} has low contrast ratio ${ratio.toFixed(2)} with parent background ${parentBg}`,
              rawCss: `${childSelector} {\n  background-color: ${suggestedBg} !important;\n}`
            });
          }
        }
      }
    }

    // Walk the component chain
    for (const tagName of chain) {
      const host = element.closest<HTMLElement>(tagName);
      if (!host) continue;

      const hostStyle = getComputedStyle(host);
      const entry = this.resolveComponentMapEntryForRuntimeSelector(componentMap.entries || [], tagName, app);

      const varEntries: Array<{ name: string; value: string; kind: 'exposed' | 'consumed' | 'bridge' }> = [];
      const seen = new Set<string>();

      if (entry) {
        for (const v of entry.exposedCssVariables || []) {
          const val = hostStyle.getPropertyValue(v).trim();
          if (val && !seen.has(v)) {
            seen.add(v);
            varEntries.push({ name: v, value: val, kind: 'exposed' });
          }
        }
        for (const v of entry.consumedCssVariables || []) {
          if (seen.has(v)) continue;
          const val = hostStyle.getPropertyValue(v).trim();
          if (val) {
            seen.add(v);
            varEntries.push({ name: v, value: val, kind: 'consumed' });
          }
        }
        if (entry.primengBridge) {
          for (const [compVar, bridge] of Object.entries(
            entry.primengBridge as Record<string, { primengToken: string }>
          )) {
            if (seen.has(compVar)) continue;
            const val = hostStyle.getPropertyValue(compVar).trim();
            if (val) {
              seen.add(compVar);
              varEntries.push({ name: compVar, value: val, kind: 'bridge' });
            }
          }
        }
      }

      report.hosts.push({
        tagName,
        mapEntry: entry ? { symbolName: entry.symbolName, sourcePath: entry.sourcePath } : null,
        variables: varEntries
      });

      // Generate fix suggestions for contrast issues
      if (shouldSuggestColorFixes && entry?.primengBridge && report.contrastIssues.some(issue => issue.ratio < 4.5)) {
        for (const issue of report.contrastIssues) {
          if (issue.ratio >= 4.5) continue;
          const focusToken = issue.propA === 'background-color' ? 'background' : issue.propA.replace('-color', '');
          for (const [compVar, bridge] of Object.entries(
            entry.primengBridge as Record<string, { primengToken: string }>
          )) {
            if (!bridge.primengToken.includes(focusToken)) continue;
            const currentValue = hostStyle.getPropertyValue(compVar).trim();
            if (!currentValue) continue;

            const suggestedValue = this.suggestFixColor(currentValue, issue.propA, report.computedColors);
            const scope = tagName;
            const reason = `${bridge.primengToken} resolves to ${currentValue} on ${tagName}, contrast ratio ${issue.ratio.toFixed(2)} with ${issue.propB}=${report.computedColors[issue.propB]}`;
            report.fixSuggestions.push({
              variable: compVar,
              currentValue,
              suggestedValue,
              scope,
              primengToken: bridge.primengToken,
              reason
            });
          }
        }
      }
    }

    // Detect hover-state contrast issues from stylesheet rules (no hover required)
    const hoverBg = hoverStyles['background-color'];
    const hoverFg = hoverStyles['color'];
    const normalFg = report.computedColors['color'];
    if (shouldSuggestColorFixes && hoverBg && normalFg) {
      const ratio = this.computeContrastRatio(hoverBg, normalFg);
      if (ratio !== null && ratio < 4.5) {
        report.contrastIssues.push({
          propA: 'background-color (:hover)',
          propB: 'color',
          ratio,
          severity: ratio < 3 ? 'FAIL (AA normal text needs >= 4.5)' : 'WARN'
        });
        const suggestedValue = this.suggestFixColor(hoverBg, 'background-color', report.computedColors);
        report.fixSuggestions.push({
          variable: 'background-color',
          currentValue: hoverBg,
          suggestedValue,
          scope: `${report.target.selector}:hover`,
          reason: `:hover background ${hoverBg} has low contrast ratio ${ratio.toFixed(2)} with text ${normalFg}`
        });
      }
    }
    if (shouldSuggestColorFixes && hoverFg && report.computedColors['background-color']) {
      const ratio = this.computeContrastRatio(report.computedColors['background-color'], hoverFg);
      if (ratio !== null && ratio < 4.5) {
        report.contrastIssues.push({
          propA: 'background-color',
          propB: 'color (:hover)',
          ratio,
          severity: ratio < 3 ? 'FAIL' : 'WARN'
        });
        report.fixSuggestions.push({
          variable: 'color',
          currentValue: hoverFg,
          suggestedValue: this.suggestFixColor(hoverFg, 'color', report.computedColors),
          scope: `${report.target.selector}:hover`,
          reason: `:hover color ${hoverFg} has low contrast ratio ${ratio.toFixed(2)} with background`
        });
      }
    }

    // Print console report
    this.printCssDiagnosticReport(report);
    return report;
  }

  private resolveComponentMapEntryForRuntimeSelector(entries: any[], selector: string, app?: string) {
    const matches = (entries || []).filter(entry => entry?.selector === selector);
    if (matches.length === 0) {
      return null;
    }

    const appMatch = app ? matches.find(entry => String(entry.sourcePath || '').startsWith(`apps/${app}/`)) : null;
    if (appMatch) {
      return appMatch;
    }

    const reachableSharedMatch = app
      ? matches.find(
          entry =>
            String(entry.sourcePath || '').startsWith('libs/') &&
            Array.isArray(entry.reachableByApps) &&
            entry.reachableByApps.includes(app)
        )
      : null;
    if (reachableSharedMatch) {
      return reachableSharedMatch;
    }

    const sharedMatch = matches.find(entry => String(entry.sourcePath || '').startsWith('libs/'));
    if (sharedMatch) {
      return sharedMatch;
    }

    return matches[0];
  }

  /**
   * Injects a CSS override into the page for live testing.
   * Returns the injected <style> element id so you can remove it later.
   *
   * Usage:
   *   window.usheru.injectFix({ selector: 'alti-header ush-search-movie', property: '--search-movie-option-focus-background', value: '#f2f2f2' })
   *   // Remove with:
   *   document.getElementById('ush-bugfix-inject').remove()
   */
  injectCssFix(opts: { selector?: string; property?: string; value?: string; rawCss?: string }): string | null {
    const id = 'ush-bugfix-inject';
    let style = document.getElementById(id) as HTMLStyleElement | null;
    if (!style) {
      style = document.createElement('style');
      style.id = id;
      style.setAttribute('type', 'text/css');
      document.head.appendChild(style);
    }

    const rule = opts.rawCss || `${opts.selector} { ${opts.property}: ${opts.value} !important; }`;
    const sheet = style.sheet as CSSStyleSheet | null;
    if (sheet) {
      while (sheet.cssRules.length > 0) sheet.deleteRule(0);
      try {
        if (opts.rawCss) {
          style.textContent = rule;
        } else {
          sheet.insertRule(rule);
        }
      } catch (err) {
        console.warn('[injectCssFix] insertRule failed, falling back to textContent:', err);
        style.textContent = rule;
      }
    } else {
      style.textContent = rule;
    }

    window.usheru._lastAppliedFix = opts;

    console.log(`%c✓ Injected: ${rule}`, 'color:#059669;font-weight:600;');
    console.log('%c  Remove with: document.getElementById("ush-bugfix-inject").remove()', 'color:#a6adc8;');
    return id;
  }

  private computeContrastRatio(color1: string, color2: string): number | null {
    const l1 = this.relativeLuminance(color1);
    const l2 = this.relativeLuminance(color2);
    if (l1 === null || l2 === null) return null;
    const lighter = Math.max(l1, l2);
    const darker = Math.min(l1, l2);
    return (lighter + 0.05) / (darker + 0.05);
  }

  private relativeLuminance(cssColor: string): number | null {
    const rgb = this.parseCssColor(cssColor);
    if (!rgb) return null;
    const [r, g, b] = rgb.map(c => {
      const srgb = c / 255;
      return srgb <= 0.04045 ? srgb / 12.92 : Math.pow((srgb + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
  }

  private parseCssColor(cssColor: string): [number, number, number] | null {
    const s = cssColor.trim().toLowerCase();

    // Named colors
    const named: Record<string, string> = {
      black: '#000000',
      white: '#ffffff',
      red: '#ff0000',
      green: '#008000',
      blue: '#0000ff',
      gray: '#808080',
      grey: '#808080',
      silver: '#c0c0c0',
      transparent: 'rgba(0,0,0,0)'
    };
    if (named[s]) return this.parseCssColor(named[s]);

    // Hex
    const hex = s.match(/^#?([\da-f]{2})([\da-f]{2})([\da-f]{2})$/i);
    if (hex) return [parseInt(hex[1], 16), parseInt(hex[2], 16), parseInt(hex[3], 16)];

    const hexShort = s.match(/^#?([\da-f])([\da-f])([\da-f])$/i);
    if (hexShort)
      return [
        parseInt(hexShort[1] + hexShort[1], 16),
        parseInt(hexShort[2] + hexShort[2], 16),
        parseInt(hexShort[3] + hexShort[3], 16)
      ];

    // rgb/rgba
    const rgb = s.match(/^rgba?\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
    if (rgb) return [Number(rgb[1]), Number(rgb[2]), Number(rgb[3])] as [number, number, number];

    return null;
  }

  private suggestFixColor(currentValue: string, targetProp: string, computedColors: Record<string, string>): string {
    const bg = computedColors['background-color'] || '';
    const fg = computedColors['color'] || '';
    const bgLum = this.relativeLuminance(bg);
    const fgLum = this.relativeLuminance(fg);
    if (bgLum === null || fgLum === null) return '#f2f2f2';

    if (targetProp.includes('background')) {
      // Background is the problem — suggest one that contrasts with the text
      if (fgLum > 0.5) return '#333333';
      return '#f2f2f2';
    }
    return fgLum > 0.5 ? '#1a1a1a' : '#e5e5e5';
  }

  private findMatchingHoverStyles(element: HTMLElement): Record<string, string> {
    const styles: Record<string, string> = {};
    try {
      for (let s = 0; s < document.styleSheets.length; s++) {
        const sheet = document.styleSheets[s];
        try {
          const rules = Array.from(sheet.cssRules || sheet.rules || []);
          for (const rule of rules) {
            if (!(rule instanceof CSSStyleRule)) continue;
            const sel = rule.selectorText;
            if (!sel.includes(':hover')) continue;
            const baseSel = sel.replace(/\s*:hover\b/g, '').trim();
            if (!element.matches(baseSel)) continue;
            for (let i = 0; i < rule.style.length; i++) {
              const prop = rule.style[i];
              const val = rule.style.getPropertyValue(prop).trim();
              if (val && !styles[prop]) styles[prop] = val;
            }
          }
        } catch {
          // CORS-restricted external stylesheet — skip
        }
      }
    } catch {
      // stylesheet enumeration failed
    }
    return styles;
  }

  private printCssDiagnosticReport(report: any) {
    if (!report) return;
    const headerStyle = 'color:#7c3aed;font-weight:700;font-size:14px';
    const labelStyle = 'color:#2563eb;font-weight:600';
    const valueStyle = 'color:#111827';
    const warningStyle = 'color:#dc2626;font-weight:600';
    const okStyle = 'color:#059669;font-weight:600';
    const fixStyle = 'color:#d97706;font-weight:600';

    console.group('%c🧬 CSS Variable Diagnostics', headerStyle);
    console.log(`  Target: ${report.target.selector} (${report.target.tagName})`);
    console.log(`  Chain: ${report.target.componentChain.join(' → ')}`);

    console.group('%c Computed Colors', labelStyle);
    for (const [prop, val] of Object.entries(report.computedColors)) {
      console.log(
        `  ${prop}: %c${val}`,
        `color:${val};background:${prop.includes('background') ? '#f3f4f6' : 'transparent'};padding:1px 4px;`
      );
    }
    console.groupEnd();

    if (report.hoverStyles && Object.keys(report.hoverStyles).length > 0) {
      console.group('%c Hover Styles (from stylesheets)', labelStyle);
      for (const [prop, val] of Object.entries(report.hoverStyles)) {
        console.log(
          `  ${prop}: %c${val}`,
          `color:${val};background:${prop.includes('background') ? '#f3f4f6' : 'transparent'};padding:1px 4px;`
        );
      }
      console.groupEnd();
    }

    if (report.contrastIssues.length > 0) {
      console.group('%c⚠ Contrast Issues', warningStyle);
      for (const issue of report.contrastIssues) {
        const icon = issue.severity.startsWith('FAIL') ? '✗' : issue.severity.startsWith('WARN') ? '⚠' : '✓';
        console.log(`  ${icon} ${issue.propA} vs ${issue.propB}: ratio ${issue.ratio.toFixed(2)} — ${issue.severity}`);
      }
      console.groupEnd();
    }

    if (Object.keys(report.computedLayout).length > 0) {
      console.group('%c Layout Properties', labelStyle);
      const layoutPriority = [
        'text-align',
        'background-position',
        'display',
        'flex-direction',
        'align-items',
        'justify-content',
        'float',
        'padding-left',
        'padding-right',
        'position',
        'left',
        'right',
        'top',
        'bottom'
      ];
      const seen = new Set<string>();
      for (const prop of layoutPriority) {
        if (report.computedLayout[prop] && !seen.has(prop)) {
          seen.add(prop);
          console.log(`  ${prop}: ${report.computedLayout[prop]}`);
        }
      }
      for (const [prop, val] of Object.entries(report.computedLayout)) {
        if (!seen.has(prop)) {
          console.log(`  ${prop}: ${val}`);
        }
      }
      console.groupEnd();
    }

    if (report.layoutHints?.length > 0) {
      console.group('%c Layout Hints', labelStyle);
      for (const hint of report.layoutHints) {
        console.log(`  💡 ${hint}`);
      }
      console.groupEnd();
    }

    for (const host of report.hosts) {
      console.group(`%c Host: <${host.tagName}>`, labelStyle);
      if (host.mapEntry) {
        console.log(`  Map: ${host.mapEntry.symbolName} (${host.mapEntry.sourcePath})`);
      } else {
        console.log('  (not in component map)');
      }
      if (host.variables.length > 0) {
        for (const v of host.variables) {
          const kindIcon = v.kind === 'exposed' ? '📤' : v.kind === 'bridge' ? '🔗' : '📥';
          console.log(
            `  ${kindIcon} ${v.name}: %c${v.value}`,
            `color:#1e1e2e;background:#f3f4f6;padding:1px 4px;border-radius:2px;`
          );
        }
      } else {
        console.log('  (no CSS variables in component map)');
      }
      console.groupEnd();
    }

    if (report.fixSuggestions.length > 0) {
      console.group('%c🔧 Fix Suggestions', fixStyle);
      for (const fix of report.fixSuggestions) {
        console.log(`  Set %c${fix.variable}: ${fix.suggestedValue};`, 'color:#d97706;font-weight:600;');
        console.log(`  Scope: ${fix.scope}`);
        console.log(`  PrimeNG: ${fix.primengToken || '—'}`);
        console.log(`  Reason: ${fix.reason}`);
        console.log(`  %cTry:`, 'color:#a6adc8;');
        if (fix.rawCss) {
          console.log(`    window.usheru.injectFix({ rawCss: ${JSON.stringify(fix.rawCss)} })`);
        } else {
          console.log(
            `    window.usheru.injectFix({ selector: '${fix.scope}', property: '${fix.variable}', value: '${fix.suggestedValue}' })`
          );
        }
      }
      console.groupEnd();
    }

    console.groupEnd();
  }

  private formatDiagnosticsForLlm(report: any): string | undefined {
    if (!report) return undefined;
    const lines: string[] = [];
    lines.push(`--- Live CSS Diagnostics for <${report.target.tagName}> ---`);
    lines.push(`Chain: ${report.target.componentChain.join(' → ')}`);

    if (report.contrastIssues?.length > 0) {
      for (const issue of report.contrastIssues) {
        lines.push(
          `⚠ Contrast: ${issue.propA}=${report.computedColors[issue.propA]} vs ${issue.propB}=${report.computedColors[issue.propB]} ratio ${issue.ratio.toFixed(2)}`
        );
      }
    }

    if (report.computedColors && Object.keys(report.computedColors).length > 0) {
      lines.push(
        `Computed: ${Object.entries(report.computedColors)
          .map(([k, v]) => `${k}=${v}`)
          .join(', ')}`
      );
    }

    if (report.computedLayout && Object.keys(report.computedLayout).length > 0) {
      const layoutStr = Object.entries(report.computedLayout)
        .map(([k, v]) => `${k}=${v}`)
        .join(', ');
      lines.push(`Layout: ${layoutStr}`);
    }

    if (report.layoutHints?.length > 0) {
      lines.push(`Layout hints: ${report.layoutHints.join('; ')}`);
    }

    if (report.hoverStyles && Object.keys(report.hoverStyles).length > 0) {
      const hoverStr = Object.entries(report.hoverStyles)
        .map(([k, v]) => `${k}=${v}`)
        .join(', ');
      lines.push(`Hover (from stylesheets): ${hoverStr}`);
    }

    for (const host of report.hosts || []) {
      lines.push(`<${host.tagName}> vars:`);
      for (const v of host.variables || []) {
        const icon = v.kind === 'bridge' ? '🔗' : v.kind === 'consumed' ? '📥' : '📤';
        lines.push(`  ${icon} ${v.name}: ${v.value}`);
      }
      if (host.mapEntry) lines.push(`  (map: ${host.mapEntry.symbolName})`);
    }

    if (report.fixSuggestions?.length > 0) {
      lines.push(`Fix suggestions:`);
      for (const fix of report.fixSuggestions) {
        if (fix.rawCss) {
          lines.push(`  Apply ${fix.variable}: ${fix.suggestedValue} on <${fix.scope}> → ${fix.reason}`);
          lines.push(fix.rawCss);
        } else {
          lines.push(
            `  Set ${fix.variable}: ${fix.suggestedValue} on <${fix.scope}> ${fix.primengToken ? `(PrimeNG: ${fix.primengToken})` : `→ ${fix.reason}`}`
          );
        }
      }
    }

    return lines.join('\n');
  }

  private shouldSuggestColorDiagnosticFixes(bugContext?: string) {
    const text = String(bugContext || '').toLowerCase();
    if (!text) {
      return true;
    }

    const colorSignals =
      /\b(colou?r|background|contrast|readable|foreground|text color|icon color|border color)\b/.test(text);
    const structuralSignals =
      /\b(left|right|position|align|alignment|misaligned|spacing|padding|margin|gap|width|height|order|float)\b/.test(
        text
      );

    return colorSignals && !structuralSignals;
  }

  private findRecordedStructuralDiagnosticElement(bugContext?: string) {
    const text = String(bugContext || '').toLowerCase();
    if (!/\b(icon|left|right|position|align|alignment|spacing|padding|margin|width|height)\b/.test(text)) {
      return null;
    }

    const structuralSelectorPattern =
      /\.p-iconfield|\.p-input-icon|\.p-inputicon|\.p-inputtext|p-iconfield|p-multiselect|p-select|p-dropdown/i;
    const interactions = [...this.getRecordedDomInteractions()].reverse();
    for (const interaction of interactions) {
      if (!['mouseenter', 'click', 'input'].includes(interaction.kind)) {
        continue;
      }

      const selectors = [interaction.selector, ...(interaction.selectorCandidates || [])].filter(Boolean);
      for (const selector of selectors) {
        if (!structuralSelectorPattern.test(selector)) {
          continue;
        }
        try {
          const element = document.querySelector<HTMLElement>(selector);
          if (element && this.isElementVisible(element)) {
            return element;
          }
        } catch {
          // Ignore selector candidates that are not valid CSS selectors.
        }
      }
    }

    return null;
  }

  private registerWindowApi() {
    const usheru = this.getUsheruGlobal();
    usheru.buildAutomatedBugfixSkillInput = (request, targetElement) =>
      this.buildAutomatedBugfixSkillInput(request, targetElement);
    usheru.reviewAutomatedBugfixPlan = (request, targetElement) =>
      this.reviewAutomatedBugfixPlan(request, targetElement);
    usheru.listSelectors = limit => this.listSelectors(limit);
    usheru.describeElement = element => this.describeElement(element);
    usheru.analyzeBugfix = options => this.analyzeBugfix(options);
    usheru.help = command => this.printUsheruHelp(command);
    usheru.startRecording = (types?: string | string[]) => this.enableRecording(types);
    usheru.stopRecording = () => this.stopRecording();
    usheru.clearRecording = () => this.clearRecording();
    usheru.diagnoseElement = (...args: any[]) => (this.diagnoseElement as any)(...args);
    usheru.injectFix = opts => this.injectCssFix(opts);
    usheru.report = input =>
      this.report(
        input as string | Element | { bug?: string; expected?: string; element?: Element | null } | undefined
      );
    usheru.setBrokenElement = element => this.setBrokenElement(element);
    usheru.aiDebug = () => this.activateDebugMode(true);
    usheru.setLessonWeights = (weights: Partial<LessonScoringWeights>) => {
      this.lessonWeights = { ...this.lessonWeights, ...weights };
    };
    usheru.debugLessonScoring = () => {
      const lessons = this.aiBugfixLessonsCache || [];
      const ctx = this.parseUrl(window.location.href);
      const app = this.resolveAppIdentifier();
      const result = debugLessonScoring(lessons, {
        app: app.value,
        bugContext: '',
        expectedOutcome: '',
        path: ctx.path,
        weights: this.lessonWeights
      });
      console.table(
        result.map(r => ({
          '#': r.index,
          app: r.application,
          control: r.memoryKey,
          symptom: r.symptom,
          score: r.score,
          gated: r.gated ? r.gateReason : '',
          appPts: r.appScore,
          pathPts: r.pathScore,
          chainPts: r.chainScore,
          ctrlPts: r.controlScore,
          bonus: r.bonusScore,
          tokens: r.tokenOverlapScore,
          thresh: r.threshold
        }))
      );
      return result;
    };
    usheru.recordBugfixLesson = opts => this.recordBugfixLesson(opts as Parameters<typeof this.recordBugfixLesson>[0]);
  }

  private getUsheruHelpCommands(): AiUsheruHelpResult['commands'] {
    return [
      {
        command: 'report',
        description: 'Open the bug report chat — type freely, copy the ClickUp-ready comment. (Recommended)',
        detailedHelp:
          "window.usheru.report() or window.usheru.report($0) or window.usheru.report('describe the bug') or window.usheru.report({bug: '...', expected: '...', element: $0})"
      },
      {
        command: 'setBrokenElement',
        description: 'Set the open bug report target from the DevTools-selected element.',
        detailedHelp: 'window.usheru.setBrokenElement($0)'
      },
      {
        command: 'reviewAutomatedBugfixPlan',
        description: '[DEPRECATED] Use report() instead. This requires DevTools and $0.',
        detailedHelp: "window.usheru.help('review')"
      },
      {
        command: 'buildAutomatedBugfixSkillInput',
        description: 'Build paste-ready JSON for GitHub Actions reproduction_steps.',
        detailedHelp: "window.usheru.buildAutomatedBugfixSkillInput('help')"
      },
      {
        command: 'startRecording',
        description: 'Record clicks, navigation, hover, scroll, form changes, and inputs before building a payload.',
        detailedHelp: "window.usheru.startRecording('help')"
      },
      {
        command: 'describeElement',
        description: 'Inspect a selected DOM element for selector, component chain, surface, and layout.',
        detailedHelp: "window.usheru.help('describeElement')"
      },
      {
        command: 'listSelectors',
        description: 'List likely surfaces and interactive targets for the current page state.',
        detailedHelp: "window.usheru.help('listSelectors')"
      },
      {
        command: 'diagnoseElement',
        description: 'Run live CSS diagnostics for an element and suggest scoped fixes.',
        detailedHelp: "window.usheru.help('diagnoseElement')"
      },
      {
        command: 'injectFix',
        description: 'Temporarily inject a CSS variable or raw CSS override for browser-only testing.',
        detailedHelp: "window.usheru.help('injectFix')"
      },
      {
        command: 'analyzeBugfix',
        description: 'Programmatically run component matching and fix-routing analysis.',
        detailedHelp: "window.usheru.help('analyzeBugfix')"
      },
      {
        command: 'recordBugfixLesson',
        description: 'Record one or more validated fixes as lessons (outputs JSONL to console).',
        detailedHelp:
          "window.usheru.recordBugfixLesson({bug_context:'what is broken', expected_outcome:'what it should be', fixes:[{label:'...', selector:'...', rawCss:'...'}]})"
      }
    ];
  }

  private printUsheruHelp(
    command?: string
  ):
    | AiUsheruHelpResult
    | AiUsheruCommandHelpResult
    | AiAutomatedBugfixSkillHelpResult
    | AiBugfixReviewHelpResult
    | void {
    const normalizedCommand = String(command || '').trim();
    if (normalizedCommand) {
      return this.printUsheruCommandHelp(normalizedCommand);
    }

    const commands = this.getUsheruHelpCommands();
    const divider = '━'.repeat(70);
    console.log(`\n${divider}`);
    console.log('%cUSHERU CONSOLE COMMANDS', 'color:#2563eb;font-weight:700;font-size:14px');
    console.log(`${divider}\n`);
    console.log('These are the supported window.usheru entry points. Internal state fields are intentionally omitted.');
    console.log('');
    commands.forEach(({ command: commandName, description, detailedHelp }) => {
      console.log(
        `  %cwindow.usheru.${commandName}%c — ${description}`,
        'color:#111827;background:#f3f4f6;padding:2px 4px',
        'color:inherit'
      );
      console.log(`    Detailed help: %c${detailedHelp}`, 'color:#111827;background:#f3f4f6;padding:2px 4px');
    });
    console.log(
      `\nRun %cwindow.usheru.help('commandName')%c for tailored help when a command does not have its own help argument.`,
      'color:#111827;background:#f3f4f6;padding:2px 4px',
      'color:inherit'
    );
    console.log(`${divider}\n`);

    return {
      message: "Command index ready. Run window.usheru.help('commandName') or a command's own help form for details.",
      commands
    };
  }

  private printUsheruCommandHelp(
    command: string
  ): AiUsheruCommandHelpResult | AiAutomatedBugfixSkillHelpResult | AiBugfixReviewHelpResult | void {
    const normalizedCommand = command.trim().toLowerCase();
    if (['report', 'reportbug'].includes(normalizedCommand)) {
      return this.printReportHelp();
    }
    if (['setbrokenelement', 'brokenelement', 'target'].includes(normalizedCommand)) {
      return this.printSetBrokenElementHelp();
    }
    if (['review', 'reviewautomatedbugfixplan'].includes(normalizedCommand)) {
      return this.printReviewHelp();
    }
    if (['build', 'buildautomatedbugfixskillinput', 'payload'].includes(normalizedCommand)) {
      return this.printAutomatedBugfixSkillHelp();
    }
    if (['record', 'recording', 'recordsteps', 'startrecording'].includes(normalizedCommand)) {
      this.printStartRecordingHelp();
      return {
        message: "Recording help printed. Use window.usheru.startRecording('help') for the same output.",
        command: 'startRecording'
      };
    }

    const helpByCommand: Record<string, { command: string; lines: string[] }> = {
      describeelement: {
        command: 'describeElement',
        lines: [
          'Usage: window.usheru.describeElement($0)',
          'Use when: you need selector, component chain, surface, and layout details for the selected DevTools element.',
          'Tip: pass this as the second argument to buildAutomatedBugfixSkillInput or report() when the bug is tied to a specific element.'
        ]
      },
      listselectors: {
        command: 'listSelectors',
        lines: [
          'Usage: window.usheru.listSelectors()',
          'Optional limit: window.usheru.listSelectors(20)',
          'Use when: you need candidate selectors for the current page state, visible surface, or recent recorded interactions.'
        ]
      },
      diagnoseelement: {
        command: 'diagnoseElement',
        lines: [
          'Usage: await window.usheru.diagnoseElement($0)',
          "With app and bug context: await window.usheru.diagnoseElement($0, 'hff-example-ssr', 'button color has low contrast')",
          'Use when: you want live CSS variable, layout, hover, and contrast diagnostics before proposing a fix.'
        ]
      },
      injectfix: {
        command: 'injectFix',
        lines: [
          "Usage: window.usheru.injectFix({ selector: 'my-component .button', property: '--buttonColor', value: '#fff' })",
          'Raw CSS: window.usheru.injectFix({ rawCss: "my-component .button { color: #fff !important; }" })',
          'Remove: document.getElementById("ush-bugfix-inject")?.remove()',
          'Use when: you want to verify a CSS idea in the browser before asking the process to implement it.'
        ]
      },
      analyzebugfix: {
        command: 'analyzeBugfix',
        lines: [
          "Usage: await window.usheru.analyzeBugfix({ bugContext: 'what is wrong', expectedOutcome: 'how to verify', scope: 'optional-scope' })",
          'Use when: you need programmatic component matching and fix-routing output without building a full payload.',
          'For most QA submissions, prefer window.usheru.report() (the bug report form).'
        ]
      },
      recordbugfixlesson: {
        command: 'recordBugfixLesson',
        lines: [
          "Usage: window.usheru.recordBugfixLesson({ bug_context:'...', expected_outcome:'...', fixes:[{label:'...', selector:'...', rawCss:'...'}] })",
          'Supports multiple fixes in one call — pass an array of validated fixes.',
          'Outputs lesson records to the browser console. Save each record under ai-tooling/memory/lessons/<app>/.',
          'Optional: app, scope, path, targetElementHint auto-fill from current page context if omitted.'
        ]
      }
    };

    const help = helpByCommand[normalizedCommand];
    if (!help) {
      console.warn(`Unknown window.usheru command "${command}". Run window.usheru.help() for the command index.`);
      return {
        message: `Unknown command "${command}". Run window.usheru.help() for the command index.`,
        command
      };
    }

    const divider = '━'.repeat(70);
    console.log(`\n${divider}`);
    console.log(`%cwindow.usheru.${help.command}`, 'color:#2563eb;font-weight:700;font-size:14px');
    console.log(`${divider}\n`);
    help.lines.forEach(line => console.log(`  ${line}`));
    console.log(`\n${divider}\n`);

    return {
      message: `Help printed for window.usheru.${help.command}.`,
      command: help.command
    };
  }

  private enableRecording(types?: string | string[]) {
    if (types === 'help' || types === '?') {
      this.printStartRecordingHelp();
      return;
    }

    const typesArray = Array.isArray(types) ? types : types ? [types] : undefined;
    const configValue = typesArray ? typesArray.join(',') : 'true';
    try {
      sessionStorage.setItem(this.RECORD_STEPS_KEY, configValue);
    } catch {
      /* noop */
    }
    if (!this.initialized) {
      this.init();
      return;
    }
    const config = this.getRecordStepsConfig();
    this.registerDomInteractionListeners(config);
    if (config.has('*') || config.has('navigation')) {
      this.recordStep('entry', window.location.href);
    }
    const configLabel = this.getRecordStepsConfigLabel();
    const typeInfo = configLabel === 'all' ? 'all types' : `types: ${configLabel}`;
    console.log(
      `%c📹 Recording enabled (${typeInfo}). Interact with the page, then call buildAutomatedBugfixSkillInput() to capture steps.`,
      'color:#0f766e;font-weight:700'
    );
    console.log(
      `  Tip: use %cwindow.usheru.startRecording(['click', 'scroll'])%c to record specific types, or %cwindow.usheru.startRecording()%c for all types, or %cwindow.usheru.startRecording('help')%c for help`,
      'color:#111827;background:#f3f4f6;padding:2px 4px',
      'color:inherit',
      'color:#111827;background:#f3f4f6;padding:2px 4px',
      'color:inherit',
      'color:#111827;background:#f3f4f6;padding:2px 4px',
      'color:inherit'
    );
  }

  private stopRecording() {
    if (this.recordingAbortController) {
      this.recordingAbortController.abort();
      this.recordingAbortController = null;
    }
    this.listenersRegistered = false;
    try {
      sessionStorage.removeItem(this.RECORD_STEPS_KEY);
    } catch {
      /* noop */
    }
    if (this.scrollDebounceTimer !== null) {
      window.clearTimeout(this.scrollDebounceTimer);
      this.scrollDebounceTimer = null;
    }
    this.mouseenterDebounceMap.clear();
    console.log('%c📹 Recording stopped.', 'color:#b45309;font-weight:700');
  }

  private clearRecording() {
    const usheru = this.getUsheruGlobal();
    if (Array.isArray(usheru.recordedDomInteractions)) {
      usheru.recordedDomInteractions.length = 0;
    } else {
      usheru.recordedDomInteractions = [];
    }
    if (Array.isArray(usheru.recordedSteps)) {
      usheru.recordedSteps.length = 0;
    } else {
      usheru.recordedSteps = [];
    }
  }

  private printStartRecordingHelp() {
    const divider = '━'.repeat(70);
    console.log(`\n${divider}`);
    console.log('%c📹 RECORD INTERACTIONS HELP', 'color:#0f766e;font-weight:700;font-size:14px');
    console.log(`${divider}\n`);

    console.log('%cUSAGE:', 'color:#2563eb;font-weight:700');
    console.log(
      '  %cwindow.usheru.startRecording()%c                    — Record all event types',
      'color:#111827;background:#f3f4f6;padding:2px 4px',
      'color:inherit'
    );
    console.log(
      "  %cwindow.usheru.startRecording(['click'])%c          — Record only clicks",
      'color:#111827;background:#f3f4f6;padding:2px 4px',
      'color:inherit'
    );
    console.log(
      "  %cwindow.usheru.startRecording(['click', 'scroll'])%c — Record clicks and scrolls",
      'color:#111827;background:#f3f4f6;padding:2px 4px',
      'color:inherit'
    );
    console.log(
      "  %cwindow.usheru.startRecording('help')%c              — Show this help",
      'color:#111827;background:#f3f4f6;padding:2px 4px',
      'color:inherit'
    );

    console.log('\n%cAVAILABLE EVENT TYPES:', 'color:#2563eb;font-weight:700;margin-top:8px');
    console.log('  %cclick%c       — Mouse clicks', 'color:#0f766e;font-weight:700', 'color:inherit');
    console.log('  %cnavigation%c  — Page entry and route changes', 'color:#0f766e;font-weight:700', 'color:inherit');
    console.log('  %chover%c       — Mouse enter events', 'color:#0f766e;font-weight:700', 'color:inherit');
    console.log(
      '  %cscroll%c      — Scroll events (debounced 300ms)',
      'color:#0f766e;font-weight:700',
      'color:inherit'
    );
    console.log('  %cchange%c      — Change events on form elements', 'color:#0f766e;font-weight:700', 'color:inherit');
    console.log('  %csubmit%c      — Form submissions', 'color:#0f766e;font-weight:700', 'color:inherit');
    console.log('  %cinput%c       — Input events on text fields', 'color:#0f766e;font-weight:700', 'color:inherit');

    console.log('\n%cURL PARAM (alternative):', 'color:#2563eb;font-weight:700;margin-top:8px');
    console.log(
      '  Reload with %c?recordSteps=true%c for all types, or pick specific ones:',
      'color:inherit',
      'color:inherit'
    );
    console.log('  %c?recordSteps=click,navigation', 'color:#111827;background:#f3f4f6;padding:2px 4px');
    console.log('  %c?recordSteps=click,scroll,hover', 'color:#111827;background:#f3f4f6;padding:2px 4px');

    console.log(`\n${divider}\n`);
  }

  private registerDomInteractionListeners(config: Set<string>) {
    if (this.listenersRegistered) return;
    this.listenersRegistered = true;
    this.recordingAbortController = new AbortController();
    const signal = this.recordingAbortController.signal;

    const isAll = config.has('*');

    if (isAll || config.has('click')) {
      document.addEventListener(
        'click',
        event => {
          if (!event.isTrusted) {
            return;
          }

          this.recordDomInteractionWithTrackedSettle('click', event.target, true);
        },
        { capture: true, signal }
      );
    }

    if (isAll || config.has('change')) {
      document.addEventListener('change', event => this.recordDomInteraction('change', event), {
        capture: true,
        signal
      });
    }

    if (isAll || config.has('submit')) {
      document.addEventListener('submit', event => this.recordDomInteraction('submit', event), {
        capture: true,
        signal
      });
    }

    if (isAll || config.has('hover')) {
      document.addEventListener('mouseenter', event => this.debouncedRecordMouseEnter(event), {
        capture: true,
        signal
      });
    }

    if (isAll || config.has('input')) {
      document.addEventListener('input', event => this.recordDomInteraction('input', event), { capture: true, signal });
    }

    if (isAll || config.has('scroll')) {
      this.registerScrollListener();
    }
  }

  private registerScrollListener() {
    const signal = this.recordingAbortController?.signal;
    document.addEventListener(
      'scroll',
      event => {
        if (!event.isTrusted) return;
        this.debouncedRecordScroll(event);
      },
      { capture: true, passive: true, signal }
    );
  }

  private debouncedRecordScroll(event: Event) {
    if (!event.isTrusted) return;

    const target = event.target instanceof HTMLElement ? event.target : null;
    if (
      target?.closest('#usheru-bugfix-chat') ||
      target?.closest('ush-ai-bug-report') ||
      target?.closest('#ush-debug-btn')
    ) {
      return;
    }

    if (this.scrollDebounceTimer !== null) {
      window.clearTimeout(this.scrollDebounceTimer);
    }

    this.scrollDebounceTimer = window.setTimeout(() => {
      this.scrollDebounceTimer = null;
      this.recordScrollInteraction(event);
    }, 300);
  }

  private debouncedRecordMouseEnter(event: Event) {
    if (!event.isTrusted) return;

    const target = event.target instanceof HTMLElement ? event.target : null;
    if (!target) return;

    if (
      target.closest('#usheru-bugfix-chat') ||
      target.closest('ush-ai-bug-report') ||
      target.closest('#ush-debug-btn')
    ) {
      return;
    }

    const key = this.getElementSelector(target) || target.tagName;
    const now = Date.now();
    const lastRecorded = this.mouseenterDebounceMap.get(key);

    if (lastRecorded && now - lastRecorded < 800) return;

    this.mouseenterDebounceMap.set(key, now);

    this.recordDomInteraction('mouseenter', event);

    // Clean up stale entries periodically
    if (this.mouseenterDebounceMap.size > 200) {
      const threshold = now - 5000;
      for (const [k, t] of this.mouseenterDebounceMap) {
        if (t < threshold) this.mouseenterDebounceMap.delete(k);
      }
    }
  }

  private recordScrollInteraction(event: Event) {
    if (!this.initialized) return;

    const target = event.target instanceof Element ? event.target : document.documentElement;
    const element = target instanceof Document ? target.documentElement : target;
    const htmlElement = element instanceof HTMLElement ? element : null;

    const isWindow = element === document.documentElement || element === document.body;
    const selector = isWindow
      ? 'window'
      : htmlElement
        ? this.getElementSelectorCandidates(htmlElement)[0]
        : element.tagName.toLowerCase();
    const scrollX = isWindow ? window.scrollX : element.scrollLeft;
    const scrollY = isWindow ? window.scrollY : element.scrollTop;

    const location = this.parseUrl(window.location.href);
    const recordedDomInteractions = this.getRecordedDomInteractions();
    const previousInteraction = recordedDomInteractions[recordedDomInteractions.length - 1];

    // Dedup consecutive scrolls on same element within 1s
    if (
      previousInteraction?.kind === 'scroll' &&
      previousInteraction.selector === selector &&
      Date.now() - Date.parse(previousInteraction.at) < 1000
    ) {
      return;
    }

    const interaction: Omit<AiRecordedDomInteraction, 'index'> = {
      at: new Date().toISOString(),
      kind: 'scroll',
      title: `Scroll to (${scrollX}, ${scrollY}) in ${selector}`,
      url: location.url,
      path: location.path,
      selector,
      role: 'scrollbar',
      tagName: isWindow ? 'window' : element.tagName.toLowerCase(),
      scrollX,
      scrollY,
      settleMs: 300,
      mutationCount: 0
    };

    recordedDomInteractions.push({
      ...interaction,
      index: recordedDomInteractions.length + 1
    });
  }

  // `window.usheru` is created eagerly in the browser service constructor.
  private getRecordedSteps() {
    const usheru = this.getUsheruGlobal();
    usheru.recordedSteps = usheru.recordedSteps ?? [];
    return usheru.recordedSteps;
  }

  private getRecordedDomInteractions() {
    const usheru = this.getUsheruGlobal();
    usheru.recordedDomInteractions = usheru.recordedDomInteractions ?? [];
    return usheru.recordedDomInteractions;
  }

  private getUsheruGlobal() {
    return (window.usheru = window.usheru ?? {});
  }

  private initDebugMode() {
    const params = new URLSearchParams(window.location.search);
    if (params.get('aiDebug') !== 'true') return;
    this.activateDebugMode();
    void this.wireBridgeComponentLoader();
  }

  private async wireBridgeComponentLoader() {
    const { BridgeComponentLoader }: any = await import('@usheru-hff/core/bridge-component-loader');
    const bridge = this.injector.get(BridgeComponentLoader);
    bridge.ensureListening();
  }

  private activateDebugMode(printHelp = false) {
    this.ngZone.runOutsideAngular(() => {
      const usheru = this.getUsheruGlobal();
      if (!usheru._aiDebug) {
        usheru._aiDebug = true;
        // Prefetch AI files on localhost, or anywhere the user has explicitly
        // enabled dev mode via ?aiDebug=true. Production sites with the lesson
        // JSONL exposed at /docs/ai-bugfix-lessons/ or /assets/ai-tooling/ai-bugfix-lessons/
        // will populate the cache; sites that don't serve it will silently fall
        // back to an empty cache and the widget will just not show prior lessons.
        const host = window.location.hostname;
        const isLocalhost = host === 'localhost' || host === '127.0.0.1' || host === '::1' || host === '0.0.0.0';
        if (isLocalhost || usheru._aiDebug) {
          void this.prefetchAiBugfixLessons();
        }

        document.addEventListener(
          'click',
          event => {
            const target = event.target as Element;
            if (
              target.closest('#usheru-bugfix-chat') ||
              target.closest('ush-ai-bug-report') ||
              target.closest('#ush-debug-btn') ||
              target === usheru._debugButton
            )
              return;
            usheru._lastClickedElement = target;
          },
          true
        );

        this.createDebugButton();
      }

      if (printHelp) {
        this.printSetBrokenElementHelp();
      }
    });
  }

  private startSilentRecording() {
    const allTypes = ['click', 'hover', 'navigation', 'scroll', 'change', 'submit', 'input'] as const;
    const configValue = allTypes.join(',');
    try {
      sessionStorage.setItem(this.RECORD_STEPS_KEY, configValue);
    } catch {
      /* noop */
    }
    if (!this.initialized) {
      this.init();
      return;
    }
    const config = this.getRecordStepsConfig();
    this.registerDomInteractionListeners(config);
    if (config.has('*') || config.has('navigation')) {
      this.recordStep('entry', window.location.href);
    }
  }

  private isolateDebugEvent(event: Event) {
    event.stopPropagation();
  }

  private createDebugButton() {
    const usheru = this.getUsheruGlobal();
    if (usheru._debugButton) return;

    const btn = document.createElement('button');
    btn.id = 'ush-debug-btn';
    btn.type = 'button';
    btn.title = 'Report a bug';
    btn.innerHTML = '🐛';
    btn.style.cssText =
      'position:fixed;bottom:20px;right:20px;width:48px;height:48px;border-radius:8px;' +
      'background:#313244;color:#89b4fa;border:1px solid #45475a;font-size:20px;' +
      'cursor:pointer;z-index:999998;display:flex;align-items:center;justify-content:center;' +
      'box-shadow:0 2px 8px rgba(0,0,0,0.3);transition:transform 0.15s,right 0.15s;';
    ['pointerdown', 'mousedown', 'touchstart', 'click'].forEach(eventName => {
      btn.addEventListener(eventName, event => this.isolateDebugEvent(event));
    });
    btn.addEventListener('mouseenter', () => {
      btn.style.transform = 'scale(1.1)';
    });
    btn.addEventListener('mouseleave', () => {
      btn.style.transform = 'scale(1)';
    });
    btn.addEventListener('click', event => {
      event.preventDefault();
      this.isolateDebugEvent(event);
      this.toggleReport();
    });
    document.body.appendChild(btn);
    usheru._debugButton = btn;
  }

  updateDebugButtonPosition(chatOpen: boolean) {
    const usheru = this.getUsheruGlobal();
    if (usheru._debugButton) {
      (usheru._debugButton as HTMLElement).style.right = chatOpen ? '440px' : '20px';
    }
  }

  /** @internal exposed for the bug-report component. Returns true if a chat widget is currently visible. */
  isReportChatOpen(): boolean {
    const host = document.querySelector('ush-ai-bug-report');
    if (!(host instanceof HTMLElement)) return false;
    if (host.style.display === 'none') return false;
    return true;
  }

  /** @internal exposed for the bug-report component. Toggles the chat widget. */
  toggleReport(): void {
    const host = document.querySelector('ush-ai-bug-report');
    if (host) {
      const ngContext = (host as unknown as { __ngContext?: unknown[] }).__ngContext;
      const instance = Array.isArray(ngContext) && ngContext.length > 0
        ? ((ngContext[8] || ngContext[ngContext.length - 1]) as { instance?: { close?: () => void; reopen?: () => void } } | undefined)?.instance
        : undefined;
      const isHidden = host instanceof HTMLElement && host.style.display === 'none';

      if (isHidden) {
        if (instance?.reopen) {
          instance.reopen();
        } else {
          host.style.display = '';
          this.updateDebugButtonPosition(true);
        }
        return;
      }

      if (instance?.close) {
        instance.close();
        return;
      }
      this.closeReport();
      return;
    }
    this.report();
  }

  /** @internal closes any open report chat widget by hiding it. Used by toggleReport when the host is not discoverable via Angular context. */
  closeReport(): void {
    const host = document.querySelector('ush-ai-bug-report');
    if (host instanceof HTMLElement) {
      host.style.display = 'none';
      this.updateDebugButtonPosition(false);
    }
  }

  /** @internal exposed for use by AiBugReportComponent */
  buildClickUpPayload(bugContext: string, expectedOutcome: string, targetElement?: Element | null): object {
    const location = this.parseUrl(window.location.href);
    const appResolution = this.resolveAppIdentifier();
    const targetElementHint = targetElement ? this.describeElement(targetElement) : undefined;
    const visualExpectations = this.buildInferredVisualExpectations(
      { bug_context: bugContext, expected_outcome: expectedOutcome },
      targetElementHint
    );

    const cmCache = this._componentMapCache as unknown as Record<string, unknown> | null;
    const appBundle = cmCache?.appStyleBundles as Record<string, unknown> | undefined;
    const bundleMeta = appBundle?.[appResolution.value || ''] as Record<string, unknown> | undefined;
    const cmVersion = bundleMeta?.aiToolingVersion as string | undefined;
    const cmGeneratedAt = bundleMeta?.generatedAt as string | undefined;
    const payload: Record<string, unknown> = {
      app: appResolution.value,
      bug_context: bugContext,
      expected_outcome: expectedOutcome,
      ...(cmVersion ? { ai_tooling_version: cmVersion } : {}),
      ...(cmGeneratedAt ? { ai_tooling_generated_at: cmGeneratedAt } : {}),
      runtime_context: {
        current_url: location.url,
        current_path: location.path
      }
    };

    if (targetElementHint) {
      (payload.runtime_context as Record<string, unknown>).target_element = targetElementHint;
    }

    if (Object.keys(visualExpectations).length > 0) {
      payload.visual_expectations = visualExpectations;
    }

    const steps = this.getRecordedSteps();
    if (steps.length > 0) {
      (payload.runtime_context as Record<string, unknown>).recorded_steps = steps.map(step => ({
        ...step,
        queryParams: { ...step.queryParams }
      }));
    }

    const interactions = this.getRecordedDomInteractions();
    if (interactions.length > 0) {
      const stripped = interactions
        .filter(i => i.surface !== '#usheru-bugfix-chat' && !String(i.selector || '').startsWith('#usheru-bugfix-chat'))
        .map(i => ({
          at: i.at,
          kind: i.kind,
          selector: i.selector,
          selectorCandidates: i.selectorCandidates,
          role: i.role,
          label: i.label,
          text: i.text,
          tagName: i.tagName,
          inputType: i.inputType,
          settleMs: i.settleMs,
          mutationCount: i.mutationCount,
          value: i.value,
          checked: i.checked
        }));
      if (stripped.length > 0) {
        (payload.runtime_context as Record<string, unknown>).recorded_dom_interactions = stripped;
      }
    }

    return payload;
  }

  private setBrokenElement(element?: Element | null): AiTargetElementHint | null {
    if (!isPlatformBrowser(this.platformId)) return null;
    if (!(element instanceof Element)) {
      console.warn('Pass the DevTools-selected element explicitly: window.usheru.setBrokenElement($0)');
      return null;
    }

    const usheru = this.getUsheruGlobal();
    usheru._lastClickedElement = element;
    if (usheru._webLlmChat?.setTargetElement) {
      usheru._webLlmChat.setTargetElement(element);
    }

    return this.describeElement(element);
  }

  report(input?: string | Element | { bug?: string; expected?: string; element?: Element | null; branch?: string }) {
    if (!isPlatformBrowser(this.platformId)) return;

    let bugContext = '';
    let expectedOutcome = '';
    let element: Element | null | undefined;
    let branchName: string | undefined;

    if (typeof input === 'string') {
      bugContext = input.trim();
    } else if (input instanceof Element) {
      element = input;
    } else if (input && typeof input === 'object' && !Array.isArray(input)) {
      bugContext = String(input.bug || '').trim();
      expectedOutcome = String(input.expected || '').trim();
      element = input.element;
      const branchRaw = (input as { branch?: unknown }).branch;
      if (typeof branchRaw === 'string' && branchRaw.trim()) {
        branchName = branchRaw.trim();
      }
    }

    if (!branchName) {
      const urlBranch = new URLSearchParams(window.location.search).get('branch');
      if (urlBranch && urlBranch.trim()) {
        branchName = urlBranch.trim();
      }
    }

    if (element == null) {
      const usheru = this.getUsheruGlobal();
      const lastClicked = usheru._lastClickedElement;
      if (
        lastClicked instanceof Element &&
        document.contains(lastClicked) &&
        this.isElementVisible(lastClicked as HTMLElement)
      ) {
        element = lastClicked;
      }
    }

    const location = this.parseUrl(window.location.href);
    const appResolution = this.resolveAppIdentifier();
    const targetElementHint = element ? this.describeElement(element as Element) : undefined;
    const scope = this.inferAutomatedBugfixRouteFocus({
      app: appResolution.value,
      bug_context: bugContext,
      expected_outcome: expectedOutcome,
      runtime_context: {
        current_path: location.path,
        current_url: location.url,
        target_element: targetElementHint || undefined,
        recorded_steps: this.getRecordedSteps(),
        recorded_dom_interactions: this.getRecordedDomInteractions(),
        source: '',
        app_type: '',
        channel_name: null,
        hostname: '',
        origin: '',
        page_title: '',
        app_resolution: appResolution
      } as unknown as AiAutomatedBugfixSkillInput['runtime_context']
    } as unknown as AiAutomatedBugfixSkillInput);

    this.openReportChat({
      bugContext,
      expectedOutcome,
      element,
      app: appResolution.value,
      route: location.path,
      currentUrl: location.url,
      scope,
      branch: branchName
    });
  }

  private openReportChat(ctx: {
    bugContext: string;
    expectedOutcome: string;
    element?: Element | null;
    app: string;
    route: string;
    currentUrl?: string;
    scope: string;
    branch?: string;
  }) {
    this.updateDebugButtonPosition(true);

    const targetElementHint = ctx.element ? this.describeElement(ctx.element) : null;

    const defaultFixRouting = {
      fixType: 'code-fix' as string,
      routeAction: 'minimal-code-fix' as string,
      visualIssueType: '' as string,
      existingStyleVariableHints: [] as string[],
      layoutConstraintHints: [] as string[]
    };

    const relevantLessons = ctx.bugContext || ctx.expectedOutcome
      ? this.findRelevantAiBugfixLessons({
          app: this.resolveAppIdentifier().value,
          bugContext: ctx.bugContext || '',
          expectedOutcome: ctx.expectedOutcome || '',
          path: typeof window !== 'undefined' ? window.location.pathname : '',
          targetElementHint
        })
      : [];

    this.actions$.next({
      type: 'open-bug-report',
      payload: {
        ctx,
        targetElementHint,
        proposedFixes: [],
        relevantEntries: relevantLessons,
        fixRouting: defaultFixRouting
      }
    });
  }

  // createReportChatWidget removed — replaced by BridgeComponentLoader + core/ai-bug-report component

  private listSelectors(limit = 12): AiSelectorListing {
    const location = this.parseUrl(window.location.href);
    const appResolution = this.resolveAppIdentifier();
    const safeLimit = Math.min(Math.max(Number(limit) || 12, 1), 30);
    const recentInteractions = this.getRecordedDomInteractions()
      .slice(-5)
      .map(interaction => ({
        title: interaction.title,
        selector: interaction.selector,
        role: interaction.role,
        label: interaction.label,
        text: interaction.text
      }));
    const focusElement = this.resolveFocusElement(recentInteractions);
    const primarySurfaceElement = this.resolvePrimarySurfaceElement(focusElement);
    const componentChain = this.buildComponentChain(primarySurfaceElement || focusElement);
    const surfaces = this.collectPrimarySurfaceHints(primarySurfaceElement, componentChain, safeLimit);
    const primarySurface = surfaces[0];
    const interactiveTargets = this.collectRelevantInteractiveTargets(primarySurfaceElement || focusElement, safeLimit);

    return {
      app: appResolution.value,
      current_url: location.url,
      current_path: location.path,
      focus_selector: focusElement ? this.getElementSelector(focusElement) : undefined,
      primary_surface: primarySurface,
      component_chain: componentChain,
      surfaces,
      interactive_targets: interactiveTargets,
      recent_interactions: recentInteractions,
      guidance: [
        'Start from primary_surface and component_chain, not from app-root or generic wrappers.',
        'Mention the user action that exposes the bug, then the visible surface, then the symptom.',
        'Use interactive_targets only to name the control if the human surface name is ambiguous.'
      ],
      example_bug_context: this.buildBugContextExample(primarySurface, recentInteractions)
    };
  }

  /** @internal exposed for use by AiBugReportComponent */
  describeElement(element?: Element | null): AiTargetElementHint | null {
    if (element === undefined || element === null) {
      return null;
    }

    if (!(element instanceof HTMLElement)) {
      console.warn('window.usheru.describeElement expects an HTMLElement. Select an element in DevTools and pass $0.');
      return null;
    }

    return {
      ...this.buildSelectorHint(element),
      selectorCandidates: this.getElementSelectorCandidates(element),
      componentChain: this.buildComponentChain(element),
      relatedOwners: this.describeRelatedOwners(element),
      structuralOwner: this.describeStructuralOwner(element),
      layout: this.getElementLayoutHint(element)
    };
  }

  private describeRelatedOwners(element: HTMLElement): NonNullable<AiTargetElementHint['relatedOwners']> {
    const owners: NonNullable<AiTargetElementHint['relatedOwners']> = [];
    const seen = new Set<string>();
    const addOwner = (kind: string, owner: HTMLElement | null, reason: string) => {
      if (!owner) return;
      const selector = this.getElementSelector(owner);
      const key = `${kind}:${selector}`;
      if (!selector || seen.has(key)) return;
      seen.add(key);
      owners.push({
        kind,
        selector,
        tagName: owner.tagName.toLowerCase(),
        classes: Array.from(owner.classList).slice(0, 12),
        reason
      });
    };

    addOwner('selected', element, 'The actual DevTools $0 element selected by QA.');
    addOwner('component-host', this.getClosestCustomElement(element), 'Closest custom element host around $0.');
    addOwner(
      'interactive-owner',
      element.closest<HTMLElement>(
        'button, a[href], input, select, textarea, [role="button"], [role="combobox"], [role="option"], [role="tab"], p-select, p-dropdown, p-multiselect, p-autocomplete'
      ),
      'Closest interactive control owner around $0.'
    );
    addOwner(
      'overlay-owner',
      element.closest<HTMLElement>(
        '[role="dialog"], [role="listbox"], p-overlay, .p-overlay, [class*="overlay"], [class*="panel"], [class*="popup"], [class*="dropdown"]'
      ),
      'Closest overlay or detached panel owner around $0.'
    );
    addOwner(
      'structural-owner',
      element.closest<HTMLElement>(
        '[class*="iconfield"], [class*="input-icon"], [class*="field"], [class*="filter"], [class*="label"], [class*="trigger"], [class*="wrapper"]'
      ),
      'Closest rendered structural wrapper that may own layout, spacing, position, or visible label behavior.'
    );

    return owners;
  }

  private describeStructuralOwner(element: HTMLElement): AiTargetElementHint['structuralOwner'] | undefined {
    const iconContainer = element.closest<HTMLElement>(
      'p-iconfield, .p-iconfield, .p-iconfield-left, .p-iconfield-right, .p-input-icon-left, .p-input-icon-right'
    );
    if (!iconContainer) {
      return undefined;
    }

    const classes = Array.from(iconContainer.classList);
    const icon = iconContainer.querySelector<HTMLElement>('.p-inputicon, .p-input-icon, .p-icon, i, svg');
    const input = iconContainer.querySelector<HTMLElement>('.p-inputtext, input, textarea');

    return {
      selector: this.getPrimeNgIconContainerSelector(iconContainer),
      tagName: iconContainer.tagName.toLowerCase(),
      classes,
      overlayScope: this.getPrimeNgOverlayScopeSelector(iconContainer) || undefined,
      iconSelector: icon ? this.getElementSelector(icon) : undefined,
      inputSelector: input ? this.getElementSelector(input) : undefined
    };
  }

  private resolveAppIdentifier(requestedApp?: string): AiAppResolution {
    const explicitApp = this.normalizeExplicitAppName(requestedApp);
    if (explicitApp) {
      return {
        value: explicitApp,
        source: 'request',
        confidence: 'high',
        candidates: [explicitApp]
      };
    }

    const gitVersionApp = this.getGitVersionRuntimeAppName();
    if (gitVersionApp) {
      return {
        value: gitVersionApp,
        source: 'git_version',
        confidence: 'high',
        candidates: [gitVersionApp]
      };
    }

    const candidates = this.getRuntimeAppCandidates();
    const primaryCandidate = candidates[0] ?? {
      value: this.getFallbackAppIdentifier(),
      source: 'fallback',
      confidence: 'low' as const
    };

    return {
      value: primaryCandidate.value,
      source: primaryCandidate.source,
      confidence: primaryCandidate.confidence,
      candidates: [
        ...new Set(
          candidates
            .map(candidate => candidate.value)
            .filter(Boolean)
            .concat(primaryCandidate.value)
        )
      ]
    };
  }

  private getRuntimeAppCandidates(): AiResolvedAppCandidate[] {
    const candidates: AiResolvedAppCandidate[] = [];
    const pushCandidates = (...items: AiResolvedAppCandidate[]) => {
      items.forEach(item => {
        if (!item.value || candidates.some(candidate => candidate.value === item.value)) {
          return;
        }
        candidates.push(item);
      });
    };

    const viteApp = this.getViteRuntimeAppName();
    if (viteApp) {
      pushCandidates({ value: viteApp, source: 'vite_asset_path', confidence: 'high' });
    }

    pushCandidates(...this.buildAppCandidatesFromLabel(this.channelService.channel?.name, 'channel_name', 'medium'));
    pushCandidates(...this.buildAppCandidatesFromHostname());
    pushCandidates(...this.buildAppCandidatesFromEnvConfig());
    pushCandidates(...this.buildAppCandidatesFromMetaTag());

    return candidates;
  }

  private buildAppCandidatesFromEnvConfig(): AiResolvedAppCandidate[] {
    const appName = (this.env as any)?.appName || (this.env as any)?.appSlug;
    if (!appName) {
      return [];
    }
    const slug = this.slugify(appName);
    return this.createPrefixedAppCandidates(slug, 'hostname', 'low');
  }

  private buildAppCandidatesFromMetaTag(): AiResolvedAppCandidate[] {
    const appName =
      document.querySelector<HTMLMetaElement>('meta[name="application-name"]')?.content ||
      document.querySelector<HTMLMetaElement>('meta[name="app-name"]')?.content ||
      document.querySelector<HTMLMetaElement>('meta[property="og:site_name"]')?.content;

    if (!appName) {
      return [];
    }

    const slug = this.slugify(appName);
    return this.createPrefixedAppCandidates(slug, 'hostname', 'low');
  }

  private getGitVersionRuntimeAppName() {
    return this.appGitVersion?.projectName?.trim() || '';
  }

  private getGitVersionMetadata() {
    return this.appGitVersion ? { ...this.appGitVersion } : undefined;
  }

  private getAngularVersion() {
    return document.querySelector<HTMLElement>('[ng-version]')?.getAttribute('ng-version')?.trim() || undefined;
  }

  private getHtmlAppVersion() {
    return document.querySelector<HTMLElement>('[ush-version]')?.getAttribute('ush-version')?.trim() || undefined;
  }

  private buildLikelyProductionUrl(route: string) {
    try {
      const parsedUrl = new URL(route, window.location.origin);
      parsedUrl.hostname = parsedUrl.hostname
        .replace(/\.develop(?=\.)/i, '')
        .replace(/\.demo(?=\.)/i, '')
        .replace(/-develop(?=\.)/i, '')
        .replace(/-demo(?=\.)/i, '');
      return parsedUrl.toString();
    } catch {
      return 'https://example.com/current-route';
    }
  }

  private buildRecordStepsUrl() {
    const parsedUrl = new URL(window.location.href);
    parsedUrl.searchParams.set('recordSteps', 'true');
    return parsedUrl.toString();
  }

  private getViteRuntimeAppName() {
    const assetUrls = [
      ...Array.from(document.querySelectorAll<HTMLScriptElement>('script[src]')).map(script => script.src),
      ...Array.from(document.querySelectorAll<HTMLLinkElement>('link[href]')).map(link => link.href)
    ];

    const appName = assetUrls.map(url => url.match(/\.angular\/vite-root\/([^/]+)\//)?.[1]).find(Boolean);

    return appName ? appName.trim() : '';
  }

  private buildAppCandidatesFromHostname(): AiResolvedAppCandidate[] {
    const hostname = window.location.hostname
      .toLowerCase()
      .replace(/^www\./, '')
      .replace(/^(localhost|127\.0\.0\.1)$/i, '');

    if (!hostname) {
      return [];
    }

    const hostnameSlug = this.slugify(hostname.split('.')[0]);
    return this.createPrefixedAppCandidates(hostnameSlug, 'hostname', 'low');
  }

  private buildAppCandidatesFromLabel(
    label: string | null | undefined,
    source: Extract<AiAppResolution['source'], 'channel_name'>,
    confidence: AiAppResolution['confidence']
  ): AiResolvedAppCandidate[] {
    const slug = this.slugify(label);
    return this.createPrefixedAppCandidates(slug, source, confidence);
  }

  private createPrefixedAppCandidates(
    slug: string,
    source: Exclude<AiAppResolution['source'], 'request' | 'vite_asset_path' | 'fallback'>,
    confidence: AiAppResolution['confidence']
  ): AiResolvedAppCandidate[] {
    if (!slug) {
      return [];
    }

    if (/^(hff|wc)-/.test(slug)) {
      return [{ value: slug, source, confidence }];
    }

    const prefix = this.env.appType === 'widget' ? 'wc' : 'hff';
    const baseName = `${prefix}-${slug}`;

    if (prefix === 'hff') {
      return this.isLikelySsrApp()
        ? [
            { value: `${baseName}-ssr`, source, confidence },
            { value: baseName, source, confidence }
          ]
        : [
            { value: baseName, source, confidence },
            { value: `${baseName}-ssr`, source, confidence }
          ];
    }

    return [{ value: baseName, source, confidence }];
  }

  private normalizeExplicitAppName(value: string | undefined) {
    return typeof value === 'string' ? value.trim() : '';
  }

  private getFallbackAppIdentifier() {
    return this.env.appType === 'widget' ? 'wc-current-runtime-app' : 'hff-current-runtime-app';
  }

  private isLikelySsrApp() {
    return !!document.querySelector('[ng-server-context]');
  }

  private slugify(value: string | null | undefined) {
    return (value ?? '')
      .toLowerCase()
      .trim()
      .replace(/&/g, ' and ')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  private getInteractionElement(target: EventTarget | null, kind: AiRecordedDomInteraction['kind']) {
    const eventTarget = target instanceof Element ? target : target instanceof Node ? target.parentElement : null;
    if (!eventTarget) {
      return null;
    }

    if (kind === 'submit') {
      return eventTarget.closest<HTMLFormElement>('form');
    }

    const actionableElement = eventTarget.closest<HTMLElement>(
      [
        'button',
        'a[href]',
        'input',
        'select',
        'textarea',
        'summary',
        '[role="button"]',
        '[role="link"]',
        '[role="tab"]',
        '[role="menuitem"]',
        '[role="checkbox"]',
        '[role="radio"]',
        '[role="switch"]',
        '[role="option"]',
        'li.p-multiselect-item',
        '.p-multiselect-item',
        'li.p-dropdown-item',
        '.p-dropdown-item',
        'p-multiselect',
        'p-select',
        'p-dropdown',
        'p-autocomplete'
      ].join(', ')
    );
    if (actionableElement) {
      return actionableElement;
    }

    const element = eventTarget.closest<HTMLElement>('[data-testid], [aria-label], label, [role]');
    if (element instanceof HTMLLabelElement) {
      return element.control || element.querySelector<HTMLElement>('input, select, textarea, button, [role]');
    }

    return element ?? (eventTarget as HTMLElement);
  }

  private shouldSkipClickInteraction(element: HTMLElement) {
    if (
      !(
        element instanceof HTMLInputElement ||
        element instanceof HTMLSelectElement ||
        element instanceof HTMLTextAreaElement
      )
    ) {
      return false;
    }

    return !this.isMeaningfulInputClickInteraction(element);
  }

  private isMeaningfulInputClickInteraction(element: HTMLElement) {
    return !!element.closest(
      [
        'p-multiselect',
        'p-select',
        'p-dropdown',
        'p-autocomplete',
        '.p-multiselect',
        '.p-select',
        '.p-dropdown',
        '.p-autocomplete',
        '.p-iconfield',
        '.p-input-icon-left',
        '.p-input-icon-right',
        '.p-iconfield-left',
        '.p-iconfield-right',
        '[role="combobox"]'
      ].join(', ')
    );
  }

  private shouldSkipDuplicateDomInteraction(
    previousInteraction: AiRecordedDomInteraction,
    interaction: Omit<AiRecordedDomInteraction, 'index'>
  ) {
    return (
      previousInteraction.kind === interaction.kind &&
      previousInteraction.selector === interaction.selector &&
      previousInteraction.path === interaction.path &&
      previousInteraction.value === interaction.value &&
      previousInteraction.checked === interaction.checked &&
      Date.now() - Date.parse(previousInteraction.at) < 750
    );
  }

  private buildDomInteraction(
    kind: AiRecordedDomInteraction['kind'],
    element: HTMLElement,
    settleMs: number,
    mutationCount: number
  ): Omit<AiRecordedDomInteraction, 'index'> {
    const location = this.parseUrl(window.location.href);
    const role = this.getElementRole(element);
    const tagName = element.tagName.toLowerCase();
    const inputType = element instanceof HTMLInputElement ? element.type.toLowerCase() : undefined;
    const label = this.getElementLabel(element);
    const text = this.getElementText(element);
    const name = this.getElementName(element);
    const value = this.getElementValue(element);
    const checked = this.getElementChecked(element);
    const selectorCandidates = this.getElementSelectorCandidates(element);
    const selector = selectorCandidates[0] || tagName;
    const surface = this.describeSurface(element);
    const componentChain = this.buildComponentChain(element);

    return {
      at: new Date().toISOString(),
      kind,
      settleMs,
      mutationCount,
      title: this.buildDomInteractionTitle(kind, role, label, text, selector, value, checked, inputType),
      url: location.url,
      path: location.path,
      selector,
      selectorCandidates: selectorCandidates.length > 0 ? selectorCandidates : undefined,
      role,
      tagName,
      inputType,
      label: label || undefined,
      text: text || undefined,
      name: name || undefined,
      value: value || undefined,
      checked,
      surface,
      componentChain: componentChain.length > 0 ? componentChain : undefined
    };
  }

  private buildDomInteractionTitle(
    kind: AiRecordedDomInteraction['kind'],
    role: string,
    label: string,
    text: string,
    selector: string,
    value: string,
    checked: boolean | undefined,
    inputType: string | undefined
  ) {
    const descriptor = this.buildInteractionDescriptor(role, label, text, selector);

    if (kind === 'submit') {
      return `Submit ${descriptor}`;
    }

    if (kind === 'change') {
      if (role === 'radio' || inputType === 'radio') {
        return `Select ${descriptor}`;
      }

      if (role === 'checkbox' || inputType === 'checkbox') {
        return `${checked ? 'Check' : 'Uncheck'} ${descriptor}`;
      }

      if (value) {
        return `Change ${descriptor} to "${value}"`;
      }

      return `Change ${descriptor}`;
    }

    if (kind === 'input') {
      if (value) {
        return `Type "${value}" in ${descriptor}`;
      }

      return `Type in ${descriptor}`;
    }

    return `Click ${descriptor}`;
  }

  private buildInteractionDescriptor(role: string, label: string, text: string, selector: string) {
    const visibleName = this.getPreferredInteractionName(label, text);

    if (visibleName) {
      return `${role} "${visibleName}"`;
    }

    return `${role} ${selector}`.trim();
  }

  private getPreferredInteractionName(label: string, text: string) {
    if (text && label && this.slugify(text) === this.slugify(label)) {
      return text;
    }

    return label || text;
  }

  private getElementRole(element: HTMLElement) {
    const explicitRole = element.getAttribute('role')?.trim();
    if (explicitRole) {
      return explicitRole;
    }

    if (element instanceof HTMLButtonElement) {
      return 'button';
    }

    if (element instanceof HTMLAnchorElement && element.href) {
      return 'link';
    }

    if (element instanceof HTMLSelectElement) {
      return 'combobox';
    }

    if (element instanceof HTMLTextAreaElement) {
      return 'textbox';
    }

    if (element instanceof HTMLInputElement) {
      if (element.type === 'radio') {
        return 'radio';
      }
      if (element.type === 'checkbox') {
        return 'checkbox';
      }
      if (['button', 'submit', 'reset'].includes(element.type)) {
        return 'button';
      }
      return 'textbox';
    }

    if (element instanceof HTMLFormElement) {
      return 'form';
    }

    return element.tagName.toLowerCase();
  }

  private collectSelectorHints(elements: HTMLElement[], limit: number): AiSelectorHint[] {
    const seenSelectors = new Set<string>();
    const hints: AiSelectorHint[] = [];

    for (const element of elements) {
      if (!this.isElementVisible(element)) {
        continue;
      }

      const hint = this.buildSelectorHint(element);
      if (!hint.selector || seenSelectors.has(hint.selector)) {
        continue;
      }

      seenSelectors.add(hint.selector);
      hints.push(hint);

      if (hints.length >= limit) {
        break;
      }
    }

    return hints;
  }

  private collectPrimarySurfaceHints(
    primarySurfaceElement: HTMLElement | null,
    componentChain: string[],
    limit: number
  ): AiSelectorHint[] {
    const elements: HTMLElement[] = [];

    if (primarySurfaceElement) {
      elements.push(primarySurfaceElement);
    }

    componentChain.forEach(tagName => {
      const element =
        primarySurfaceElement?.closest<HTMLElement>(tagName) || document.querySelector<HTMLElement>(tagName);
      if (element && !elements.includes(element)) {
        elements.push(element);
      }
    });

    return this.collectSelectorHints(elements, limit);
  }

  private collectRelevantInteractiveTargets(root: HTMLElement | null, limit: number): AiSelectorHint[] {
    const scopeRoot = root || document.body;
    return this.collectSelectorHints(
      Array.from(
        scopeRoot.querySelectorAll<HTMLElement>(
          'button, a[href], input:not([type="hidden"]), select, textarea, p-select, p-dropdown, p-multiselect, .p-select-label, .p-dropdown-label, .p-multiselect-label, [role="button"], [role="link"], [role="tab"], [role="menuitem"], [role="combobox"], [role="checkbox"], [role="radio"], [role="switch"], [role="option"]'
        )
      ),
      limit
    );
  }

  private buildSelectorHint(element: HTMLElement): AiSelectorHint {
    return {
      selector: this.getElementSelector(element),
      role: this.getElementRole(element),
      tagName: element.tagName.toLowerCase(),
      label: this.getElementLabel(element) || undefined,
      text: this.getElementText(element) || undefined,
      surface: this.describeSurface(element)
    };
  }

  private isInterestingSurfaceElement(element: HTMLElement) {
    const tagName = element.tagName.toLowerCase();
    const role = element.getAttribute('role')?.trim().toLowerCase();

    return tagName.includes('-') || role === 'dialog' || role === 'alertdialog' || tagName === 'dialog';
  }

  private resolveFocusElement(
    recentInteractions: Array<Pick<AiRecordedDomInteraction, 'title' | 'selector' | 'role' | 'label' | 'text'>>
  ) {
    const latestInteraction = recentInteractions[recentInteractions.length - 1];
    const interactionElement = latestInteraction?.selector
      ? document.querySelector<HTMLElement>(latestInteraction.selector)
      : null;

    if (interactionElement && this.isElementVisible(interactionElement)) {
      return interactionElement;
    }

    const visibleDialog = this.getVisibleDialogs()[0];
    if (visibleDialog) {
      return visibleDialog;
    }

    const activeElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    if (activeElement && this.isElementVisible(activeElement)) {
      return activeElement;
    }

    return document.querySelector<HTMLElement>('body');
  }

  private resolvePrimarySurfaceElement(element: HTMLElement | null) {
    const visibleDialogs = this.getVisibleDialogs();
    if (visibleDialogs.length > 0) {
      const relatedDialog = element?.closest<HTMLElement>('dialog, [role="dialog"], [role="alertdialog"]');
      return relatedDialog || visibleDialogs[0];
    }

    const customElement = element ? this.getClosestCustomElement(element) : null;
    if (customElement) {
      return customElement;
    }

    const visibleCustomElement = Array.from(document.querySelectorAll<HTMLElement>('body *')).find(
      candidate => this.isInterestingSurfaceElement(candidate) && this.isElementVisible(candidate)
    );

    return visibleCustomElement || null;
  }

  private getVisibleDialogs() {
    return Array.from(document.querySelectorAll<HTMLElement>('dialog, [role="dialog"], [role="alertdialog"]')).filter(
      element => this.isElementVisible(element)
    );
  }

  private isElementVisible(element: HTMLElement) {
    const style = window.getComputedStyle(element);
    const rect = element.getBoundingClientRect();

    return !(
      style.display === 'none' ||
      style.visibility === 'hidden' ||
      style.opacity === '0' ||
      rect.width <= 0 ||
      rect.height <= 0
    );
  }

  private describeSurface(element: HTMLElement) {
    const dialog = element.closest<HTMLElement>('dialog, [role="dialog"], [role="alertdialog"]');
    if (dialog) {
      const dialogLabel =
        this.getElementLabel(dialog) ||
        this.normalizeText(
          dialog.querySelector<HTMLElement>('h1, h2, h3, [data-testid], [aria-label]')?.textContent || ''
        );
      return dialogLabel ? `dialog "${dialogLabel}"` : this.getElementSelector(dialog);
    }

    const customElement = this.getClosestCustomElement(element);
    if (customElement) {
      return customElement.tagName.toLowerCase();
    }

    const idContainer = element.closest<HTMLElement>('[id]');
    return idContainer?.id ? `#${idContainer.id}` : 'current page';
  }

  private getClosestCustomElement(element: HTMLElement) {
    let current: HTMLElement | null = element;

    while (current) {
      if (current.tagName.toLowerCase().includes('-')) {
        return current;
      }
      current = current.parentElement;
    }

    return null;
  }

  private buildBugContextExample(
    primarySurface: AiSelectorHint | undefined,
    recentInteractions: Array<Pick<AiRecordedDomInteraction, 'title' | 'selector' | 'role' | 'label' | 'text'>>
  ) {
    const surfaceName =
      primarySurface?.surface || primarySurface?.label || primarySurface?.selector || 'the current page';
    const latestInteraction = recentInteractions[recentInteractions.length - 1]?.title;

    if (latestInteraction) {
      return `After ${latestInteraction.toLowerCase()}, inside ${surfaceName}, describe the visible symptom in user terms.`;
    }

    return `Inside ${surfaceName}, describe the visible symptom in user terms and mention the action needed to expose it.`;
  }

  private buildComponentChain(element: HTMLElement | null) {
    const chain: string[] = [];
    let current = element;

    while (current) {
      const tagName = current.tagName.toLowerCase();
      if (tagName.includes('-') && !chain.includes(tagName)) {
        chain.push(tagName);
      }
      current = current.parentElement;
    }

    return chain.slice(0, 8);
  }

  private getElementLabel(element: HTMLElement) {
    const ariaLabel = this.normalizeText(element.getAttribute('aria-label'));
    if (ariaLabel) {
      return ariaLabel;
    }

    const ariaLabelledBy = this.normalizeText(
      (element.getAttribute('aria-labelledby') ?? '')
        .split(/\s+/)
        .map(id => document.getElementById(id)?.textContent ?? '')
        .join(' ')
    );
    if (ariaLabelledBy) {
      return ariaLabelledBy;
    }

    if (
      element instanceof HTMLInputElement ||
      element instanceof HTMLSelectElement ||
      element instanceof HTMLTextAreaElement
    ) {
      const labels = Array.from(element.labels ?? [])
        .map(label => this.normalizeText(label.textContent))
        .find(Boolean);
      if (labels) {
        return labels;
      }
    }

    const title = this.normalizeText(element.getAttribute('title'));
    if (title) {
      return title;
    }

    return this.getElementText(element);
  }

  private getElementText(element: HTMLElement) {
    return this.normalizeText(element.textContent);
  }

  private getElementName(element: HTMLElement) {
    const name =
      this.normalizeText(element.getAttribute('name')) ||
      this.normalizeText(element.getAttribute('formcontrolname')) ||
      this.normalizeText(element.id);
    return name === 'undefined' ? undefined : name;
  }

  private getElementValue(element: HTMLElement) {
    if (element instanceof HTMLSelectElement) {
      return this.normalizeText(element.selectedOptions[0]?.textContent || element.value);
    }

    if (element instanceof HTMLInputElement) {
      if (element.type === 'password' || element.type === 'file') {
        return '';
      }

      return this.normalizeText(element.value);
    }

    if (element instanceof HTMLTextAreaElement) {
      return this.normalizeText(element.value);
    }

    return '';
  }

  private getElementChecked(element: HTMLElement) {
    if (element instanceof HTMLInputElement && ['radio', 'checkbox'].includes(element.type)) {
      return element.checked;
    }

    const ariaChecked = element.getAttribute('aria-checked');
    if (ariaChecked === 'true') {
      return true;
    }
    if (ariaChecked === 'false') {
      return false;
    }

    return undefined;
  }

  private getElementSelector(element: HTMLElement) {
    return this.getElementSelectorCandidates(element)[0] || element.tagName.toLowerCase();
  }

  private getPrimeNgOverlayScopeSelector(element: HTMLElement) {
    const overlay = element.closest<HTMLElement>(
      '.p-multiselect-overlay, .p-select-overlay, .p-autocomplete-panel, .p-dropdown-panel, .p-cascadeselect-overlay, .p-treeselect-overlay, .p-overlaypanel'
    );
    if (!overlay) {
      return '';
    }

    const overlayClass = Array.from(overlay.classList).find(className =>
      /^p-(?:multi)?select-|^p-autocomplete-|^p-dropdown-|^p-cascadeselect-|^p-treeselect-|^p-overlaypanel$/.test(
        className
      )
    );

    return overlayClass ? `.${CSS.escape(overlayClass)}` : this.getElementSelector(overlay);
  }

  private getPrimeNgIconContainerSelector(iconContainer: HTMLElement) {
    const iconPositionClasses = Array.from(iconContainer.classList).filter(className =>
      /^p-(?:iconfield|input-icon)(?:-|$)/.test(className)
    );
    const iconContainerSelector =
      iconPositionClasses.length > 0
        ? iconPositionClasses.map(className => `.${CSS.escape(className)}`).join('')
        : this.getElementSelector(iconContainer);
    const overlayScopeSelector = this.getPrimeNgOverlayScopeSelector(iconContainer);

    return overlayScopeSelector ? `${overlayScopeSelector} ${iconContainerSelector}` : iconContainerSelector;
  }

  private getElementSelectorCandidates(element: HTMLElement) {
    const selectors: string[] = [];
    const pushSelector = (selector: string | null | undefined) => {
      const normalizedSelector = String(selector || '').trim();
      if (!normalizedSelector || selectors.includes(normalizedSelector)) {
        return;
      }
      selectors.push(normalizedSelector);
    };

    this.getPrimeNgDialogCloseSelectorCandidates(element).forEach(pushSelector);
    this.buildScopedSelectorCandidates(element).forEach(pushSelector);
    this.getStableAttributeSelectors(element).forEach(pushSelector);
    pushSelector(this.getGenericElementSelector(element));

    return selectors.slice(0, 8);
  }

  private getPrimeNgDialogCloseSelectorCandidates(element: HTMLElement): string[] {
    const dialog = element.closest<HTMLElement>('dialog, [role="dialog"], [role="alertdialog"]');
    if (!dialog) {
      return [];
    }

    // Only emit the close-button selector when the picked element IS the close button
    // (or its icon descendant). Otherwise we'd misrepresent other dialog buttons.
    const isCloseIconElement = element.matches(
      '.p-dialog-close-button, .p-dialog-close-icon, button.p-dialog-header-icon, button[aria-label*="close" i]'
    );
    if (!isCloseIconElement) {
      return [];
    }

    const candidates: string[] = [];
    const dialogSelector = this.getDialogContainerSelector(dialog);
    if (!dialogSelector) {
      return candidates;
    }

    candidates.push(`${dialogSelector} .p-dialog-close-button`);
    candidates.push(`${dialogSelector} .p-dialog-header button`);
    return candidates;
  }

  private getDialogContainerSelector(dialog: HTMLElement): string {
    const id = dialog.id?.trim();
    if (id && !this.isGeneratedUiId(id)) {
      return `#${CSS.escape(id)}`;
    }
    const ariaLabel = dialog.getAttribute('aria-label')?.trim();
    if (ariaLabel) {
      return `[role="dialog"][aria-label*="${this.escapeAttributeValue(ariaLabel)}" i]`;
    }
    // Fall back to the closest stable ancestor (id or data-testid) so the resulting
    // close-button selector is still uniquely scoped when the dialog itself has no
    // id or aria-label. This commonly happens when the dialog lives inside a wrapper
    // element like <div id="authentication-modal"> that supplies the stable scope.
    const stableAncestor = dialog.parentElement?.closest<HTMLElement>('[id], [data-testid]');
    if (stableAncestor) {
      const ancestorId = stableAncestor.id?.trim();
      if (ancestorId && !this.isGeneratedUiId(ancestorId)) {
        return `${this.getScopeRootSelector(stableAncestor)} [role="dialog"]`;
      }
      const dataTestId = stableAncestor.getAttribute('data-testid')?.trim();
      if (dataTestId) {
        return `${this.getScopeRootSelector(stableAncestor)} [role="dialog"]`;
      }
    }
    return '[role="dialog"]';
  }

  private getStableAttributeSelectors(element: HTMLElement) {
    const tagName = element.tagName.toLowerCase();
    const id = element.id.trim();
    if (id && !this.isGeneratedUiId(id)) {
      return [`#${CSS.escape(id)}`];
    }

    const dataTestId = element.getAttribute('data-testid')?.trim();
    if (dataTestId) {
      return [`[data-testid="${this.escapeAttributeValue(dataTestId)}"]`];
    }

    const formControlName = element.getAttribute('formcontrolname')?.trim();
    if (formControlName) {
      return [`${tagName}[formcontrolname="${this.escapeAttributeValue(formControlName)}"]`];
    }

    const name = element.getAttribute('name')?.trim();
    if (name && name !== 'undefined') {
      return [`${tagName}[name="${this.escapeAttributeValue(name)}"]`];
    }

    const role = element.getAttribute('role')?.trim();
    const ariaLabel = element.getAttribute('aria-label')?.trim();
    if (role && ariaLabel) {
      return [`[role="${this.escapeAttributeValue(role)}"][aria-label="${this.escapeAttributeValue(ariaLabel)}"]`];
    }

    if (ariaLabel) {
      return [`${tagName}[aria-label="${this.escapeAttributeValue(ariaLabel)}"]`];
    }

    const title = element.getAttribute('title')?.trim();
    if (title) {
      return [`${tagName}[title="${this.escapeAttributeValue(title)}"]`];
    }

    return [];
  }

  private getGenericElementSelector(element: HTMLElement) {
    const tagName = element.tagName.toLowerCase();
    const classNames = this.getSelectorClassSuffix(element);
    return `${tagName}${classNames}`;
  }

  private buildScopedSelectorCandidates(element: HTMLElement) {
    const scopeRoots = this.getSelectorScopeRoots(element);
    const genericTargetSelector = this.getGenericElementSelector(element);
    const classTargetSelector = this.getSelectorClassSuffix(element);
    const targetTagName = element.tagName.toLowerCase();
    const candidates: string[] = [];
    const nearestCustomScope = this.getNearestCustomElementAncestor(element);
    const nearestCustomSelector = nearestCustomScope
      ? this.getScopeRootSelector(nearestCustomScope)
      : '';

    if (nearestCustomSelector && nearestCustomScope) {
      const targetWithClass = classTargetSelector
        ? `${targetTagName}${classTargetSelector}`
        : genericTargetSelector;
      if (targetWithClass) {
        const simpleCandidate = `${nearestCustomSelector} ${targetWithClass}`.trim();
        if (!candidates.includes(simpleCandidate)) {
          candidates.push(simpleCandidate);
        }
        try {
          const matches = document.querySelectorAll(simpleCandidate);
          if (matches.length > 1) {
            const idx = Array.from(matches).indexOf(element);
            if (idx >= 0) {
              const disambiguated = `${simpleCandidate}:nth-of-type(${idx + 1})`;
              if (!candidates.includes(disambiguated)) {
                candidates.push(disambiguated);
              }
            }
          }
        } catch {
          // ignore selector errors
        }
      }
    }

    for (const scopeRoot of scopeRoots) {
      const scopeRootSelector = this.getScopeRootSelector(scopeRoot);
      if (!scopeRootSelector) {
        continue;
      }

      const relativePathSegments = this.buildRelativeSelectorPath(scopeRoot, element);
      if (relativePathSegments.length === 0) {
        continue;
      }

      if (classTargetSelector) {
        const c = `${scopeRootSelector} ${classTargetSelector}`.trim();
        if (!candidates.includes(c)) candidates.push(c);
      }
      const c2 = `${scopeRootSelector} ${genericTargetSelector}`.trim();
      if (!candidates.includes(c2)) candidates.push(c2);
      const c3 = `${scopeRootSelector} ${relativePathSegments.join(' ')}`.trim();
      if (!candidates.includes(c3)) candidates.push(c3);
      const simplifiedSegments = relativePathSegments.map((segment, index) =>
        index === relativePathSegments.length - 1 ? targetTagName : segment
      );
      const c4 = `${scopeRootSelector} ${simplifiedSegments.join(' ')}`.trim();
      if (!candidates.includes(c4)) candidates.push(c4);
    }

    return candidates;
  }

  private getNearestCustomElementAncestor(element: HTMLElement): HTMLElement | null {
    let current = element.parentElement;
    while (current) {
      const tagName = current.tagName.toLowerCase();
      if (tagName.includes('-') && !this.isSkippableSelectorScopeTag(tagName)) {
        return current;
      }
      current = current.parentElement;
    }
    return null;
  }

  private getSelectorScopeRoots(element: HTMLElement) {
    const roots: HTMLElement[] = [];
    const pushRoot = (candidate: HTMLElement | null) => {
      if (!candidate || candidate === element || roots.includes(candidate)) {
        return;
      }
      roots.push(candidate);
    };

    pushRoot(this.getNearestClassScopeAncestor(element));
    pushRoot(element.closest<HTMLElement>('dialog, [role="dialog"], [role="alertdialog"]'));

    const customAncestors = this.getCustomAncestors(element);
    if (customAncestors[0]) {
      pushRoot(customAncestors[0]);
    }
    if (customAncestors[1]) {
      pushRoot(customAncestors[1]);
    }
    if (customAncestors[2]) {
      pushRoot(customAncestors[2]);
    }

    pushRoot(element.closest<HTMLElement>('[data-testid]'));
    pushRoot(element.closest<HTMLElement>('[id]'));

    return roots.slice(0, 4);
  }

  private getNearestClassScopeAncestor(element: HTMLElement) {
    let current = element.parentElement;

    while (current) {
      if (this.getSelectorScopeClassSuffix(current)) {
        return current;
      }
      current = current.parentElement;
    }

    return null;
  }

  private getCustomAncestors(element: HTMLElement) {
    const ancestors: HTMLElement[] = [];
    let current = element.parentElement;

    while (current) {
      const tagName = current.tagName.toLowerCase();
      if (tagName.includes('-') && !this.isSkippableSelectorScopeTag(tagName) && !ancestors.includes(current)) {
        ancestors.push(current);
      }
      current = current.parentElement;
    }

    return ancestors;
  }

  private isSkippableSelectorScopeTag(tagName: string) {
    return ['app-root', 'router-outlet', 'body', 'html'].includes(tagName);
  }

  private getScopeRootSelector(element: HTMLElement) {
    const tagName = element.tagName.toLowerCase();
    const id = element.id.trim();
    if (id && !this.isGeneratedUiId(id)) {
      return `#${CSS.escape(id)}`;
    }

    const dataTestId = element.getAttribute('data-testid')?.trim();
    if (dataTestId) {
      return `[data-testid="${this.escapeAttributeValue(dataTestId)}"]`;
    }

    const role = element.getAttribute('role')?.trim();
    if (role === 'dialog' || role === 'alertdialog' || tagName === 'dialog') {
      const dialogLabel = this.getElementLabel(element);
      if (dialogLabel) {
        return `[role="${this.escapeAttributeValue(role || 'dialog')}"][aria-label="${this.escapeAttributeValue(dialogLabel)}"]`;
      }
    }

    const scopeClassSuffix = this.getSelectorScopeClassSuffix(element);
    if (scopeClassSuffix) {
      return `${tagName}${scopeClassSuffix}`;
    }

    return this.buildStructuralSelectorSegment(element, false);
  }

  private isGeneratedUiId(id: string) {
    return /^(pn_id_|pr_id_|p_\d+_|p-[a-z0-9]*\d|cdk-|mat-)/i.test(String(id || '').trim());
  }

  private getElementLayoutHint(element: HTMLElement) {
    const rect = element.getBoundingClientRect();
    return {
      clientWidth: element.clientWidth,
      clientHeight: element.clientHeight,
      offsetWidth: element.offsetWidth,
      offsetHeight: element.offsetHeight,
      boundingWidth: Math.round(rect.width * 100) / 100,
      boundingHeight: Math.round(rect.height * 100) / 100,
      boundingTop: Math.round(rect.top * 100) / 100,
      boundingLeft: Math.round(rect.left * 100) / 100,
      scrollX: Math.round(window.scrollX),
      scrollY: Math.round(window.scrollY),
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight
    };
  }

  private buildRelativeSelectorPath(scopeRoot: HTMLElement, element: HTMLElement) {
    const path: HTMLElement[] = [];
    let current: HTMLElement | null = element;

    while (current && current !== scopeRoot) {
      path.push(current);
      current = current.parentElement;
    }

    if (current !== scopeRoot) {
      return [];
    }

    return path
      .reverse()
      .map((segment, index) => this.buildStructuralSelectorSegment(segment, index === path.length - 1))
      .filter((segment): segment is string => Boolean(segment));
  }

  private buildStructuralSelectorSegment(element: HTMLElement, isTarget: boolean) {
    const tagName = element.tagName.toLowerCase();
    if (!isTarget && this.isUninformativeStructuralNode(element)) {
      return '';
    }
    const classSuffix = isTarget ? this.getSelectorClassSuffix(element) : '';
    const siblingSelectorIndex = this.getSiblingSelectorIndex(element);
    return `${tagName}${classSuffix}${siblingSelectorIndex}`;
  }

  /**
   * A plain element with no id, no class, no test attribute, and no nth-of-type
   * does not narrow a selector. Walk past it instead of emitting `div` in the
   * middle of a path like `ush-custom-page div div div ush-movies-carousel div button`.
   */
  private isUninformativeStructuralNode(element: HTMLElement) {
    const hasId = element.id && !this.isGeneratedUiId(element.id);
    const hasClass = Array.from(element.classList).some(c => !c.startsWith('ng-'));
    if (hasId || hasClass) return false;
    if (element.getAttribute('data-testid')) return false;
    if (element.getAttribute('role')) return false;
    if (element.getAttribute('aria-label')) return false;
    if (element.getAttribute('formcontrolname')) return false;
    const parent = element.parentElement;
    if (parent) {
      const sameTagSiblings = Array.from(parent.children).filter(
        sibling => sibling.tagName === element.tagName
      );
      if (sameTagSiblings.length > 1) return false;
    }
    return true;
  }

  private getSelectorClassSuffix(element: HTMLElement) {
    return Array.from(element.classList)
      .filter(className => !className.startsWith('ng-'))
      .slice(0, 2)
      .map(className => `.${CSS.escape(className)}`)
      .join('');
  }

  private getSelectorScopeClassSuffix(element: HTMLElement) {
    const ignoredScopeClasses = new Set(['p-component', 'p-element', 'p-ripple']);
    return Array.from(element.classList)
      .filter(className => !className.startsWith('ng-'))
      .filter(className => !ignoredScopeClasses.has(className))
      .filter(className => !/^p-(?:button|dialog|overlay|component|element)$/i.test(className))
      .slice(0, 2)
      .map(className => `.${CSS.escape(className)}`)
      .join('');
  }

  private getSiblingSelectorIndex(element: HTMLElement) {
    const parent = element.parentElement;
    if (!parent) {
      return '';
    }

    const sameTagSiblings = Array.from(parent.children).filter(
      sibling => sibling.tagName.toLowerCase() === element.tagName.toLowerCase()
    );

    if (sameTagSiblings.length <= 1) {
      return '';
    }

    return `:nth-of-type(${sameTagSiblings.indexOf(element) + 1})`;
  }

  private escapeAttributeValue(value: string) {
    return value.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  }

  private async measureDomSettleMetrics(
    options?: {
      settleMs?: number;
      mutationCount?: number;
    },
    maxWaitMs = 1500,
    idleWindowMs = 250
  ) {
    if (typeof options?.settleMs === 'number' || typeof options?.mutationCount === 'number') {
      return {
        settleMs: typeof options?.settleMs === 'number' ? options.settleMs : 0,
        mutationCount: typeof options?.mutationCount === 'number' ? options.mutationCount : 0
      };
    }

    return new Promise<{ settleMs: number; mutationCount: number }>(resolve => {
      const root = document.documentElement;
      if (!root) {
        resolve({
          settleMs: 0,
          mutationCount: 0
        });
        return;
      }

      const startedAt = window.performance?.now?.() ?? Date.now();
      let lastMutationAt = startedAt;
      let mutationCount = 0;
      let finished = false;
      let pollTimer = 0;
      let timeoutTimer = 0;

      const finish = () => {
        if (finished) {
          return;
        }

        finished = true;
        observer.disconnect();
        window.clearInterval(pollTimer);
        window.clearTimeout(timeoutTimer);
        const endedAt = window.performance?.now?.() ?? Date.now();
        resolve({
          settleMs: Math.max(0, Math.round(endedAt - startedAt)),
          mutationCount
        });
      };

      const observer = new MutationObserver(() => {
        lastMutationAt = window.performance?.now?.() ?? Date.now();
        mutationCount += 1;
      });

      observer.observe(root, {
        subtree: true,
        childList: true,
        attributes: true,
        characterData: true
      });

      pollTimer = window.setInterval(() => {
        const now = window.performance?.now?.() ?? Date.now();
        if (now - lastMutationAt >= idleWindowMs) {
          finish();
        }
      }, 50);

      timeoutTimer = window.setTimeout(finish, maxWaitMs);
    });
  }

  private normalizeText(value: string | null | undefined) {
    return (value ?? '').replace(/\s+/g, ' ').trim().slice(0, 120);
  }

  // The recorder flag is removed from the stored URL so replay instructions stay cleaner and more reproducible.
  private buildStep(type: AiRecordedStep['type'], rawUrl: string): Omit<AiRecordedStep, 'index'> {
    const location = this.parseUrl(rawUrl);

    return {
      type,
      title: type === 'entry' ? `Open ${location.url}` : `Navigate to ${location.url}`,
      url: location.url,
      path: location.path,
      hash: location.hash,
      queryParams: location.queryParams
    };
  }

  private parseUrl(rawUrl: string) {
    const parsedUrl = new URL(rawUrl, window.location.origin);
    parsedUrl.searchParams.delete('recordSteps');

    const queryParams: Record<string, string | string[]> = {};
    parsedUrl.searchParams.forEach((value, key) => {
      const existingValue = queryParams[key];
      if (existingValue === undefined) {
        queryParams[key] = value;
        return;
      }

      queryParams[key] = Array.isArray(existingValue) ? [...existingValue, value] : [existingValue, value];
    });

    const search = parsedUrl.searchParams.toString();

    return {
      url: `${parsedUrl.pathname}${search ? `?${search}` : ''}${parsedUrl.hash}`,
      path: parsedUrl.pathname,
      hash: parsedUrl.hash,
      queryParams
    };
  }

  private async analyzeBugfix(options: {
    bugContext: string;
    expectedOutcome: string;
    scope?: string;
  }): Promise<AiBugfixAnalysisResult | { error: string }> {
    const engine = await loadReasoningEngine();
    if (!engine) {
      return { error: 'Reasoning engine not available' };
    }

    const appResolution = this.resolveAppIdentifier();
    const input = {
      app: appResolution.value,
      scope:
        options.scope ||
        this.inferAutomatedBugfixRouteFocus({
          bug_context: options.bugContext,
          expected_outcome: options.expectedOutcome,
          runtime_context: { current_path: window.location.pathname } as any
        } as any),
      bugContext: options.bugContext,
      expectedResult: options.expectedOutcome,
      path: window.location.pathname,
      likelyFiles: []
    };

    const componentMap = await this.fetchComponentMap();
    const adapters = this.buildBrowserAdapters(componentMap);
    const styleFilePaths = this.getComponentMapStylePaths(componentMap, input.app);

    const relevantEntries = engine.findRelevantEntries(componentMap, input, adapters);
    const fixRouting = engine.classifyFixRouting(input, relevantEntries, [], {
      ...adapters,
      environmentKeys: [],
      styleFilePaths
    });

    return {
      relevantEntries: relevantEntries.slice(0, 10),
      fixRouting,
      loadedFrom: 'bundle'
    };
  }

  private getComponentMapStylePaths(
    componentMap: {
      appStyleBundles?: Record<string, { transitiveStylePaths?: string[]; stylePaths?: string[] }>;
    },
    app: string
  ): string[] {
    const bundle = componentMap.appStyleBundles?.[app];
    const stylePaths = [...(bundle?.transitiveStylePaths || []), ...(bundle?.stylePaths || [])];
    return [...new Set(['libs/styles/primeng.overrides.scss', ...stylePaths])];
  }

  /**
   * @deprecated Use `window.usheru.report()` instead — the bug report form is the primary entry point.
   * This method still works but requires DevTools and $0. The form-based flow handles the same analysis
   * with or without a selected element.
   *
   * USAGE (legacy):
   * `window.usheru.reviewAutomatedBugfixPlan({ bug_context: 'CTA wraps on mobile', expected_outcome: 'CTA stays on one line' }, $0)`
   *
   * NEW USAGE (preferred):
   * `window.usheru.report()` — opens the bug report form
   * `window.usheru.report({bug: 'CTA wraps', expected: 'Button stays on one line'})` — with initial text
   */
  reviewAutomatedBugfixPlan(
    request?: AiBugfixReviewConsoleRequest,
    targetElement?: Element | null
  ): AiBugfixReviewTicket | AiBugfixReviewHelpResult {
    if (request == null || (typeof request === 'string' && /^(help|\?|example|examples)$/i.test(request.trim()))) {
      return this.printReviewHelp();
    }

    const normalizedRequest: Partial<AiAutomatedBugfixSkillRequest> =
      typeof request === 'string' ? { bug_context: request } : (request ?? {});
    const bugContext = String(normalizedRequest.bug_context || '').trim();
    const expectedOutcome = String(normalizedRequest.expected_outcome || '').trim();

    if (!bugContext) {
      console.error('reviewAutomatedBugfixPlan requires bug_context.');
      return { id: '', status: 'error', copyPayload: () => {} };
    }
    if (!expectedOutcome) {
      console.error('reviewAutomatedBugfixPlan requires expected_outcome.');
      return { id: '', status: 'error', copyPayload: () => {} };
    }
    if (!targetElement || !(targetElement instanceof Element)) {
      console.error('reviewAutomatedBugfixPlan requires a target element. Select an element in DevTools and pass $0.');
      return { id: '', status: 'error', copyPayload: () => {} };
    }

    console.warn(
      '%c⚠️ reviewAutomatedBugfixPlan is deprecated. Use window.usheru.report() instead (the bug report form).',
      'color:#f38ba8;font-weight:600;'
    );

    const ticketId = `review-${Date.now()}`;
    const ticket: AiBugfixReviewTicket = {
      id: ticketId,
      status: 'pending',
      copyPayload: () => this.copyLastReviewPayload()
    };

    console.log('%c🔍 BUGFIX PLAN REVIEW (legacy)', 'color:#7c3aed;font-weight:700;font-size:14px');
    console.log(`%c[${ticketId}] Analyzing: "${bugContext}"`, 'color:#6b7280');

    this.runReviewAnalysis(ticketId, normalizedRequest, targetElement, bugContext, expectedOutcome);

    return ticket;
  }

  private async runReviewAnalysis(
    ticketId: string,
    normalizedRequest: Partial<AiAutomatedBugfixSkillRequest>,
    targetElement: Element,
    bugContext: string,
    expectedOutcome: string
  ) {
    const usheru = this.getUsheruGlobal();
    usheru._lastReviewPayloadJson = undefined;

    console.log(`%c[${ticketId}] ⏳ Loading reasoning engine...`, 'color:#b45309');

    const engine = await loadReasoningEngine();
    if (!engine) {
      console.error(`%c[${ticketId}] ✘ Reasoning engine not available`, 'color:#dc2626');
      return;
    }

    console.log(`%c[${ticketId}] ⏳ Inspecting target element...`, 'color:#b45309');
    const targetElementHint = this.describeElement(targetElement);

    console.log(`%c[${ticketId}] ⏳ Inferring scope and fix routing...`, 'color:#b45309');
    const scope = this.inferAutomatedBugfixRouteFocus({
      bug_context: bugContext,
      expected_outcome: expectedOutcome,
      runtime_context: {
        current_path: window.location.pathname,
        target_element: targetElementHint || undefined,
        recorded_steps: this.getRecordedSteps()
      } as any
    } as any);

    const reasoningInput = {
      app: this.resolveAppIdentifier().value,
      scope,
      bugContext,
      expectedResult: expectedOutcome,
      path: window.location.pathname,
      likelyFiles: [],
      runtimeContext: targetElementHint
        ? {
            target_element: targetElementHint
          }
        : undefined
    };

    console.log(`%c[${ticketId}] ⏳ Fetching component registry...`, 'color:#b45309');
    const componentMap = await this.fetchComponentMap();
    const adapters = this.buildBrowserAdapters(componentMap);
    const styleFilePaths = this.getComponentMapStylePaths(componentMap, reasoningInput.app);

    console.log(`%c[${ticketId}] ⏳ Scoring relevant files...`, 'color:#b45309');
    const relevantEntries = engine.findRelevantEntries(componentMap, reasoningInput, adapters);
    const fixRouting = engine.classifyFixRouting(reasoningInput, relevantEntries, [], {
      ...adapters,
      environmentKeys: [],
      styleFilePaths
    });

    console.log(`%c[${ticketId}] ⏳ Building payload...`, 'color:#b45309');
    const payload = this.buildAutomatedBugfixSkillPayload(normalizedRequest, targetElement);

    const assumptions: string[] = [];
    assumptions.push(`Scope: ${scope}`);
    assumptions.push(`Fix type: ${fixRouting.fixType}`);
    assumptions.push(`Route action: ${fixRouting.routeAction}`);
    if (normalizedRequest.source_of_truth && normalizedRequest.source_of_truth !== 'local') {
      assumptions.push(`Source of truth: ${normalizedRequest.source_of_truth}`);
    }
    if (fixRouting.existingStyleVariableHints.length > 0) {
      assumptions.push(`CSS variables detected: ${fixRouting.existingStyleVariableHints.join(', ')}`);
    }
    if (fixRouting.layoutConstraintHints.length > 0) {
      assumptions.push(`Layout constraints: ${fixRouting.layoutConstraintHints.join(', ')}`);
    }
    if (relevantEntries.length > 0) {
      assumptions.push(`Top component target: ${relevantEntries[0].kind} (score: ${relevantEntries[0].score})`);
    }
    if (targetElementHint) {
      assumptions.push(`Target selector: ${targetElementHint.selector}`);
      if (targetElementHint.componentChain.length > 0) {
        assumptions.push(`Component chain: ${targetElementHint.componentChain.join(' → ')}`);
      }
    }

    const payloadJson = JSON.stringify(payload);
    const usheruGlobal = this.getUsheruGlobal();
    usheruGlobal._lastReviewPayloadJson = payloadJson;

    const divider = '━'.repeat(70);
    console.log(`\n${divider}`);
    console.log(`%c[${ticketId}] ✓ ANALYSIS COMPLETE`, 'color:#059669;font-weight:700;font-size:14px');
    console.log(`${divider}\n`);

    this.printDecisionAnalysis({
      ticketId,
      bugContext,
      expectedOutcome,
      scope,
      targetElementHint,
      relevantEntries,
      fixRouting,
      currentPath: window.location.pathname
    });

    this.printValidationExpectations(payload);

    // Live CSS diagnostics on the target element
    const diagnosticElement =
      this.findRecordedStructuralDiagnosticElement(bugContext) || (targetElement as HTMLElement);
    console.log(
      diagnosticElement === targetElement
        ? `\n%c🧬 DIAGNOSING TARGET ELEMENT...`
        : `\n%c🧬 DIAGNOSING RECORDED STRUCTURAL ELEMENT...`,
      'color:#7c3aed;font-weight:700'
    );
    const diagnosticReport = await this.diagnoseElement(
      diagnosticElement,
      this.resolveAppIdentifier().value,
      bugContext
    );

    // Format diagnostics text for LLM context
    const cssDiagnosticsText = this.formatDiagnosticsForLlm(diagnosticReport);

    // Summarize fix suggestions in assumptions
    const fixCount = diagnosticReport?.fixSuggestions?.length ?? 0;
    if (fixCount > 0) {
      assumptions.push(`Fix suggestions from live CSS diagnostics: ${fixCount} found`);
      for (const fix of diagnosticReport.fixSuggestions) {
        assumptions.push(`  → Set ${fix.variable}: ${fix.suggestedValue} at ${fix.scope}`);
      }
    } else {
      assumptions.push('Live CSS diagnostics: no fix suggestions (contrast OK or no matching PrimeNG bridges)');
    }

    console.log('\n%cASSUMPTIONS (implicit decisions made by the analyzer):', 'color:#2563eb;font-weight:700');
    assumptions.forEach((a, i) => console.log(`  ${i + 1}. ${a}`));

    console.log('\n%cCOPY PAYLOAD:', 'color:#2563eb;font-weight:700');
    console.log(
      '  If this plan looks correct: %ccopy(window.usheru._lastReviewPayloadJson)',
      'color:#111827;background:#f3f4f6;padding:2px 4px'
    );
    console.log('  Then paste into GitHub Actions reproduction_steps');

    console.log(`\n${divider}`);

    this.offerWebLlmChat({
      bugContext,
      expectedOutcome,
      scope,
      relevantEntries,
      targetElementHint,
      fixRouting,
      currentPath: window.location.pathname,
      recordedSteps: this.getRecordedSteps(),
      recordedDomInteractions: this.getRecordedDomInteractions(),
      cssFixSuggestions: diagnosticReport?.fixSuggestions || [],
      cssDiagnosticsText
    });

    void this.suggestAndDisplayWebLlmFix({
      bugContext,
      expectedOutcome,
      scope,
      app: this.resolveAppIdentifier().value,
      targetElementHint,
      fixRouting,
      relevantEntries,
      cssDiagnosticsText,
      cssFixSuggestions: diagnosticReport?.fixSuggestions || []
    });
  }

  private async suggestAndDisplayWebLlmFix(ctx: {
    bugContext: string;
    expectedOutcome: string;
    scope: string;
    app: string;
    targetElementHint: AiTargetElementHint | null;
    fixRouting: {
      fixType: string;
      routeAction: string;
      existingStyleVariableHints: string[];
    };
    relevantEntries: Array<{ selector: string; symbolName: string; kind: string; score: number }>;
    cssDiagnosticsText?: string;
    cssFixSuggestions: Array<{ variable: string; currentValue: string; suggestedValue: string; scope: string }>;
  }) {
    if (!hasWebGpuSupport()) return;

    const result = await suggestWebLlmFix({
      bugContext: ctx.bugContext,
      expectedOutcome: ctx.expectedOutcome,
      scope: ctx.scope,
      app: ctx.app,
      targetSelector: ctx.targetElementHint?.selector || ctx.targetElementHint?.selectorCandidates?.[0] || '',
      fixType: ctx.fixRouting.fixType,
      routeAction: ctx.fixRouting.routeAction,
      componentChain: ctx.targetElementHint?.componentChain || [],
      topComponents: ctx.relevantEntries,
      existingStyleVariableHints: ctx.fixRouting.existingStyleVariableHints,
      cssDiagnosticsText: ctx.cssDiagnosticsText,
      cssFixSuggestions: ctx.cssFixSuggestions
    });

    if (!result) return;

    const lines = result.split('\n').filter(Boolean);
    const variable = lines.find(l => /^Variable:/i.test(l))?.replace(/^Variable:\s*/i, '') || '';
    const value = lines.find(l => /^Value:/i.test(l))?.replace(/^Value:\s*/i, '') || '';
    const scope = lines.find(l => /^Scope:/i.test(l))?.replace(/^Scope:\s*/i, '') || '';
    const filePath = lines.find(l => /^File:/i.test(l))?.replace(/^File:\s*/i, '') || '';
    const reason = lines.find(l => /^Reason:/i.test(l))?.replace(/^Reason:\s*/i, '') || '';

    console.log('\n%c🧠 AI FIX SUGGESTION (via WebLLM)', 'color:#89b4fa;font-weight:700;font-size:13px;');
    if (variable && value) {
      console.log(`  %c${variable}: ${value}`, 'color:#a6e3a1;font-weight:600');
      if (scope) console.log(`  On:        ${scope}`);
      if (filePath) console.log(`  File:      ${filePath}`);
      if (reason) console.log(`  Why:       ${reason}`);
    } else {
      console.log(`  ${result.slice(0, 300)}`);
    }
    console.log('');
  }

  private offerWebLlmChat(ctx: {
    bugContext: string;
    expectedOutcome: string;
    scope: string;
    relevantEntries: Array<{ score: number; sourcePath: string; kind: string; selector: string; symbolName: string }>;
    targetElementHint: AiTargetElementHint | null;
    fixRouting: {
      fixType: string;
      routeAction: string;
      visualIssueType?: string;
      existingStyleVariableHints: string[];
      layoutConstraintHints: string[];
    };
    currentPath: string;
    recordedSteps: AiRecordedStep[];
    recordedDomInteractions: AiRecordedDomInteraction[];
    cssFixSuggestions?: AiCssFixSuggestion[];
    cssDiagnosticsText?: string;
  }) {
    const usheru = this.getUsheruGlobal();
    if (!hasWebGpuSupport()) {
      console.log(
        '\n%c🤖 AI Bugfix Assistant: WebGPU not supported in this browser.',
        'color:#f38ba8;font-weight:600;'
      );
      console.log('  Use Chrome 113+ or Edge 113+ for the interactive chat assistant.');
      return;
    }

    if (usheru._webLlmChat) return;

    const readiness = this.computeReadinessScore(ctx);
    const topComponent = ctx.relevantEntries.length > 0 ? ctx.relevantEntries[0].selector : 'unknown';

    const chatInstance = createWebLlmChatWidget({
      app: this.resolveAppIdentifier().value,
      bugContext: ctx.bugContext,
      expectedOutcome: ctx.expectedOutcome,
      scope: ctx.scope,
      relevantComponents: ctx.targetElementHint?.componentChain || [],
      targetSelector: ctx.targetElementHint?.selector || '',
      readinessScore: readiness.score,
      fixType: ctx.fixRouting.fixType,
      routeAction: ctx.fixRouting.routeAction,
      topComponent,
      questions: readiness.questions.slice(0, 3).map(q => q.question),
      hasRecordedSteps: ctx.recordedSteps.length > 0 || ctx.recordedDomInteractions.length > 0,
      recordedSteps: ctx.recordedSteps,
      recordedDomInteractions: ctx.recordedDomInteractions,
      cssFixSuggestions: ctx.cssFixSuggestions,
      reasoningContext: {
        targetElementHint: ctx.targetElementHint,
        relevantEntries: ctx.relevantEntries.slice(0, 3),
        fixRouting: ctx.fixRouting,
        cssVars: ctx.fixRouting.existingStyleVariableHints
      },
      cssDiagnosticsText: ctx.cssDiagnosticsText
    });
    usheru._webLlmChat = chatInstance;

    console.log('\n%c🤖 AI Bugfix Assistant ready!', 'color:#89b4fa;font-weight:700;font-size:13px;');
    console.log(
      '  Open the interactive chat: %cwindow.usheru._webLlmChat.open()',
      'color:#1e1e2e;background:#89b4fa;padding:2px 6px;'
    );
    console.log('  Ask questions, get suggestions for improving your bug report.');
  }

  private printDecisionAnalysis(ctx: {
    ticketId: string;
    bugContext: string;
    expectedOutcome: string;
    scope: string;
    targetElementHint: AiTargetElementHint | null;
    relevantEntries: Array<{ score: number; sourcePath: string; selector: string; symbolName: string; kind: string }>;
    fixRouting: {
      fixType: string;
      routeAction: string;
      visualIssueType?: string;
      existingStyleVariableHints: string[];
      layoutConstraintHints: string[];
    };
    currentPath: string;
  }) {
    const readiness = this.computeReadinessScore(ctx);

    console.log('%cREADINESS SCORE:', 'color:#7c3aed;font-weight:700;font-size:14px');
    console.log(`  ${readiness.score}% — ${readiness.verdict}`);
    if (readiness.gaps.length > 0) {
      console.log('%c  Missing to reach 100%:', 'color:#dc2626');
      readiness.gaps.forEach(g => console.log(`    ✗ ${g}`));
    }
    if (readiness.strengths.length > 0) {
      console.log('%c  Strengths:', 'color:#059669');
      readiness.strengths.forEach(s => console.log(`    ✓ ${s}`));
    }

    console.log(
      '\n%cTARGET FOLLOW-UP QUESTIONS (answer these to improve confidence):',
      'color:#b45309;font-weight:700'
    );
    if (readiness.questions.length === 0) {
      console.log('  None — input is strong enough to proceed');
    } else {
      readiness.questions.forEach((q, i) => {
        const severity = q.severity === 'blocker' ? '🔴' : q.severity === 'important' ? '🟡' : '🔵';
        console.log(`  ${severity} ${i + 1}. ${q.question}`);
        if (q.suggestion) {
          console.log(`     → ${q.suggestion}`);
        }
      });
    }

    if (ctx.targetElementHint) {
      console.log('\n%cYOUR SELECTED ELEMENT ($0):', 'color:#059669;font-weight:700');
      console.log(`  Selector: ${ctx.targetElementHint.selector}`);
      if (ctx.targetElementHint.role) console.log(`  Role: ${ctx.targetElementHint.role}`);
      if (ctx.targetElementHint.surface) console.log(`  Surface: ${ctx.targetElementHint.surface}`);
      if (ctx.targetElementHint.componentChain.length > 0) {
        const chainComponents = ctx.targetElementHint.componentChain.filter(c => c !== 'app-root');
        console.log(`  Component chain: ${ctx.targetElementHint.componentChain.join(' → ')}`);
        console.log(`  Relevant components: ${chainComponents.join(', ')}`);
      }
      if (ctx.targetElementHint.relatedOwners?.length) {
        console.log(
          `  Related owners: ${ctx.targetElementHint.relatedOwners
            .slice(0, 5)
            .map(owner => `${owner.kind}:${owner.selector}`)
            .join(' | ')}`
        );
      }
      if (ctx.targetElementHint.layout) {
        const l = ctx.targetElementHint.layout;
        console.log(`  Dimensions: ${l.offsetWidth}×${l.offsetHeight}`);
      }
    }

    console.log('\n%cSCOPE DECISION:', 'color:#2563eb;font-weight:700');
    console.log(`  → "${ctx.scope}"`);
    console.log(`  Signals:`);
    const scopeSignals = this.buildScopeSignals(ctx);
    scopeSignals.forEach(s => console.log(`    ${s.matched ? '✓' : '✗'} ${s.label} ${s.matched ? '' : '(no match)'}`));

    console.log('\n%cFIX ROUTING RATIONALE:', 'color:#2563eb;font-weight:700');
    const routingExplanation = this.explainFixRouting(ctx.fixRouting);
    routingExplanation.forEach(line => console.log(`  ${line}`));

    console.log('\n%cTARGET COMPONENTS:', 'color:#2563eb;font-weight:700');
    if (ctx.relevantEntries.length === 0) {
      console.log('  ⚠ No component map entries matched — AI will use text-based search only');
    } else {
      const elementChain = ctx.targetElementHint?.componentChain || [];

      ctx.relevantEntries.slice(0, 5).forEach((f, i) => {
        const prefix = i === 0 ? '→ ' : '  ';
        const isInChain = elementChain.some(c => c === f.selector);
        const chainMarker = isInChain ? ' ✅ (in your element chain)' : ' (not in chain)';
        console.log(`  ${prefix}<${f.selector}> (${f.kind}, score: ${f.score})${chainMarker}`);
      });
      if (ctx.relevantEntries.length > 5) {
        console.log(`  ...and ${ctx.relevantEntries.length - 5} more`);
      }

      // Decision guidance
      const topEntry = ctx.relevantEntries[0];
      const topInChain = elementChain.some(c => c === topEntry.selector);
      const scoreDiff = ctx.relevantEntries.length > 1 ? topEntry.score - ctx.relevantEntries[1].score : 999;

      console.log('');
      if (topInChain) {
        console.log(`  ✓ Top match <${topEntry.selector}> is in your element's chain — this is the right component`);
      } else if (scoreDiff <= 10 && ctx.relevantEntries.length > 1) {
        const second = ctx.relevantEntries[1];
        console.log(`  ⚠ Scores are close (${topEntry.score}% vs ${second.score}%)`);
        console.log(`  → Your element lives inside: ${elementChain.join(' → ')}`);
        console.log(`  → If the bug is in the carousel itself, use <${topEntry.selector}>`);
        if (elementChain.includes(second.selector)) {
          console.log(`  → If the bug is in inner items, use <${second.selector}>`);
        }
      } else if (topEntry.score < 40) {
        console.log(`  ⚠ Low confidence — verify <${topEntry.selector}> is the right component`);
      }
    }

    console.log('\n%cALTERNATIVE PATHS (if primary assumption is wrong):', 'color:#2563eb;font-weight:700');
    const alternatives = this.buildAlternatives(ctx);
    if (alternatives.length === 0) {
      console.log('  No obvious alternatives detected');
    } else {
      alternatives.forEach((a, i) => console.log(`  ${i + 1}. ${a}`));
    }

    console.log('\n%cRISK ASSESSMENT:', 'color:#2563eb;font-weight:700');
    const risks = this.buildRiskAssessment(ctx);
    if (risks.length === 0) {
      console.log('  Low risk — straightforward fix pattern');
    } else {
      risks.forEach(r => console.log(`  ${r}`));
    }
  }

  /** @internal exposed for use by AiBugReportComponent */
  computeReadinessScore(ctx: {
    bugContext: string;
    expectedOutcome: string;
    scope: string;
    targetElementHint: AiTargetElementHint | null;
    relevantEntries: Array<{ score: number; sourcePath: string; kind: string; selector: string; symbolName: string }>;
    fixRouting: { fixType: string; routeAction: string };
    currentPath: string;
  }): {
    score: number;
    verdict: string;
    strengths: string[];
    gaps: string[];
    questions: Array<{ question: string; suggestion?: string; severity: 'blocker' | 'important' | 'nice-to-have' }>;
  } {
    let score = 0;
    const strengths: string[] = [];
    const gaps: string[] = [];
    const questions: Array<{
      question: string;
      suggestion?: string;
      severity: 'blocker' | 'important' | 'nice-to-have';
    }> = [];

    // Bug context quality (0-25 points)
    const bugWords = ctx.bugContext.split(/\s+/).length;
    if (ctx.bugContext.length >= 30) {
      score += 25;
      strengths.push(`Detailed bug description (${bugWords} words)`);
    } else if (ctx.bugContext.length >= 15) {
      score += 15;
      if (bugWords < 5) gaps.push('Bug description is vague — describe what you see vs what you expect');
    } else {
      score += 5;
      gaps.push('Bug description too short');
      questions.push({
        question: 'What exactly is broken?',
        suggestion:
          'Describe the current visual state in detail (e.g. "arrows are white and 2px apart" not "arrows look wrong")',
        severity: 'blocker'
      });
    }

    // Expected outcome quality (0-25 points)
    const outcomeWords = ctx.expectedOutcome.split(/\s+/).length;
    if (ctx.expectedOutcome.length >= 25) {
      score += 25;
      strengths.push(`Clear expected outcome (${outcomeWords} words)`);
    } else if (ctx.expectedOutcome.length >= 12) {
      score += 15;
      gaps.push('Expected outcome could be more specific');
    } else {
      score += 5;
      gaps.push('Expected outcome too vague');
      questions.push({
        question: 'What should the user see after the fix?',
        suggestion:
          'Describe the visual change as if writing a test (e.g. "arrows should be blue (#0066cc) with 8px gap between them")',
        severity: 'blocker'
      });
    }

    // Target element (0-20 points)
    if (ctx.targetElementHint) {
      score += 10;
      strengths.push('Target element identified');
      if (ctx.targetElementHint.selector) {
        score += 5;
        strengths.push('Selector resolved');
      }
      if (ctx.targetElementHint.componentChain.length > 0) {
        score += 5;
        strengths.push(`Component chain resolved (${ctx.targetElementHint.componentChain.length} components)`);
      } else {
        gaps.push('No component chain — element may be outside a known component');
      }
    } else {
      gaps.push('No target element selected');
      questions.push({
        question: 'Which element on the page is affected?',
        suggestion: 'Right-click the broken element → Inspect, then pass $0 as the second argument',
        severity: 'important'
      });
    }

    // File matches (0-15 points)
    if (ctx.relevantEntries.length > 0) {
      const topScore = ctx.relevantEntries[0].score;
      const topSelector =
        ctx.relevantEntries[0].selector || ctx.relevantEntries[0].symbolName || ctx.relevantEntries[0].kind;
      const topKind = ctx.relevantEntries[0].kind;
      const otherMatches = ctx.relevantEntries.slice(1, 4);
      const chainSelectors = ctx.targetElementHint?.componentChain || [];
      const topInChain = chainSelectors.includes(topSelector);
      // Chain confirmation overrides raw token-match score
      const effectiveScore = topInChain ? Math.max(topScore, 80) : topScore;

      if (effectiveScore > 60 || topInChain) {
        score += 15;
        if (topInChain) {
          strengths.push(`Component confirmed by element chain: <${topSelector}> (${topKind})`);
        } else {
          strengths.push(`High-confidence match: <${topSelector}> (${topKind}, ${topScore}%)`);
        }
      } else if (effectiveScore > 30) {
        score += 10;

        const bugContextLower = ctx.bugContext.toLowerCase();
        const chainMismatch =
          !topInChain &&
          chainSelectors.length > 0 &&
          chainSelectors.some(c => bugContextLower.includes(c.replace('ush-', '').split('-').join(' ')));

        if (topInChain) {
          questions.push({
            question: `Matched <${topSelector}> (${topKind}) — confirmed by element chain`,
            suggestion: this.buildComponentImprovementSuggestion(
              topKind,
              topSelector,
              topScore,
              otherMatches,
              ctx,
              true
            ),
            severity: 'nice-to-have'
          });
        } else if (chainMismatch) {
          questions.push({
            question: `Matched <${topSelector}> (${topKind}, ${topScore}%) but your element is in a different component chain`,
            suggestion: `Your selected element lives inside: ${chainSelectors.filter(c => c !== 'app-root').join(' → ')}. If the bug is actually here, select an element inside this component instead.`,
            severity: 'important'
          });
        } else {
          questions.push({
            question: `Matched <${topSelector}> (${topKind}) with medium confidence (${topScore}%)`,
            suggestion: this.buildComponentImprovementSuggestion(
              topKind,
              topSelector,
              topScore,
              otherMatches,
              ctx,
              false
            ),
            severity: 'important'
          });
        }
      } else {
        score += 5;

        const bugContextLower = ctx.bugContext.toLowerCase();
        const chainSelectors = ctx.targetElementHint?.componentChain || [];
        const mentionsDifferentComponent = chainSelectors.some(c =>
          bugContextLower.includes(c.replace('ush-', '').split('-').join(' '))
        );

        if (topInChain) {
          score += 10;
          strengths.push(`Component confirmed by element chain: <${topSelector}> (${topKind})`);
        } else if (mentionsDifferentComponent && !chainSelectors.includes(topSelector)) {
          questions.push({
            question: `Bug context mentions a different component than where $0 lives`,
            suggestion: `$0 is inside: ${chainSelectors.filter(c => c !== 'app-root').join(' → ')}. Either select the element inside the component you mentioned, or update bug_context to match where the bug actually is.`,
            severity: 'important'
          });
        } else {
          questions.push({
            question: `Best match <${topSelector}> (${topKind}) is weak (${topScore}%)`,
            suggestion: this.buildComponentImprovementSuggestion(topKind, topSelector, topScore, otherMatches, ctx),
            severity: 'important'
          });
        }
      }
    } else {
      gaps.push('No known components matched');
      questions.push({
        question: 'Could not identify any target component',
        suggestion:
          'Describe which UI section the bug is in. For example: "the movie hero banner at the top", "the channel carousel section", "the filter sidebar on the movies page"',
        severity: 'blocker'
      });
    }

    // Scope clarity (0-10 points)
    if (ctx.scope !== 'generic') {
      score += 10;
      strengths.push(`Specific scope: "${ctx.scope}"`);
    } else {
      gaps.push('Scope is "generic" — AI will have less routing guidance');
      questions.push({
        question: 'Where on the site does this bug live?',
        suggestion: 'Include page context like "on the movie detail hero" or "in the homepage carousel"',
        severity: 'important'
      });
    }

    // Fix routing clarity (0-5 points)
    if (ctx.fixRouting.fixType !== 'unknown' && ctx.fixRouting.routeAction !== 'unknown') {
      score += 5;
      strengths.push(`Fix type classified: ${ctx.fixRouting.fixType} → ${ctx.fixRouting.routeAction}`);
    }

    score = Math.min(score, 100);
    const verdict =
      score >= 85
        ? 'Ready to submit'
        : score >= 65
          ? 'Good, but could be stronger'
          : score >= 45
            ? 'Needs more context'
            : 'Insufficient — add details before submitting';

    return { score, verdict, strengths, gaps, questions };
  }

  private buildComponentImprovementSuggestion(
    kind: string,
    selector: string,
    score: number,
    otherMatches: Array<{ score: number; selector: string; kind: string }>,
    ctx: { bugContext: string; expectedOutcome: string; scope: string; targetElementHint: AiTargetElementHint | null },
    chainConfirmed: boolean = false
  ): string {
    const parts: string[] = [];

    if (chainConfirmed) {
      parts.push(
        `<${selector}> confirmed by element chain — token match score is ${score}% due to similar components on page`
      );
    } else {
      parts.push(`The system matched the bug to <${selector}> (${score}% token match)`);
    }

    // Point out other candidates only if scores are competitive
    const competitiveMatches = otherMatches.filter(m => Math.abs(m.score - score) <= 10 && m.selector !== selector);
    if (competitiveMatches.length > 0) {
      if (chainConfirmed) {
        const otherNames = competitiveMatches
          .filter(m => !ctx.targetElementHint?.componentChain.includes(m.selector))
          .map(m => `<${m.selector}> (${m.score}%)`)
          .join(', ');
        if (otherNames) {
          parts.push(`Other similar components on page (not in your chain): ${otherNames}`);
        }
      } else {
        const otherNames = competitiveMatches.map(m => `<${m.selector}> (${m.score}%)`).join(', ');
        parts.push(`Similar matches: ${otherNames}`);
      }
    }

    // Suggest visual specifics if outcome is vague
    if (ctx.expectedOutcome.split(' ').length < 5) {
      parts.push('Describe the expected visual state in detail (colors, spacing, alignment)');
    }

    return parts.join('. ');
  }

  private buildScopeSignals(ctx: {
    scope: string;
    bugContext: string;
    expectedOutcome: string;
    currentPath: string;
    targetElementHint: AiTargetElementHint | null;
  }): Array<{ label: string; matched: boolean }> {
    const combined = `${ctx.bugContext} ${ctx.expectedOutcome} ${ctx.currentPath}`.toLowerCase();
    const target = ctx.targetElementHint?.selector?.toLowerCase() || '';

    return [
      {
        label: `Path matches scope "${ctx.scope}"`,
        matched: combined.includes(ctx.scope.toLowerCase().split('/')[0].trim())
      },
      {
        label: 'Bug keywords match known patterns',
        matched: /\bcarousel|hero|filter|button|arrow|overlay|modal|header|footer\b/.test(combined)
      },
      { label: 'Target element provides additional context', matched: target.length > 10 },
      { label: 'Expected outcome is specific', matched: ctx.expectedOutcome.split(' ').length > 5 }
    ];
  }

  private explainFixRouting(routing: {
    fixType: string;
    routeAction: string;
    visualIssueType?: string;
    existingStyleVariableHints: string[];
    layoutConstraintHints: string[];
  }): string[] {
    const lines: string[] = [];
    lines.push(`Fix type: "${routing.fixType}" → ${this.explainFixType(routing.fixType, routing.routeAction)}`);
    lines.push(`Route action: "${routing.routeAction}" → ${this.explainRouteAction(routing.routeAction)}`);
    if (routing.visualIssueType) {
      lines.push(`Visual issue type: "${routing.visualIssueType}"`);
    }

    if (routing.existingStyleVariableHints.length > 0) {
      lines.push(
        `Existing CSS variables available: ${routing.existingStyleVariableHints.join(', ')} — can reuse instead of hardcoding`
      );
    }
    if (routing.layoutConstraintHints.length > 0) {
      lines.push(`Layout constraints detected: ${routing.layoutConstraintHints.join(', ')} — fix must respect these`);
    }

    return lines;
  }

  private explainFixType(fixType: string, routeAction?: string): string {
    if (fixType === 'shared-style-fix' && routeAction === 'structural-style-override') {
      return 'CSS/style fix — update selector-level layout, alignment, or position styles';
    }

    const map: Record<string, string> = {
      css: 'Visual/styling fix — change CSS rules or add/override styles',
      'css-variable': 'Configuration via CSS variable — update variable value or add override',
      'shared-style-fix':
        'CSS/style fix — use a relevant existing variable when one matches the visual issue, otherwise add a narrow style override',
      template: 'Template/HTML fix — change component markup or bindings',
      config: 'Environment/config fix — update environment flag or feature toggle',
      component: 'Component logic fix — change TypeScript behavior',
      unknown: 'Cannot determine fix type from available signals'
    };
    return map[fixType] || map['unknown'];
  }

  private explainRouteAction(routeAction: string): string {
    const map: Record<string, string> = {
      'add-css-override': 'Add CSS override in primeng.overrides.scss or app stylesheet',
      'app-override-existing-variable': 'Override an existing CSS variable in the target app stylesheet',
      'structural-style-override': 'Add or update a selector-level structural style for layout, alignment, or position',
      'update-css-variable': 'Update CSS variable value in existing stylesheet',
      'shared-variable-plus-app-override': 'Update the shared CSS variable and add an app-level override if needed',
      'edit-template': 'Edit component template (HTML)',
      'edit-component': 'Edit component TypeScript logic',
      'update-config': 'Update environment configuration or feature flag',
      unknown: 'Cannot determine specific action — will need manual routing'
    };
    return map[routeAction] || map['unknown'];
  }

  private buildAlternatives(ctx: {
    scope: string;
    fixRouting: { fixType: string };
    relevantEntries: Array<{ kind: string; sourcePath: string; selector: string; score: number }>;
    targetElementHint: AiTargetElementHint | null;
  }): string[] {
    const alternatives: string[] = [];

    if (ctx.fixRouting.fixType === 'css') {
      alternatives.push('If not a CSS fix, check if the issue is driven by an environment config flag');
      alternatives.push('If not CSS or config, check if a component TypeScript change is needed');
    }
    if (ctx.fixRouting.fixType === 'config') {
      alternatives.push('If not a config fix, check if inline CSS override in primeng.overrides.scss is needed');
    }
    if (ctx.fixRouting.fixType === 'template') {
      alternatives.push('If not a template fix, check if CSS-only override would suffice');
    }
    if (ctx.relevantEntries.length > 1) {
      const second = ctx.relevantEntries[1];
      alternatives.push(
        `If not <${ctx.relevantEntries[0].selector}>, try <${second.selector}> (${second.kind}, ${second.score}%)`
      );
    }
    if (ctx.targetElementHint && ctx.targetElementHint.surface) {
      alternatives.push(
        `Surface hint "${ctx.targetElementHint.surface}" may point to a parent component owning this element`
      );
    }

    return alternatives;
  }

  private buildRiskAssessment(ctx: {
    scope: string;
    relevantEntries: Array<{ score: number }>;
    targetElementHint: AiTargetElementHint | null;
  }): string[] {
    const risks: string[] = [];

    if (ctx.relevantEntries.length === 0) {
      risks.push('⚠ No known components matched — AI may target wrong location');
    }
    if (ctx.relevantEntries.length > 0 && ctx.relevantEntries[0].score < 30) {
      risks.push('⚠ Low confidence file match — verify top target before submitting');
    }
    if (!ctx.targetElementHint) {
      risks.push('⚠ No target element — AI has less context for scoping the fix');
    }
    if (ctx.scope === 'generic') {
      risks.push('⚠ Scope is "generic" — bug may be harder to route correctly');
    }

    return risks;
  }

  private copyLastReviewPayload() {
    const usheru = this.getUsheruGlobal();
    if (usheru._lastReviewPayloadJson) {
      // Uses navigator.clipboard API — available in browser console
      navigator.clipboard
        .writeText(usheru._lastReviewPayloadJson)
        .then(() => {
          console.log('%c✓ Payload copied to clipboard', 'color:#059669');
        })
        .catch(() => {
          console.log('%cPayload available at window.usheru._lastReviewPayloadJson', 'color:#b45309');
        });
    } else {
      console.log('%cNo review payload yet. Wait for analysis to complete.', 'color:#b45309');
    }
  }

  private printReportHelp(): { message: string; command: string } {
    console.log('%c🐛 Bug Report — Quick Start%c', 'color:#89b4fa;font-weight:700;font-size:14px;', 'color:inherit');
    console.log('  1. Click the floating 🐛 button (bottom-right) or type:');
    console.log('     %cwindow.usheru.report()%c', 'color:#111827;background:#f3f4f6;padding:2px 4px', 'color:inherit');
    console.log('  2. Type your bug description in plain English.');
    console.log('  3. Click "Copy for ClickUp" — paste into a ClickUp task comment.');
    console.log('');
    console.log('%cOther forms:%c', 'color:#89b4fa;font-weight:600;', 'color:inherit');
    console.log(
      '  %cwindow.usheru.report("CTA wraps on mobile")%c',
      'color:#111827;background:#f3f4f6;padding:2px 4px',
      'color:inherit'
    );
    console.log(
      '  %cwindow.usheru.report({bug: "CTA wraps", expected: "Button stays on one line"})%c',
      'color:#111827;background:#f3f4f6;padding:2px 4px',
      'color:inherit'
    );
    console.log(
      '  %cwindow.usheru.report({bug: "...", expected: "...", branch: "PROD-123"})%c',
      'color:#111827;background:#f3f4f6;padding:2px 4px',
      'color:inherit'
    );
    console.log(
      '  or add %c?branch=PROD-123%c to the URL before calling report()',
      'color:#111827;background:#f3f4f6;padding:2px 4px',
      'color:inherit'
    );
    console.log(
      '  %cwindow.usheru.setBrokenElement($0)%c',
      'color:#111827;background:#f3f4f6;padding:2px 4px',
      'color:inherit'
    );
    console.log('');
    console.log(
      'To show the floating button on every page, open with %c?aiDebug=true%c in the URL.',
      'color:#a6e3a1',
      'color:inherit'
    );
    return {
      message: 'Report help printed. Call window.usheru.report() to open the bug report chat.',
      command: 'report'
    };
  }

  private printSetBrokenElementHelp(): { message: string; command: string } {
    console.log('%cSet Broken Element%c', 'color:#89b4fa;font-weight:700;font-size:14px;', 'color:inherit');
    console.log('  Select an element in DevTools, then run:');
    console.log(
      '  %cwindow.usheru.setBrokenElement($0)%c',
      'color:#111827;background:#f3f4f6;padding:2px 4px',
      'color:inherit'
    );
    console.log('  This updates the open bug report chat and closes Pick element mode.');
    return { message: 'Set broken element help printed.', command: 'setBrokenElement' };
  }

  private printReviewHelp(): AiBugfixReviewHelpResult {
    const location = this.parseUrl(window.location.href);
    const appResolution = this.resolveAppIdentifier();
    const config = this.getRecordStepsConfig();
    const recordStepsEnabled = config.size > 0;
    const configLabel = this.getRecordStepsConfigLabel();
    const recordingUrl = this.buildRecordStepsUrl();

    const divider = '━'.repeat(70);
    console.log(`\n${divider}`);
    console.log('%c⚠️ DEPRECATED — reviewAutomatedBugfixPlan', 'color:#f38ba8;font-weight:700;font-size:14px');
    console.log(
      '%cUse %cwindow.usheru.report()%c instead — opens the bug report form with the same analysis.',
      'color:#f38ba8',
      'color:#111827;background:#f3f4f6;padding:2px 4px',
      'color:#f38ba8'
    );
    console.log(
      '  %cwindow.usheru.report({bug: "what\s wrong", expected: "how to verify"})%c',
      'color:#111827;background:#f3f4f6;padding:2px 4px',
      'color:inherit'
    );
    console.log(`${divider}\n`);

    console.log('%cQUICK START (old API — still works):', 'color:#b45309;font-weight:700;margin-top:8px');
    console.log(
      "1. Basic review (requires $0): %cwindow.usheru.reviewAutomatedBugfixPlan({bug_context: 'what\\s wrong', expected_outcome: 'how to verify'}, $0)",
      'color:#111827;background:#f3f4f6;padding:2px 4px'
    );
    console.log(
      '2. Copy payload after review: %ccopy(window.usheru._lastReviewPayloadJson)',
      'color:#111827;background:#f3f4f6;padding:2px 4px'
    );
    console.log(
      "3. Compare against production: %cwindow.usheru.reviewAutomatedBugfixPlan({bug_context: '...', expected_outcome: '...', source_of_truth: 'production'}, $0)",
      'color:#111827;background:#f3f4f6;padding:2px 4px'
    );
    console.log('4. If the plan looks right, paste the copied JSON into GitHub Actions reproduction_steps');

    console.log('%c\nWHAT THIS DOES (legacy):', 'color:#b45309;font-weight:700;margin-top:8px');
    console.log('  - Builds the full bugfix payload (same as buildAutomatedBugfixSkillInput)');
    console.log('  - Runs reasoning analysis to find relevant files');
    console.log('  - Classifies fix routing (config vs template vs CSS)');
    console.log('  - Prints assumptions so QA can review before committing');

    console.log('%c\nFIELD GUIDE:', 'color:#b45309;font-weight:700;margin-top:8px');
    console.log(
      "%cbug_context%c: What's broken right now (specific, not vague)",
      'color:#111827;font-weight:700',
      'color:inherit'
    );
    console.log(
      "%cexpected_outcome%c: How to know it's fixed (describe the visual change you expect)",
      'color:#111827;font-weight:700',
      'color:inherit'
    );
    console.log(
      '%c$0%c: Selected element in DevTools (required for review — use when multiple similar controls exist)',
      'color:#111827;font-weight:700',
      'color:inherit'
    );

    if (!recordStepsEnabled) {
      console.log('%c\nRECORD INTERACTIONS:', 'color:#b45309;font-weight:700;margin-top:8px');
      console.log('Reload with: %c' + recordingUrl, 'color:#111827;background:#f3f4f6;padding:2px 4px');
      console.log('Then interact with the bug before calling the helper.');
      console.log(
        '  Use ?recordSteps=true for all types, or pick specific ones: ?recordSteps=click,navigation,hover,scroll'
      );
    }

    console.log('%c\nCONTEXT:', 'color:#2563eb;font-weight:700;margin-top:8px');
    console.table({
      app: appResolution.value,
      route: location.path,
      recordingActive: recordStepsEnabled ? 'yes' : 'no',
      recordingTypes: configLabel
    });

    console.log(`${divider}\n`);

    return {
      message: '⚠️ reviewAutomatedBugfixPlan is deprecated. Use window.usheru.report() instead.',
      recordStepsEnabled,
      recordStepsConfig: configLabel,
      recordedSteps: this.getRecordedSteps().length,
      recordedDomInteractions: this.getRecordedDomInteractions().length,
      recordStepsUrl: recordingUrl
    };
  }

  private _componentMapCache: ComponentMapPayload | null = null;

  private async fetchFromPaths(paths: string[]): Promise<Response | null> {
    for (const p of paths) {
      try {
        const r = await fetch(p);
        if (r.ok) return r;
      } catch {
        /* try next */
      }
    }
    return null;
  }

  /** @internal exposed for use by AiBugReportComponent */
  async fetchComponentMap(): Promise<ComponentMapPayload> {
    if (this._componentMapCache) return this._componentMapCache;

    const indexPaths = ['/assets/ai-tooling/repo-index/search-index.json', '/docs/repo-index/search-index.json'];
    const bundlePaths = (appName: string) => [
      `/assets/ai-tooling/repo-index/style-bundles/${appName}.json`,
      `/docs/repo-index/style-bundles/${appName}.json`
    ];
    const legacyPaths = ['/assets/ai-tooling/component-map.json', '/docs/component-map.json'];

    try {
      const indexResponse = await this.fetchFromPaths(indexPaths);
      const searchIndex = indexResponse ? await indexResponse.json() : null;

      if (searchIndex) {
        const appResolution = this.resolveAppIdentifier();
        const appName = appResolution?.value;
        let appStyleBundles: Record<string, unknown> | undefined;
        if (appName) {
          const bundleResponse = await this.fetchFromPaths(bundlePaths(appName));
          if (bundleResponse) {
            appStyleBundles = { [appName]: await bundleResponse.json() };
          }
        }

        const result = { entries: [], searchIndex, appStyleBundles } as ComponentMapPayload;
        this._componentMapCache = result;
        return result;
      }
    } catch {
      // Fall through to legacy loading
    }

    try {
      const response = await this.fetchFromPaths(legacyPaths);
      if (response?.ok) {
        const result = await response.json();
        this._componentMapCache = result;
        return result;
      }
    } catch {
      // Fallback: return empty component map
    }

    const fallback = { entries: [] };
    this._componentMapCache = fallback;
    return fallback;
  }

  private resolvePrimengBridgeForHost(tagName: string): Record<string, string> | null {
    const cm = this._componentMapCache;
    if (!cm?.entries?.length) return null;
    const entry = this.resolveComponentMapEntryForRuntimeSelector(cm.entries, tagName);
    if (!entry?.primengBridge) return null;
    const bridge = entry.primengBridge as Record<string, { primengToken: string }>;
    const result: Record<string, string> = {};
    for (const [compVar, mapping] of Object.entries(bridge)) {
      result[compVar] = mapping.primengToken;
    }
    return Object.keys(result).length > 0 ? result : null;
  }

  private buildBrowserAdapters(componentMap?: {
    appStyleBundles?: Record<
      string,
      {
        files?: Array<{
          path: string;
          exposedCssVariables?: string[];
          consumedCssVariables?: string[];
        }>;
      }
    >;
  }) {
    const virtualFiles = new Map<string, string>();
    for (const bundle of Object.values(componentMap?.appStyleBundles || {})) {
      for (const file of bundle.files || []) {
        const variables = [...(file.exposedCssVariables || []), ...(file.consumedCssVariables || [])];
        if (file.path && variables.length > 0) {
          virtualFiles.set(file.path, variables.map(variable => `${variable}: var(${variable});`).join('\n'));
        }
      }
    }

    return {
      fileReader: (path: string): string | null => virtualFiles.get(path) ?? null,
      pathExists: (path: string): boolean => virtualFiles.has(path),
      templateReader: (_entry: { templatePath?: string; sourcePath: string }): string => ''
    };
  }
}
